import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  QueryClientItem,
  QueryGeneralDefaultsResponse,
  QueryMiscClientFieldRow,
  QueryReportItem,
  QueryReportOptionPair,
  QueryReportOptionsInput,
  QueryReportOptionsMetadataResponse,
  SaveQueryA1,
} from '../../models/api/query/query-a1';
import { LayoutListItem } from '../../models/api/query/query-layouts';
import {
  QueryDetailResponse,
  QueryListItem,
} from '../../models/api/query/query-list';
import { QueryScheduleDefaultTimeResponse } from '../../models/api/query/query-schedules';
import { NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { OmniInsightsTitleService } from '../../services/omni-insights-title/omni-insights-title.service';
import { ValidatorsEx } from '../../validators/validators-ex';
import { BaseComponent } from '../base/base.component';
import { ButtonComponent } from '../core/buttons/button.component';
import { CheckboxComponent } from '../core/controls/checkbox.component';
import { DatePickerComponent } from '../core/controls/date-picker.component';
import { InputComponent } from '../core/controls/input.component';
import { RadioButtonComponent } from '../core/controls/radio-button.component';
import { SelectComponent } from '../core/controls/select.component';
import { PageHeaderComponent } from '../core/labels/page-header.component';
import { ProgressbarComponent } from '../core/labels/progressbar.component';
import { ActionRowComponent } from '../core/panels/action-row.component';
import { EmptyStateComponent } from '../core/panels/empty-state.component';
import { StepComponent } from '../core/stepper/step.component';
import { StepperComponent } from '../core/stepper/stepper.component';
import { ColumnComponent } from '../core/table/column.component';
import { TableComponent } from '../core/table/table.component';
import { HomeCardComponent } from '../home/home-card.component';
import { QueryCompletedComponent } from './query-completed/query-completed.component';
import { LayoutEditorComponent } from './query-layouts/layout-editor.component';
import { LayoutListComponent } from './query-layouts/layout-list.component';
import { QueryListComponent } from './query-list/query-list.component';
import { ScheduleListComponent } from './query-schedules/schedule-list.component';
import { SuspendedListComponent } from './query-suspended/suspended-list.component';
import {
  ADVANCED_PARAMETER_MENU_KEYS,
  getParameterSideMenuScrollAnchor,
  isParameterSideMenuItemVisible,
  PARAMETER_SIDE_MENU_LABELS,
  ParameterSideMenuKey,
  ParameterSideMenuOptions,
  SIMPLE_PARAMETER_MENU_KEYS,
} from './query-parameter-side-menu';
import { QueryParameterPanelHostComponent } from './query-parameter-panel-host.component';
import {
  applyDsClaimParametersFromApi,
  applyFinancialOptionsFromApi,
  applyMajorLiabilityParametersFromApi,
  applyQueryWorkFromApi,
  applyWcClaimTypesSelection,
  applyWcInjuryParametersFromApi,
  createQueryExtendedParamsForm,
  extendedParamsToPayload,
  isQueryFinancialsValidationMessage,
  markQueryAdvancedPaymentsTouched,
  markQueryFinancialsTouched,
  validateQueryAdvancedPayments,
  validateQueryFinancials,
} from './query-extended-params.form';
import {
  queryClaimFlagFormKeyForCode,
  queryClaimFlagFromStorage,
} from './query-claim-flags.helpers';
import {
  formatScheduleGeneralDateParen,
  generalDateFieldsFromQueryDetail,
  QUERY_SCHEDULE_INCREMENT_FROM_ROWS,
  QUERY_SCHEDULE_INCREMENT_THROUGH_ROWS,
  QueryGeneralDateField,
} from './query-schedule.helpers';
import {
  mapReserveChangeMinChangeUnitFromStorage,
  QUERY_RESERVE_CHANGE_DEFAULT_MIN_CHANGE,
  QUERY_RESERVE_CHANGE_REPORT_ID,
  resolveReserveChangeDefaultClaimId,
} from './query-reserve-change.helpers';
import {
  mapReportOptionStateOfFromStorage,
  resolveClaimIdFromStorage,
} from './query-standard-report-options.helpers';
import { validateSelectedReportsLobCompatibility } from './query-report-lob.validation';
import { Snackbar } from '../../services/snackbar/snackbar.service';
import { DateEx } from '../../functions/date-ex';

type QuerySectionKey =
  | 'request'
  | 'view-queries'
  | 'create-layout'
  | 'view-layouts'
  | 'view-schedules'
  | 'view-completed'
  | 'view-suspended'
  | 'schedule-only';

/** Where the layout editor was opened from; drives save/cancel navigation. */
type LayoutEditorReturnContext = 'stepper' | 'layouts';

interface SelectedReportEntry {
  report: QueryReportItem;
  checked: boolean;
}

interface ReportOptionState {
  reportType?: string;
  claimId?: string;
  sortBy?: string;
  availableLevels?: string[];
  selectedLevels?: SelectedLevelOption[];
  duration?: string;
  maxClaimCount?: string;
  formType?: string;
  lastCsrRunDate?: Date | null;
  reserveFromDate?: Date | null;
  reserveToDate?: Date | null;
  minChange?: string;
  minChangeUnit?: 'Dollar' | 'Percent';
  workStatusBeginDate?: Date | null;
  workStatusEndDate?: Date | null;
  includePaymentDetail?: boolean;
  summarySort?: string;
  selectedBankNumber?: string;
  selectedPayCodes?: string[];
  selectedPayCodeSeries?: string[];
  selectedBankNumbers?: string[];
  selectedBreakLevel?: string;
  numClaims?: string;
  selectedMiscFields?: string[];
  miscFieldSelections?: Record<string, string>;
  workStatusBeginDateSelected?: boolean;
  workStatusEndDateSelected?: boolean;
}

interface SelectedLevelOption {
  name: string;
  breakPage: boolean;
  stateOf?: 'Jurisdiction' | 'Loss' | 'Pay/Prem';
  structureIntermediate?: boolean;
  structureAccount?: boolean;
  structureUnit?: boolean;
  customDateValue?: string;
  customDatePeriod?: 'D' | 'M' | 'Y';
}

/**
 * Claim Report "Normal" used `<option value="">` in legacy HTML. PrimeNG Select
 * with `optionValue=""` does not set `p-inputwrapper-filled`, so the float label
 * sits on the same line as the value. This sentinel maps to/from `''` in state.
 */
const QUERY_CLAIM_REPORT_TYPE_NORMAL_OPTION_VALUE = '_QUERY_RPT_NORMAL_';

/**
 * Paycode Analysis "all bank numbers" uses `<option value="">` in legacy; PrimeNG Select
 * treats `''` as empty (`p-inputwrapper-filled` false), so the float label overlaps the value.
 * This sentinel maps to/from `''` in ReportOptionState / BANK-NUMBER.
 */
const QUERY_PAYCODE_ALL_BANK_NUMBERS_OPTION_VALUE = '_QUERY_PAYCODE_ALL_BANKS_';

/**
 * Report 525 "Form Type" prompt used `<option value="">` in legacy; PrimeNG Select
 * treats `''` as unfilled, so the float label overlaps the visible "Select" text.
 * Maps to/from `''` in `ReportOptionState` / `FORM-TYPE`.
 */
const QUERY_REPORT_FORM_TYPE_UNSELECTED_OPTION_VALUE =
  '_QUERY_RPT525_FORM_UNSELECTED_';

/**
 * Report 119 break level "None" uses `''` in legacy; PrimeNG float label overlaps
 * when the bound value is empty. Maps to/from `''` in state / `REPEATERLIST-REPORT-BREAK-LEVEL`.
 */
const QUERY_REPORT_BREAK_LEVEL_NONE_OPTION_VALUE =
  '_QUERY_RPT119_BREAK_LEVEL_NONE_';

/** Pay Code Analysis (report 193) series checkbox labels — must match legacy strings. */
const REPORT_OPTION_PAY_CODE_SERIES_LABELS = [
  'All 100 Series',
  'All 200 Series',
  'All 300 Series',
  'All 400 Series',
  'All 500 Series',
  'All 600 Series',
  'All 700 Series',
] as const;

/** Canonical LOBs (order matches QueryGeneralDefaultsHandler / Legacy). */
const QUERY_LINES_OF_BUSINESS_ALL = [
  'Auto Liability',
  'General Liability',
  'Property',
  "Workers' Comp",
  'Disability',
] as const;

type LobSelectOption = { label: string; value: string; disabled?: boolean };

@Component({
  selector: 'app-query-page',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    StepperComponent,
    StepComponent,
    SelectComponent,
    RadioButtonComponent,
    InputComponent,
    CheckboxComponent,
    DatePickerComponent,
    ButtonComponent,
    ActionRowComponent,
    QueryListComponent,
    QueryCompletedComponent,
    ScheduleListComponent,
    LayoutListComponent,
    LayoutEditorComponent,
    SuspendedListComponent,
    TableComponent,
    ColumnComponent,
    HomeCardComponent,
    PageHeaderComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    QueryParameterPanelHostComponent,
  ],
  template: `
    <app-page-header label="Query" headerSize="h1" />
    <ng-template #queryRecurringScheduleOptionsTpl>
      <div
        class="tw-mt-6 tw-pt-4 tw-border-t tw-border-[var(--sed-black-60-color)]"
      >
        <h3 class="tw-font-semibold tw-pb-3">Recurring Schedule Options</h3>
        <div class="tw-pb-4">
          <app-checkbox
            [control]="scheduleForm.controls.incrementAsOfDate"
            [label]="scheduleIncrementLabel('Increment As Of Date', 'asOfDate')"
          />
        </div>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-4">
          <div>
            <div class="tw-font-semibold tw-underline tw-pb-2">
              Increment From Dates
            </div>
            <div class="tw-flex tw-flex-col tw-gap-1">
              @for (
                row of scheduleIncrementFromRows;
                track row.scheduleControl
              ) {
                <app-checkbox
                  [control]="
                    scheduleIncrementCheckboxControl(row.scheduleControl)
                  "
                  [label]="scheduleIncrementLabel(row.label, row.generalField)"
                />
              }
            </div>
          </div>
          <div>
            <div class="tw-font-semibold tw-underline tw-pb-2">
              Increment Through Dates
            </div>
            <div class="tw-flex tw-flex-col tw-gap-1">
              @for (
                row of scheduleIncrementThroughRows;
                track row.scheduleControl
              ) {
                <app-checkbox
                  [control]="
                    scheduleIncrementCheckboxControl(row.scheduleControl)
                  "
                  [label]="scheduleIncrementLabel(row.label, row.generalField)"
                />
              }
            </div>
          </div>
        </div>
      </div>
    </ng-template>
    <app-home-card type="Large">
      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 sm:tw-gap-8">
        <aside
          class="tw-w-full sm:tw-w-[280px] tw-flex-shrink-0 tw-min-h-0 sm:tw-min-h-[400px]"
          [attr.aria-label]="
            showGeneralParameterSidebar
              ? 'General query parameters sidebar'
              : 'Query page navigation sidebar'
          "
        >
          @if (showGeneralParameterSidebar) {
            <nav
              id="query-page-general-parameters-nav"
              class="tw-flex tw-flex-col tw-gap-0 tw-text-[var(--sed-dynamic-black-color)]"
              aria-label="General query parameters"
            >
              <button
                type="button"
                class="tw-w-full tw-text-left tw-py-2 tw-px-3 tw-rounded-t tw-font-bold tw-text-sm tw-text-[var(--sed-black-60-color)] hover:tw-bg-[var(--sed-grey-40-color)] tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                [attr.aria-expanded]="parameterMenuExpandedSimple"
                [attr.aria-label]="parameterGroupToggleAriaLabel('simple')"
                (click)="toggleParameterMenuGroup('simple')"
              >
                Simple
              </button>
              @if (parameterMenuExpandedSimple) {
                <ul
                  class="tw-flex tw-flex-col tw-gap-0 tw-list-none tw-p-0 tw-m-0"
                  aria-label="Simple parameter sections"
                >
                  @for (
                    item of visibleSimpleParameterMenuItems;
                    track item.key
                  ) {
                    <li class="tw-m-0 tw-p-0">
                      <button
                        type="button"
                        class="tw-w-full tw-text-left tw-py-2 tw-pl-5 tw-pr-3 tw-rounded tw-text-sm tw-font-bold tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                        [ngClass]="{
                          'tw-bg-[var(--sed-grey-40-color)] tw-border tw-border-[var(--sed-black-60-color)]':
                            parameterActiveKey === item.key,
                          'hover:tw-bg-[var(--sed-grey-40-color)]':
                            parameterActiveKey !== item.key,
                        }"
                        [attr.aria-current]="
                          parameterActiveKey === item.key ? 'true' : null
                        "
                        [attr.aria-label]="
                          parameterMenuItemAriaLabel(item.label, 'simple')
                        "
                        (click)="onParameterSideMenuClick(item.key)"
                      >
                        {{ item.label }}
                      </button>
                    </li>
                  }
                </ul>
              }
              <button
                type="button"
                class="tw-w-full tw-text-left tw-py-2 tw-px-3 tw-font-bold tw-text-sm tw-text-[var(--sed-black-60-color)] tw-mt-1 hover:tw-bg-[var(--sed-grey-40-color)] tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                [attr.aria-expanded]="parameterMenuExpandedAdvanced"
                [attr.aria-label]="parameterGroupToggleAriaLabel('advanced')"
                (click)="toggleParameterMenuGroup('advanced')"
              >
                Advanced
              </button>
              @if (parameterMenuExpandedAdvanced) {
                <ul
                  class="tw-flex tw-flex-col tw-gap-0 tw-list-none tw-p-0 tw-m-0"
                  aria-label="Advanced parameter sections"
                >
                  @for (
                    item of visibleAdvancedParameterMenuItems;
                    track item.key
                  ) {
                    <li class="tw-m-0 tw-p-0">
                      <button
                        type="button"
                        class="tw-w-full tw-text-left tw-py-2 tw-pl-5 tw-pr-3 tw-rounded tw-text-sm tw-font-bold tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                        [ngClass]="{
                          'tw-bg-[var(--sed-grey-40-color)] tw-border tw-border-[var(--sed-black-60-color)]':
                            parameterActiveKey === item.key,
                          'hover:tw-bg-[var(--sed-grey-40-color)]':
                            parameterActiveKey !== item.key,
                        }"
                        [attr.aria-current]="
                          parameterActiveKey === item.key ? 'true' : null
                        "
                        [attr.aria-label]="
                          parameterMenuItemAriaLabel(item.label, 'advanced')
                        "
                        (click)="onParameterSideMenuClick(item.key)"
                      >
                        {{ item.label }}
                      </button>
                    </li>
                  }
                </ul>
              }
            </nav>
          } @else {
            <nav
              class="tw-flex tw-flex-col tw-gap-1"
              aria-label="Query primary sections"
            >
              <ul
                class="tw-flex tw-flex-col tw-gap-1 tw-list-none tw-p-0 tw-m-0"
              >
                @for (item of queryMenu; track item.key) {
                  <li class="tw-m-0 tw-p-0">
                    <button
                      type="button"
                      class="tw-w-full tw-text-left tw-py-2 tw-px-3 tw-rounded tw-font-bold"
                      [ngClass]="{
                        'tw-bg-[var(--sed-grey-40-color)] tw-border tw-border-[var(--sed-black-60-color)]':
                          activeSection === item.key,
                        'hover:tw-bg-[var(--sed-grey-40-color)]':
                          activeSection !== item.key,
                      }"
                      [attr.aria-current]="
                        activeSection === item.key ? 'page' : null
                      "
                      [attr.aria-label]="queryMenuItemAriaLabel(item.label)"
                      (click)="selectSection(item.key)"
                    >
                      {{ item.label }}
                    </button>
                  </li>
                }
              </ul>
            </nav>
          }
        </aside>

        <section class="tw-flex-1 tw-min-w-0 tw-w-full">
          @if (!activeSection) {
            <div></div>
          } @else {
            @if (activeSection === 'view-queries') {
              <app-query-list
                (editRequested)="onEditQueryRequested($event)"
                (scheduleQueryRequested)="onScheduleQueryRequested($event)"
              />
            } @else if (activeSection === 'view-completed') {
              <app-query-completed />
            } @else if (activeSection === 'create-layout') {
              <app-layout-editor
                [layoutId]="editLayoutId"
                [copyLayoutId]="copyLayoutId"
                [viewOnly]="layoutViewOnly"
                (cancelled)="onLayoutEditorCancelled()"
                (saved)="onLayoutEditorSaved($event)"
              />
            } @else if (activeSection === 'view-layouts') {
              <app-layout-list
                (createRequested)="onCreateLayoutRequested()"
                (editRequested)="onEditLayoutRequested($event)"
                (copyRequested)="onCopyLayoutRequested($event)"
              />
            } @else if (activeSection === 'view-schedules') {
              <app-schedule-list />
            } @else if (activeSection === 'view-suspended') {
              <app-suspended-list />
            } @else if (activeSection === 'schedule-only') {
              <div class="tw-p-6">
                <h2 class="tw-pb-4">Schedule Query</h2>
                <p class="tw-pb-4 tw-text-[var(--sed-black-60-color)]">
                  Set up a schedule for this query to run automatically at a
                  specified time.
                </p>

                @if (isLoadingScheduleQuery) {
                  <app-empty-state
                    height="200px"
                    icon="fa-light fa-clock"
                    label="Loading query"
                    message="Retrieving schedule options for this query"
                    class="tw-w-full"
                  >
                    <div class="tw-py-4">
                      <app-progressbar />
                    </div>
                  </app-empty-state>
                } @else {
                  @if (
                    form.controls.name.value || form.controls.description.value
                  ) {
                    <div
                      class="tw-bg-[var(--sed-grey-40-color)] tw-rounded tw-p-4 tw-mb-4"
                    >
                      <table class="tw-w-full tw-text-sm">
                        <tr>
                          <th
                            class="tw-text-left tw-pr-4 tw-py-1 tw-font-semibold"
                          >
                            Name:
                          </th>
                          <td class="tw-py-1">
                            {{ form.controls.name.value || '&nbsp;' }}
                          </td>
                          <th
                            class="tw-text-left tw-pr-4 tw-pl-6 tw-py-1 tw-font-semibold"
                          >
                            Description:
                          </th>
                          <td class="tw-py-1">
                            {{ form.controls.description.value || '&nbsp;' }}
                          </td>
                        </tr>
                      </table>
                    </div>
                  }

                  @if (scheduleError) {
                    <div
                      class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                      role="alert"
                    >
                      {{ scheduleError }}
                    </div>
                  }
                  @if (scheduleSuccess) {
                    <div
                      class="tw-bg-[color-mix(in_srgb,var(--sed-status-green-color)_12%,white)] tw-border tw-border-[var(--sed-status-green-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                      role="status"
                    >
                      {{ scheduleSuccess }}
                    </div>
                  }

                  <div class="tw-max-w-full sm:tw-max-w-[600px]">
                    <div
                      class="tw-pb-4 tw-flex tw-flex-wrap tw-gap-4 tw-items-end"
                    >
                      <div class="tw-w-full sm:tw-min-w-[140px] tw-min-w-0">
                        <app-select
                          [control]="scheduleForm.controls.frequency"
                          label="Frequency"
                          [options]="scheduleFrequencyOptions"
                          optionLabel="label"
                          optionValue="value"
                        />
                      </div>
                      <div class="tw-w-full sm:tw-min-w-[140px] tw-min-w-0">
                        <app-date-picker
                          [control]="scheduleForm.controls.runDate"
                          label="Run Date"
                          [minDate]="scheduleMinDate"
                        />
                      </div>
                      <div class="tw-flex tw-gap-2 tw-items-end">
                        <span class="tw-font-semibold tw-pb-2">Run Time:</span>
                        <app-input
                          [control]="scheduleForm.controls.runTimeHour"
                          label=""
                          type="Integer"
                          placeholder="HH"
                          classes="tw-w-14"
                          [min]="1"
                          [max]="12"
                        />
                        <span class="tw-pb-2">:</span>
                        <app-input
                          [control]="scheduleForm.controls.runTimeMinute"
                          label=""
                          type="Integer"
                          placeholder="MM"
                          classes="tw-w-14"
                          [min]="0"
                          [max]="59"
                        />
                        <app-select
                          [control]="scheduleForm.controls.runTimeAmPm"
                          label=""
                          [options]="runTimeAmPmOptions"
                          optionLabel="label"
                          optionValue="value"
                          [filter]="false"
                          [showClear]="false"
                          classes="tw-w-20"
                        />
                      </div>
                    </div>

                    @if (scheduleForm.controls.frequency.value === 'weekly') {
                      <div class="tw-pb-4">
                        <app-select
                          [control]="scheduleForm.controls.dayOfWeek"
                          label="Day of Week"
                          [options]="dayOfWeekOptions"
                          optionLabel="label"
                          optionValue="value"
                        />
                      </div>
                    }

                    @if (scheduleForm.controls.frequency.value === 'monthly') {
                      <div class="tw-pb-4">
                        <app-select
                          [control]="scheduleForm.controls.dayOfMonth"
                          label="Day of Month"
                          [options]="dayOfMonthOptions"
                          optionLabel="label"
                          optionValue="value"
                        />
                      </div>
                    }
                  </div>

                  @if (scheduleForm.controls.frequency.value !== 'once') {
                    <ng-container
                      *ngTemplateOutlet="queryRecurringScheduleOptionsTpl"
                    />
                  }

                  <app-action-row classes="tw-pt-6">
                    <app-button
                      left1
                      label="Cancel"
                      emphasis="Low"
                      (click)="cancelScheduleOnly()"
                    />
                    <app-button
                      right1
                      [loading]="scheduling"
                      label="Create Schedule"
                      (click)="createSchedule()"
                    />
                  </app-action-row>
                }
              </div>
            } @else if (activeSection === 'request') {
              <app-stepper
                #stepper
                [selected]="requestInitialStep ?? 'type'"
                [preserveSteps]="true"
                [activateAllSteps]="editMode"
              >
                <app-step
                  name="type"
                  label="Select query type"
                  icon="fa-circle-1"
                >
                  <ng-template>
                    <div
                      class="tw-p-6"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      <h2
                        id="query-page-wizard-step-type"
                        tabindex="-1"
                        class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                      >
                        Select Query Type
                      </h2>
                      <div class="tw-max-w-[500px] tw-pt-2">
                        <app-radio-button
                          [control]="form.controls.queryType"
                          [options]="queryTypeOptions"
                          optionLabel="label"
                          optionValue="value"
                          screenReaderLabel="Select query type"
                          [showClear]="false"
                          [vertical]="true"
                        />
                      </div>
                      <app-action-row classes="tw-pt-6">
                        <app-button
                          right1
                          label="Next"
                          (click)="goNextFromType()"
                        />
                      </app-action-row>
                    </div>
                  </ng-template>
                </app-step>

                <app-step
                  name="client"
                  label="Select client"
                  icon="fa-circle-2"
                >
                  <ng-template>
                    <div
                      class="tw-p-6"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      <h2
                        id="query-page-wizard-step-client"
                        tabindex="-1"
                        class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                      >
                        Select Client
                      </h2>
                      @if (clientsLoading) {
                        <app-empty-state
                          height="280px"
                          icon="fa-light fa-building"
                          label="Loading clients"
                          message="Retrieving client contracts"
                          class="tw-w-full tw-max-w-[700px]"
                        >
                          <div class="tw-py-4">
                            <app-progressbar />
                          </div>
                        </app-empty-state>
                      } @else {
                        <div class="tw-w-full tw-max-w-[700px]">
                          @if (form.controls.contractNumber.value) {
                            <div
                              class="tw-mb-4 tw-p-3 tw-rounded tw-bg-[var(--sed-grey-40-color)] tw-border tw-border-[var(--sed-black-60-color)]"
                              role="status"
                              aria-live="polite"
                            >
                              <div class="tw-text-sm tw-font-semibold tw-pb-1">
                                Selected client
                              </div>
                              <div>{{ getSelectedClientSummary() }}</div>
                            </div>
                          }
                          <app-table
                            class="tw-w-full"
                            [dataSource]="clientTableRows"
                            [showFilter]="true"
                            recordLabel="Client"
                            recordLabelPlural="Clients"
                            dataKey="contractNumber"
                            [pageSize]="10"
                            [globalFilterFields]="[
                              'contractNumber',
                              'clientName',
                              'display',
                            ]"
                            filterPlaceholder="Search clients"
                            filterLabel="Search clients"
                            [sortField]="'contractNumber'"
                            [sortOrder]="1"
                          >
                            <app-column
                              name="contractNumber"
                              label="Client #"
                              [sort]="true"
                              width="110px"
                            />
                            <app-column
                              name="clientName"
                              label="Name"
                              [sort]="true"
                            >
                              <ng-template #cell let-row>
                                {{ clientRowDisplayName(row) }}
                              </ng-template>
                            </app-column>
                            <app-column
                              name="actions"
                              label="Action"
                              width="160px"
                            >
                              <ng-template #cell let-row>
                                <app-button
                                  [label]="
                                    isClientRowSelected(row)
                                      ? 'Selected'
                                      : 'Select'
                                  "
                                  [icon]="
                                    isClientRowSelected(row)
                                      ? 'fa-light fa-circle-check'
                                      : 'fa-light fa-circle'
                                  "
                                  [emphasis]="
                                    isClientRowSelected(row) ? 'High' : 'Low'
                                  "
                                  [ariaLabel]="
                                    (isClientRowSelected(row)
                                      ? 'Selected: '
                                      : 'Select ') + clientRowDisplayName(row)
                                  "
                                  (click)="selectClientRow(row)"
                                />
                              </ng-template>
                            </app-column>
                          </app-table>
                        </div>
                      }
                      <app-action-row classes="tw-pt-6">
                        <app-button
                          left1
                          label="Previous"
                          emphasis="Low"
                          (click)="stepper.previous()"
                        />
                        <app-button
                          right1
                          label="Next"
                          (click)="goNextFromClient()"
                        />
                      </app-action-row>
                    </div>
                  </ng-template>
                </app-step>

                <app-step
                  name="output"
                  label="Select output"
                  icon="fa-circle-3"
                >
                  <ng-template>
                    <div
                      class="tw-p-6 tw-min-w-0 tw-max-w-full tw-box-border"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      @if (activeReportOptionReport) {
                        @if (
                          activeReportOptionReport.reportId === 199 ||
                          activeReportOptionReport.reportId === 338
                        ) {
                          <div
                            id="query-page-wizard-step-output"
                            tabindex="-1"
                            class="tw-text-center tw-font-bold tw-text-2xl sm:tw-text-3xl tw-leading-[1.2] tw-pb-1 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                          >
                            Claim Report
                          </div>
                          <div class="tw-text-center tw-pb-3">
                            Client: {{ getSelectedClientLabel() }}
                          </div>
                          <div
                            class="tw-border-t tw-border-[var(--sed-black-60-color)] tw-mb-4"
                          ></div>
                        } @else if (activeReportOptionReport.reportId === 193) {
                          <div
                            id="query-page-wizard-step-output"
                            tabindex="-1"
                            class="tw-text-2xl sm:tw-text-3xl tw-leading-[1.2] tw-font-bold tw-pb-1 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                          >
                            Paycode Analysis
                          </div>
                          <div class="tw-pb-3">
                            Client: {{ getSelectedClientLabel() }}
                          </div>
                          <div
                            class="tw-border-t tw-border-[var(--sed-black-60-color)] tw-mb-4"
                          ></div>
                        } @else {
                          <h2
                            id="query-page-wizard-step-output"
                            tabindex="-1"
                            class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                          >
                            Report Options
                          </h2>
                          <div class="tw-text-base tw-font-bold tw-pb-3">
                            {{ activeReportOptionReport.reportName }}
                          </div>
                        }
                        @if (reportOptionError) {
                          <div
                            class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                            role="alert"
                          >
                            {{ reportOptionError }}
                          </div>
                        }
                        @if (reportOptionLoading) {
                          <div
                            class="tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pb-3"
                          >
                            Loading report options...
                          </div>
                        }
                        @if (reportOptionState) {
                          @if (
                            activeReportOptionReport.reportId === 199 ||
                            activeReportOptionReport.reportId === 338
                          ) {
                            <div
                              class="tw-flex tw-max-w-[560px] tw-flex-col tw-gap-2"
                            >
                              <app-select
                                [control]="
                                  reportOptionScalarForm.controls.reportType
                                "
                                label="Report Type"
                                [options]="
                                  reportOptionClaimReportTypeSelectItems
                                "
                                optionLabel="label"
                                optionValue="value"
                                [filter]="false"
                                [showClear]="false"
                                [virtualScroll]="false"
                                [showHelpText]="false"
                              />
                              <app-select
                                [control]="
                                  reportOptionScalarForm.controls.claimId
                                "
                                label="Claim# ID"
                                [options]="reportOptionClaimIdSelectItems()"
                                optionLabel="label"
                                optionValue="value"
                                [filter]="false"
                                [showClear]="false"
                                [virtualScroll]="false"
                                [showHelpText]="false"
                              />
                              @if (
                                !hideSortByForReport(
                                  activeReportOptionReport.reportId
                                )
                              ) {
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.sortBy
                                  "
                                  label="Sort By"
                                  [options]="reportOptionSortSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              }
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 184) {
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[900px] tw-pt-2"
                            >
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.claimId
                                  "
                                  label="Claim# ID"
                                  [options]="reportOptionClaimIdSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                          }

                          @if (
                            activeReportOptionReport.reportId ===
                            QUERY_RESERVE_CHANGE_REPORT_ID
                          ) {
                            <div
                              class="tw-flex tw-flex-col tw-gap-3 tw-max-w-[900px] tw-pt-2"
                            >
                              <div
                                class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4"
                              >
                                <app-date-picker
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .reserveFromDate
                                  "
                                  label="Reserve Change Begin Date"
                                  screenReaderLabelPrefix="Reserve Change"
                                  [showHelpText]="false"
                                />
                                <app-date-picker
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .reserveToDate
                                  "
                                  label="Reserve Change End Date"
                                  screenReaderLabelPrefix="Reserve Change"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-checkbox
                                  [control]="
                                    reportOptionReportCheckboxForm.controls
                                      .workStatusBeginDateSelected
                                  "
                                  label="Reserve Change Start Date Increment"
                                  [showHelpText]="false"
                                />
                                <app-checkbox
                                  [control]="
                                    reportOptionReportCheckboxForm.controls
                                      .workStatusEndDateSelected
                                  "
                                  label="Reserve Change End Date Increment"
                                  [showHelpText]="false"
                                />
                                @if (isMultipleReportsSelected()) {
                                  <div
                                    class="tw-text-[var(--sed-status-green-color)] tw-text-sm"
                                  >
                                    Checkboxes disabled because more than one
                                    report is selected.
                                  </div>
                                }
                              </div>
                              <div
                                class="tw-flex tw-flex-wrap tw-items-end tw-gap-6 tw-pt-6"
                              >
                                <div
                                  class="tw-flex tw-flex-col tw-gap-2 tw-min-w-[200px]"
                                >
                                  <app-input
                                    [control]="
                                      reportOptionScalarForm.controls.minChange
                                    "
                                    label="Minimum Change To Report"
                                    screenReaderLabelPrefix="Reserve Change"
                                    [maxlength]="15"
                                    [showHelpText]="false"
                                  />
                                </div>
                                <app-radio-button
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .minChangeUnit
                                  "
                                  [options]="reportOptionMinChangeUnitItems"
                                  optionLabel="label"
                                  optionValue="value"
                                  screenReaderLabel="Minimum change in dollars or percent"
                                  [showClear]="false"
                                />
                              </div>
                              <div
                                class="tw-flex tw-flex-col tw-gap-2 tw-max-w-[360px]"
                              >
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.claimId
                                  "
                                  label="Claim# ID"
                                  [options]="reportOptionClaimIdSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                          }

                          @if (
                            supportsStandardOptions(
                              activeReportOptionReport.reportId
                            )
                          ) {
                            @if (
                              !(
                                activeReportOptionReport.reportId === 199 ||
                                activeReportOptionReport.reportId === 338
                              ) &&
                              !hideSortByForReport(
                                activeReportOptionReport.reportId
                              )
                            ) {
                              <div
                                class="tw-pt-4 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[760px]"
                              >
                                <div class="tw-flex tw-flex-col tw-gap-2">
                                  <app-select
                                    [control]="
                                      reportOptionScalarForm.controls.sortBy
                                    "
                                    label="Sort By"
                                    [options]="reportOptionSortSelectItems()"
                                    optionLabel="label"
                                    optionValue="value"
                                    [filter]="false"
                                    [showClear]="false"
                                    [virtualScroll]="false"
                                    [showHelpText]="false"
                                  />
                                </div>
                              </div>
                            }

                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[250px_auto_1fr] tw-gap-4 tw-items-start tw-pt-4"
                            >
                              <div>
                                <label
                                  for="query-page-ro-available-levels-std"
                                  class="tw-font-bold tw-pb-2 tw-block"
                                  >Available Levels</label
                                >
                                <select
                                  id="query-page-ro-available-levels-std"
                                  #availableLevelsSelect
                                  multiple
                                  class="tw-w-full tw-h-[220px] tw-border tw-rounded tw-p-2"
                                >
                                  @for (
                                    level of reportOptionState.availableLevels ??
                                      [];
                                    track level
                                  ) {
                                    <option [value]="level">{{ level }}</option>
                                  }
                                </select>
                              </div>
                              <div
                                class="tw-flex tw-flex-col tw-gap-2 tw-pt-10"
                              >
                                <app-button
                                  label="→"
                                  ariaLabel="Add selected levels"
                                  (click)="
                                    addSelectedLevel(availableLevelsSelect)
                                  "
                                />
                                <app-button
                                  label="⇒"
                                  ariaLabel="Add all levels"
                                  (click)="addAllLevels()"
                                />
                              </div>
                              <div class="tw-min-w-0">
                                <div
                                  id="query-page-ro-selected-levels-std-label"
                                  class="tw-font-bold tw-pb-2"
                                >
                                  Selected Levels
                                </div>
                                <div
                                  class="tw-border tw-rounded tw-h-[220px] tw-overflow-x-auto tw-overflow-y-auto"
                                  role="group"
                                  aria-labelledby="query-page-ro-selected-levels-std-label"
                                >
                                  <table
                                    class="tw-min-w-full tw-text-sm [&_td]:tw-whitespace-nowrap [&_th]:tw-whitespace-nowrap"
                                  >
                                    <thead
                                      class="tw-bg-[var(--sed-engaged-black-color)] tw-text-[var(--sed-warm-white-color)]"
                                    >
                                      <tr>
                                        <th class="tw-p-2 tw-text-left">
                                          Actions
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Seq.
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Level
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Options
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (
                                        level of reportOptionState.selectedLevels ??
                                          [];
                                        track level.name;
                                        let i = $index
                                      ) {
                                        <tr
                                          class="tw-border-b tw-last:border-b-0"
                                        >
                                          <td
                                            class="tw-p-2 tw-whitespace-nowrap"
                                          >
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline tw-mr-2"
                                              (click)="
                                                removeSelectedLevels([
                                                  level.name,
                                                ])
                                              "
                                            >
                                              Remove
                                            </button>
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline tw-mr-2"
                                              (click)="
                                                moveSelectedLevel(
                                                  level.name,
                                                  -1
                                                )
                                              "
                                            >
                                              <span class="tw-sr-only"
                                                >{{ level.name }} record
                                              </span>
                                              Up
                                            </button>
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline"
                                              (click)="
                                                moveSelectedLevel(level.name, 1)
                                              "
                                            >
                                              <span class="tw-sr-only"
                                                >{{ level.name }} record
                                              </span>
                                              Down
                                            </button>
                                          </td>
                                          <td class="tw-p-2">{{ i + 1 }}</td>
                                          <td
                                            class="tw-p-2"
                                            [attr.id]="
                                              'qp-ro-level-name-' +
                                              activeReportOptionReport.reportId +
                                              '-' +
                                              i
                                            "
                                          >
                                            {{ level.name }}
                                          </td>
                                          <td
                                            class="tw-p-2"
                                            [attr.aria-labelledby]="
                                              'qp-ro-level-name-' +
                                              activeReportOptionReport.reportId +
                                              '-' +
                                              i
                                            "
                                          >
                                            <div
                                              class="tw-flex tw-flex-col tw-gap-2"
                                            >
                                              @let pageBreakCtl =
                                                getReportOptionLevelPageBreakControl(
                                                  level
                                                );
                                              <app-checkbox
                                                [control]="pageBreakCtl"
                                                [id]="
                                                  reportOptionLevelCheckboxInputId(
                                                    activeReportOptionReport.reportId,
                                                    i,
                                                    'pageBreak'
                                                  )
                                                "
                                                label="Page Break"
                                                [screenReaderLabelSuffix]="
                                                  level.name + ' record'
                                                "
                                                [showHelpText]="false"
                                              />
                                              @if (
                                                level.name.toUpperCase() ===
                                                'STATE'
                                              ) {
                                                <app-select
                                                  [control]="
                                                    getReportOptionLevelStateOfControl(
                                                      level
                                                    )
                                                  "
                                                  label=""
                                                  [screenReaderLabel]="
                                                    level.name +
                                                    ' grouping: state of jurisdiction or loss'
                                                  "
                                                  [options]="
                                                    reportOptionStateOfItems
                                                  "
                                                  optionLabel="label"
                                                  optionValue="value"
                                                  [filter]="false"
                                                  [showClear]="false"
                                                  [virtualScroll]="false"
                                                  [showHelpText]="false"
                                                />
                                              }
                                              @if (
                                                level.name.toUpperCase() ===
                                                'STRUCTURE'
                                              ) {
                                                <div class="tw-w-full">
                                                  <div class="tw-font-semibold">
                                                    Structure Totals At:
                                                  </div>
                                                  <div
                                                    class="tw-flex tw-flex-nowrap tw-gap-3"
                                                  >
                                                    @let structureIntCtl =
                                                      getReportOptionLevelStructureIntermediateControl(
                                                        level
                                                      );
                                                    @let structureAcctCtl =
                                                      getReportOptionLevelStructureAccountControl(
                                                        level
                                                      );
                                                    @let structureUnitCtl =
                                                      getReportOptionLevelStructureUnitControl(
                                                        level
                                                      );
                                                    <app-checkbox
                                                      [control]="
                                                        structureIntCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureIntermediate'
                                                        )
                                                      "
                                                      label="Intermediate"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                    <app-checkbox
                                                      [control]="
                                                        structureAcctCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureAccount'
                                                        )
                                                      "
                                                      label="Account"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                    <app-checkbox
                                                      [control]="
                                                        structureUnitCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureUnit'
                                                        )
                                                      "
                                                      label="Unit"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                  </div>
                                                </div>
                                              }
                                              @if (
                                                level.name.toUpperCase() ===
                                                'CUSTOM DATES'
                                              ) {
                                                <div
                                                  class="tw-grid tw-grid-cols-[1fr_auto] tw-gap-2"
                                                >
                                                  <input
                                                    class="tw-border tw-rounded tw-p-1"
                                                    type="number"
                                                    min="1"
                                                    [ngModel]="
                                                      level.customDateValue ??
                                                      '30'
                                                    "
                                                    (ngModelChange)="
                                                      level.customDateValue =
                                                        $event
                                                    "
                                                    placeholder="Increment"
                                                  />
                                                  <app-select
                                                    [control]="
                                                      getReportOptionLevelCustomDatePeriodControl(
                                                        level
                                                      )
                                                    "
                                                    label=""
                                                    [screenReaderLabel]="
                                                      level.name +
                                                      ': increment period unit'
                                                    "
                                                    [options]="
                                                      reportOptionCustomDatePeriodItems
                                                    "
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    [filter]="false"
                                                    [showClear]="false"
                                                    [virtualScroll]="false"
                                                    [showHelpText]="false"
                                                  />
                                                </div>
                                              }
                                            </div>
                                          </td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                </div>
                                <button
                                  type="button"
                                  class="focus-ring-icon-button tw-underline tw-pt-2"
                                  (click)="clearSelectedLevels()"
                                >
                                  Remove All Levels
                                </button>
                              </div>
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 525) {
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4 tw-max-w-[900px] tw-pt-4"
                            >
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.formType
                                  "
                                  label="Form Type"
                                  [options]="reportOptionFormType525Items"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-input
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .maxClaimCount
                                  "
                                  label="Max Claims"
                                  screenReaderLabelPrefix="Claim Status CSR"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-date-picker
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .lastCsrRunDate
                                  "
                                  label="Last CSR Run Date"
                                  screenReaderLabelPrefix="Claim Status CSR"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 379) {
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[760px] tw-pt-4"
                            >
                              <div>
                                <label class="tw-font-bold tw-block tw-pb-1"
                                  >Work Status Begin</label
                                >
                                <input
                                  type="date"
                                  class="tw-w-full tw-border tw-rounded tw-p-2"
                                  [ngModel]="
                                    reportOptionState.workStatusBeginDate
                                      | date: 'yyyy-MM-dd'
                                  "
                                  (ngModelChange)="
                                    reportOptionState.workStatusBeginDate =
                                      toDateOrNull($event)
                                  "
                                />
                              </div>
                              <div>
                                <label class="tw-font-bold tw-block tw-pb-1"
                                  >Work Status End</label
                                >
                                <input
                                  type="date"
                                  class="tw-w-full tw-border tw-rounded tw-p-2"
                                  [ngModel]="
                                    reportOptionState.workStatusEndDate
                                      | date: 'yyyy-MM-dd'
                                  "
                                  (ngModelChange)="
                                    reportOptionState.workStatusEndDate =
                                      toDateOrNull($event)
                                  "
                                />
                              </div>
                            </div>
                            <div class="tw-pt-3 tw-flex tw-flex-col tw-gap-2">
                              <app-checkbox
                                [control]="
                                  reportOptionReportCheckboxForm.controls
                                    .workStatusBeginDateSelected
                                "
                                label="Work Status Start Date Increment"
                                [showHelpText]="false"
                              />
                              <app-checkbox
                                [control]="
                                  reportOptionReportCheckboxForm.controls
                                    .workStatusEndDateSelected
                                "
                                label="Work Status End Date Increment"
                                [showHelpText]="false"
                              />
                              @if (isMultipleReportsSelected()) {
                                <div
                                  class="tw-text-[var(--sed-status-green-color)] tw-text-sm"
                                >
                                  Checkboxes disabled because more than one
                                  report is selected.
                                </div>
                              }
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 193) {
                            <div class="tw-font-bold tw-text-lg tw-pb-1">
                              Pay Code Analysis Report Option
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[900px] tw-pt-3"
                            >
                              <div>
                                <div class="tw-font-bold tw-block tw-pb-1">
                                  Client#
                                </div>
                                <div class="tw-py-2">
                                  {{ form.controls.contractNumber.value }}
                                </div>
                              </div>
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[900px] tw-pt-2"
                            >
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .selectedBankNumber
                                  "
                                  label="Active Bank Number"
                                  [options]="
                                    reportOptionActiveBankNumberSelectItems()
                                  "
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.summarySort
                                  "
                                  label="Sort Summary By"
                                  [options]="payCodeSummarySortOptions"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                            <div class="tw-pt-3 tw-flex tw-flex-col tw-gap-2">
                              <app-checkbox
                                [control]="
                                  reportOptionReportCheckboxForm.controls
                                    .includePaymentDetail
                                "
                                label="Include Payment Details"
                                [showHelpText]="false"
                              />
                            </div>
                            <div
                              class="tw-pt-2 tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-2 tw-max-w-[900px]"
                            >
                              <app-checkbox
                                [control]="
                                  reportOptionReportCheckboxForm.controls
                                    .payCodeAllSeries
                                "
                                label="All Pay Codes"
                                [showHelpText]="false"
                              />
                              @for (
                                series of reportOptionPayCodeSeriesLabels;
                                track series
                              ) {
                                <app-checkbox
                                  [control]="
                                    getPayCodeSeriesCheckboxControl(series)
                                  "
                                  [label]="series"
                                  [showHelpText]="false"
                                />
                              }
                            </div>
                            <div class="tw-font-bold tw-pt-3">
                              Individual Pay Codes
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[1fr_auto_1fr] tw-gap-4 tw-items-center tw-max-w-full md:tw-max-w-[900px] tw-pt-2"
                            >
                              <div>
                                <label
                                  for="query-page-ro-available-pay-codes"
                                  class="tw-font-bold tw-pb-1 tw-block"
                                  >Code# Available Codes</label
                                >
                                <select
                                  id="query-page-ro-available-pay-codes"
                                  #availablePayCodesSelect
                                  multiple
                                  class="tw-w-full tw-h-[120px] tw-border tw-rounded tw-p-2"
                                >
                                  @for (
                                    option of getAvailablePayCodes();
                                    track option
                                  ) {
                                    <option [value]="option">
                                      {{ option }}
                                    </option>
                                  }
                                </select>
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-button
                                  label="→"
                                  ariaLabel="Add selected pay codes"
                                  (click)="
                                    addSelectedPayCodes(availablePayCodesSelect)
                                  "
                                />
                                <app-button
                                  label="⇒"
                                  ariaLabel="Add all pay codes"
                                  (click)="addAllPayCodes()"
                                />
                                <app-button
                                  label="←"
                                  ariaLabel="Remove selected pay codes"
                                  (click)="
                                    removeSelectedPayCodes(
                                      selectedPayCodesSelect
                                    )
                                  "
                                />
                                <app-button
                                  label="⇐"
                                  ariaLabel="Remove all pay codes"
                                  (click)="removeAllPayCodes()"
                                />
                              </div>
                              <div>
                                <label
                                  for="query-page-ro-selected-pay-codes"
                                  class="tw-font-bold tw-pb-1 tw-block"
                                  >Selected Codes</label
                                >
                                <select
                                  id="query-page-ro-selected-pay-codes"
                                  #selectedPayCodesSelect
                                  multiple
                                  class="tw-w-full tw-h-[120px] tw-border tw-rounded tw-p-2"
                                >
                                  @for (
                                    option of reportOptionState.selectedPayCodes ??
                                      [];
                                    track option
                                  ) {
                                    <option [value]="option">
                                      {{ option }}
                                    </option>
                                  }
                                </select>
                              </div>
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[900px] tw-pt-4"
                            >
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.claimId
                                  "
                                  label="Claim# ID"
                                  [options]="reportOptionClaimIdSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.sortBy
                                  "
                                  label="Sort By"
                                  [options]="reportOptionSortSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-[250px_auto_1fr] tw-gap-4 tw-items-start tw-pt-4"
                            >
                              <div>
                                <label
                                  for="query-page-ro-available-levels-193"
                                  class="tw-font-bold tw-pb-2 tw-block"
                                  >Available Levels</label
                                >
                                <select
                                  id="query-page-ro-available-levels-193"
                                  #availableLevelsSelect193
                                  multiple
                                  class="tw-w-full tw-h-[220px] tw-border tw-rounded tw-p-2"
                                >
                                  @for (
                                    level of reportOptionState.availableLevels ??
                                      [];
                                    track level
                                  ) {
                                    <option [value]="level">{{ level }}</option>
                                  }
                                </select>
                              </div>
                              <div
                                class="tw-flex tw-flex-col tw-gap-2 tw-pt-10"
                              >
                                <app-button
                                  label="→"
                                  ariaLabel="Add selected levels"
                                  (click)="
                                    addSelectedLevel(availableLevelsSelect193)
                                  "
                                />
                                <app-button
                                  label="⇒"
                                  ariaLabel="Add all levels"
                                  (click)="addAllLevels()"
                                />
                              </div>
                              <div class="tw-min-w-0">
                                <div
                                  id="query-page-ro-selected-levels-193-label"
                                  class="tw-font-bold tw-pb-2"
                                >
                                  Selected Levels
                                </div>
                                <div
                                  class="tw-border tw-rounded tw-h-[220px] tw-overflow-x-auto tw-overflow-y-auto"
                                  role="group"
                                  aria-labelledby="query-page-ro-selected-levels-193-label"
                                >
                                  <table
                                    class="tw-min-w-full tw-text-sm [&_td]:tw-whitespace-nowrap [&_th]:tw-whitespace-nowrap"
                                  >
                                    <thead
                                      class="tw-bg-[var(--sed-engaged-black-color)] tw-text-[var(--sed-warm-white-color)]"
                                    >
                                      <tr>
                                        <th class="tw-p-2 tw-text-left">
                                          Actions
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Seq.
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Level
                                        </th>
                                        <th class="tw-p-2 tw-text-left">
                                          Options
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      @for (
                                        level of reportOptionState.selectedLevels ??
                                          [];
                                        track level.name;
                                        let i = $index
                                      ) {
                                        <tr
                                          class="tw-border-b tw-last:border-b-0"
                                        >
                                          <td
                                            class="tw-p-2 tw-whitespace-nowrap"
                                          >
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline tw-mr-2"
                                              (click)="
                                                removeSelectedLevels([
                                                  level.name,
                                                ])
                                              "
                                            >
                                              Remove
                                            </button>
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline tw-mr-2"
                                              (click)="
                                                moveSelectedLevel(
                                                  level.name,
                                                  -1
                                                )
                                              "
                                            >
                                              <span class="tw-sr-only"
                                                >{{ level.name }} record
                                              </span>
                                              Up
                                            </button>
                                            <button
                                              type="button"
                                              class="focus-ring-icon-button tw-underline"
                                              (click)="
                                                moveSelectedLevel(level.name, 1)
                                              "
                                            >
                                              <span class="tw-sr-only"
                                                >{{ level.name }} record
                                              </span>
                                              Down
                                            </button>
                                          </td>
                                          <td class="tw-p-2">{{ i + 1 }}</td>
                                          <td
                                            class="tw-p-2"
                                            [attr.id]="
                                              'qp-ro-level-name-' +
                                              activeReportOptionReport.reportId +
                                              '-' +
                                              i
                                            "
                                          >
                                            {{ level.name }}
                                          </td>
                                          <td
                                            class="tw-p-2"
                                            [attr.aria-labelledby]="
                                              'qp-ro-level-name-' +
                                              activeReportOptionReport.reportId +
                                              '-' +
                                              i
                                            "
                                          >
                                            <div
                                              class="tw-flex tw-flex-col tw-gap-2"
                                            >
                                              @let pageBreakCtl =
                                                getReportOptionLevelPageBreakControl(
                                                  level
                                                );
                                              <app-checkbox
                                                [control]="pageBreakCtl"
                                                [id]="
                                                  reportOptionLevelCheckboxInputId(
                                                    activeReportOptionReport.reportId,
                                                    i,
                                                    'pageBreak'
                                                  )
                                                "
                                                label="Page Break"
                                                [screenReaderLabelSuffix]="
                                                  level.name + ' record'
                                                "
                                                [showHelpText]="false"
                                              />
                                              @if (
                                                level.name.toUpperCase() ===
                                                'STATE'
                                              ) {
                                                <app-select
                                                  [control]="
                                                    getReportOptionLevelStateOfControl(
                                                      level
                                                    )
                                                  "
                                                  label=""
                                                  [screenReaderLabel]="
                                                    level.name +
                                                    ' grouping: state of jurisdiction or loss'
                                                  "
                                                  [options]="
                                                    reportOptionStateOfItems
                                                  "
                                                  optionLabel="label"
                                                  optionValue="value"
                                                  [filter]="false"
                                                  [showClear]="false"
                                                  [virtualScroll]="false"
                                                  [showHelpText]="false"
                                                />
                                              }
                                              @if (
                                                level.name.toUpperCase() ===
                                                'STRUCTURE'
                                              ) {
                                                <div class="tw-w-full">
                                                  <div class="tw-font-semibold">
                                                    Structure Totals At:
                                                  </div>
                                                  <div
                                                    class="tw-flex tw-flex-nowrap tw-gap-3"
                                                  >
                                                    @let structureIntCtl =
                                                      getReportOptionLevelStructureIntermediateControl(
                                                        level
                                                      );
                                                    @let structureAcctCtl =
                                                      getReportOptionLevelStructureAccountControl(
                                                        level
                                                      );
                                                    @let structureUnitCtl =
                                                      getReportOptionLevelStructureUnitControl(
                                                        level
                                                      );
                                                    <app-checkbox
                                                      [control]="
                                                        structureIntCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureIntermediate'
                                                        )
                                                      "
                                                      label="Intermediate"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                    <app-checkbox
                                                      [control]="
                                                        structureAcctCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureAccount'
                                                        )
                                                      "
                                                      label="Account"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                    <app-checkbox
                                                      [control]="
                                                        structureUnitCtl
                                                      "
                                                      [id]="
                                                        reportOptionLevelCheckboxInputId(
                                                          activeReportOptionReport.reportId,
                                                          i,
                                                          'structureUnit'
                                                        )
                                                      "
                                                      label="Unit"
                                                      [screenReaderLabelSuffix]="
                                                        level.name + ' record'
                                                      "
                                                      [showHelpText]="false"
                                                    />
                                                  </div>
                                                </div>
                                              }
                                              @if (
                                                level.name.toUpperCase() ===
                                                'CUSTOM DATES'
                                              ) {
                                                <div
                                                  class="tw-grid tw-grid-cols-[1fr_auto] tw-gap-2"
                                                >
                                                  <input
                                                    class="tw-border tw-rounded tw-p-1"
                                                    type="number"
                                                    min="1"
                                                    [ngModel]="
                                                      level.customDateValue ??
                                                      '30'
                                                    "
                                                    (ngModelChange)="
                                                      level.customDateValue =
                                                        $event
                                                    "
                                                    placeholder="Increment"
                                                  />
                                                  <app-select
                                                    [control]="
                                                      getReportOptionLevelCustomDatePeriodControl(
                                                        level
                                                      )
                                                    "
                                                    label=""
                                                    [screenReaderLabel]="
                                                      level.name +
                                                      ': increment period unit'
                                                    "
                                                    [options]="
                                                      reportOptionCustomDatePeriodItems
                                                    "
                                                    optionLabel="label"
                                                    optionValue="value"
                                                    [filter]="false"
                                                    [showClear]="false"
                                                    [virtualScroll]="false"
                                                    [showHelpText]="false"
                                                  />
                                                </div>
                                              }
                                            </div>
                                          </td>
                                        </tr>
                                      }
                                    </tbody>
                                  </table>
                                </div>
                                <button
                                  type="button"
                                  class="focus-ring-icon-button tw-underline tw-pt-2"
                                  (click)="clearSelectedLevels()"
                                >
                                  Remove All Levels
                                </button>
                              </div>
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 119) {
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 tw-max-w-[900px] tw-pt-4"
                            >
                              <div>
                                <label class="tw-font-bold tw-block tw-pb-1"
                                  >Minimum # of Claims To Report</label
                                >
                                <input
                                  class="tw-w-full tw-border tw-rounded tw-p-2"
                                  [ngModel]="reportOptionState.numClaims"
                                  (ngModelChange)="
                                    reportOptionState.numClaims = $event
                                  "
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls
                                      .selectedBreakLevel
                                  "
                                  label="Break Level"
                                  [options]="
                                    reportOptionBreakLevelSelectItems()
                                  "
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.sortBy
                                  "
                                  label="Sort By"
                                  [options]="reportOptionSortSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.claimId
                                  "
                                  label="Claim# ID"
                                  [options]="reportOptionClaimIdSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 182) {
                            <div
                              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-max-w-[760px] tw-pt-4"
                            >
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.duration
                                  "
                                  label="Duration"
                                  [options]="reportOptionDuration182Items"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                              <div class="tw-flex tw-flex-col tw-gap-2">
                                <app-select
                                  [control]="
                                    reportOptionScalarForm.controls.claimId
                                  "
                                  label="Claim# ID"
                                  [options]="reportOptionClaimIdSelectItems()"
                                  optionLabel="label"
                                  optionValue="value"
                                  [filter]="false"
                                  [showClear]="false"
                                  [virtualScroll]="false"
                                  [showHelpText]="false"
                                />
                              </div>
                            </div>
                          }

                          @if (activeReportOptionReport.reportId === 184) {
                            <div class="tw-font-bold tw-text-lg tw-pb-2">
                              Miscellaneous Client Field Report Options
                            </div>
                            <div class="tw-overflow-auto tw-max-w-[980px]">
                              <table class="tw-w-full tw-text-sm tw-border">
                                <thead
                                  class="tw-bg-[var(--sed-engaged-black-color)] tw-text-[var(--sed-warm-white-color)]"
                                >
                                  <tr>
                                    <th class="tw-p-2 tw-text-left">Client</th>
                                    <th class="tw-p-2 tw-text-left">Account</th>
                                    <th class="tw-p-2 tw-text-left">Unit</th>
                                    <th class="tw-p-2 tw-text-left">
                                      Miscellaneous Field
                                    </th>
                                    <th class="tw-p-2 tw-text-left">
                                      Field Value
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  @for (
                                    row of getMiscFieldRows();
                                    track getMiscFieldRowKey(row)
                                  ) {
                                    <tr class="tw-border-b tw-last:border-b-0">
                                      <td class="tw-p-2">
                                        {{ row.contractNumber }}
                                      </td>
                                      <td class="tw-p-2">
                                        {{ row.accountNumber }}
                                      </td>
                                      <td class="tw-p-2">
                                        {{ row.unitNumber }}
                                      </td>
                                      <td class="tw-p-2">
                                        {{ row.fieldLabel }}
                                      </td>
                                      <td class="tw-p-2 tw-align-top">
                                        <div
                                          class="tw-flex tw-w-full tw-min-w-0 tw-flex-col tw-gap-2"
                                        >
                                          <app-select
                                            [control]="
                                              getMiscFieldValueControl(row)
                                            "
                                            label=""
                                            [screenReaderLabel]="
                                              'Field value for ' +
                                              row.fieldLabel
                                            "
                                            [options]="
                                              reportOptionMiscFieldValueItems
                                            "
                                            optionLabel="label"
                                            optionValue="value"
                                            [filter]="false"
                                            [showClear]="false"
                                            [virtualScroll]="false"
                                            [showHelpText]="false"
                                          />
                                        </div>
                                      </td>
                                    </tr>
                                  }
                                </tbody>
                              </table>
                            </div>
                          }
                        }
                        <app-action-row classes="tw-pt-6">
                          <app-button
                            left1
                            [label]="
                              isClaimReportOptionsActive() ? 'Cancel' : 'Back'
                            "
                            emphasis="Low"
                            (click)="closeReportOptions(false)"
                          />
                          <app-button
                            right1
                            [label]="
                              isClaimReportOptionsActive()
                                ? 'Submit'
                                : 'Save options'
                            "
                            (click)="closeReportOptions(true)"
                          />
                        </app-action-row>
                      } @else {
                        <h2
                          id="query-page-wizard-step-output"
                          tabindex="-1"
                          class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                        >
                          {{
                            isBankingQuery
                              ? 'Select custom layout'
                              : 'Select Reports'
                          }}
                        </h2>
                        <div
                          class="tw-pb-4 tw-min-w-0 tw-max-w-full"
                          [ngClass]="
                            isBankingQuery
                              ? ''
                              : 'tw-grid tw-grid-cols-[260px_minmax(0,1fr)] tw-gap-6'
                          "
                        >
                          @if (!isBankingQuery) {
                            <div
                              class="tw-bg-[var(--sed-open-grey-color)] tw-rounded-md tw-p-4 tw-h-fit"
                              role="tablist"
                              aria-label="Report output type"
                              aria-orientation="vertical"
                            >
                              <button
                                type="button"
                                id="query-page-output-tab-reports"
                                role="tab"
                                class="tw-block tw-w-full tw-text-left tw-rounded tw-px-2 tw-py-1 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                                [class.tw-font-bold]="
                                  outputLayoutType === 'reports'
                                "
                                [class.tw-text-[var(--sed-black-60-color)]]="
                                  outputLayoutType !== 'reports'
                                "
                                [attr.aria-selected]="
                                  outputLayoutType === 'reports'
                                "
                                tabindex="0"
                                aria-controls="query-page-output-layout-panel"
                                (keydown)="
                                  onOutputLayoutTabKeydown($event, 'reports')
                                "
                                (click)="selectOutputLayoutType('reports')"
                              >
                                Report Layouts
                              </button>
                              <button
                                type="button"
                                id="query-page-output-tab-custom"
                                role="tab"
                                class="tw-block tw-w-full tw-text-left tw-pt-2 tw-rounded tw-px-2 tw-py-1 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                                [class.tw-font-bold]="
                                  outputLayoutType === 'custom'
                                "
                                [class.tw-text-[var(--sed-black-60-color)]]="
                                  outputLayoutType !== 'custom'
                                "
                                [attr.aria-selected]="
                                  outputLayoutType === 'custom'
                                "
                                tabindex="0"
                                aria-controls="query-page-output-layout-panel"
                                (keydown)="
                                  onOutputLayoutTabKeydown($event, 'custom')
                                "
                                (click)="selectOutputLayoutType('custom')"
                              >
                                Custom Layouts
                              </button>
                            </div>
                          }
                          <div
                            id="query-page-output-layout-panel"
                            class="tw-min-w-0 tw-max-w-full tw-overflow-hidden"
                            [attr.role]="
                              !isBankingQuery ? 'tabpanel' : undefined
                            "
                            [attr.aria-labelledby]="
                              !isBankingQuery
                                ? outputLayoutType === 'reports'
                                  ? 'query-page-output-tab-reports'
                                  : 'query-page-output-tab-custom'
                                : undefined
                            "
                          >
                            @if (
                              !isBankingQuery && outputLayoutType === 'reports'
                            ) {
                              <div
                                class="tw-grid tw-grid-cols-[1fr_auto_1fr] tw-gap-4 tw-items-center"
                              >
                                <div>
                                  <label
                                    for="query-page-available-reports"
                                    class="tw-font-bold tw-pb-2 tw-block"
                                    >Available Reports</label
                                  >
                                  <select
                                    id="query-page-available-reports"
                                    #availableReportsSelect
                                    multiple
                                    class="tw-w-full tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-2"
                                  >
                                    @for (
                                      report of availableReports;
                                      track report.reportId
                                    ) {
                                      <option [value]="report.reportId">
                                        {{ report.reportName }}
                                      </option>
                                    }
                                  </select>
                                </div>
                                <div class="tw-flex tw-flex-col tw-gap-2">
                                  <app-button
                                    label="→"
                                    ariaLabel="Add selected reports"
                                    (click)="
                                      addSelectedReports(availableReportsSelect)
                                    "
                                  />
                                  <app-button
                                    label="⇒"
                                    ariaLabel="Add all reports"
                                    (click)="addAllReports()"
                                  />
                                  <app-button
                                    label="←"
                                    ariaLabel="Remove selected reports"
                                    (click)="removeCheckedReports()"
                                  />
                                  <app-button
                                    label="⇐"
                                    ariaLabel="Remove all reports"
                                    (click)="removeAllReports()"
                                  />
                                </div>
                                <div>
                                  <div
                                    id="query-page-output-selected-reports-label"
                                    class="tw-font-bold tw-pb-2"
                                  >
                                    Selected Reports
                                  </div>
                                  <div
                                    class="tw-h-[220px] tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-overflow-auto tw-p-2"
                                    role="group"
                                    aria-labelledby="query-page-output-selected-reports-label"
                                  >
                                    @for (
                                      entry of selectedReports;
                                      track entry.report.reportId
                                    ) {
                                      <div
                                        class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-py-1"
                                      >
                                        <div
                                          class="tw-min-w-0 tw-flex-1 tw-pr-2"
                                        >
                                          <app-checkbox
                                            [control]="
                                              getSelectedReportCheckedControl(
                                                entry.report.reportId
                                              )
                                            "
                                            [label]="entry.report.reportName"
                                            [showHelpText]="false"
                                          />
                                        </div>
                                        <button
                                          type="button"
                                          class="focus-ring-icon-button tw-underline"
                                          [attr.aria-describedby]="
                                            'query-page-sr-report-options-' +
                                            entry.report.reportId
                                          "
                                          (click)="
                                            openReportOptions(
                                              entry.report.reportId
                                            )
                                          "
                                        >
                                          {{
                                            hasSavedOptions(
                                              entry.report.reportId
                                            )
                                              ? 'options *'
                                              : 'options'
                                          }}
                                        </button>
                                        <span
                                          class="tw-sr-only"
                                          [attr.id]="
                                            'query-page-sr-report-options-' +
                                            entry.report.reportId
                                          "
                                        >
                                          Opens output options for
                                          {{
                                            entry.report.reportName?.trim() ||
                                              'the selected report'
                                          }}
                                          , such as sort order, break levels,
                                          and other report-specific settings.
                                          @if (
                                            hasSavedOptions(
                                              entry.report.reportId
                                            )
                                          ) {
                                            Saved option settings exist for this
                                            report.
                                          }
                                        </span>
                                      </div>
                                    }
                                  </div>
                                </div>
                              </div>
                            } @else {
                              <div class="tw-min-w-0 tw-max-w-full">
                                @if (selectedCustomLayout) {
                                  <div
                                    class="tw-border tw-border-[var(--sed-black-60-color)] tw-rounded tw-p-4 tw-mb-4 tw-min-w-0 tw-max-w-full"
                                  >
                                    <div class="tw-font-bold tw-pb-2">
                                      Selected Layout
                                    </div>
                                    <div
                                      class="tw-grid tw-grid-cols-[auto_1fr] tw-gap-x-4 tw-gap-y-1"
                                    >
                                      <span
                                        class="tw-text-[var(--sed-black-60-color)]"
                                        >Owner:</span
                                      >
                                      <span>{{
                                        selectedCustomLayout.owner
                                      }}</span>
                                      <span
                                        class="tw-text-[var(--sed-black-60-color)]"
                                        >Name:</span
                                      >
                                      <span class="tw-break-words">{{
                                        selectedCustomLayout.name
                                      }}</span>
                                      <span
                                        class="tw-text-[var(--sed-black-60-color)]"
                                        >Description:</span
                                      >
                                      <span class="tw-break-words">{{
                                        selectedCustomLayout.description || '-'
                                      }}</span>
                                    </div>
                                    <button
                                      type="button"
                                      class="focus-ring-icon-button tw-underline tw-pt-2"
                                      (click)="removeCustomLayout()"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                }
                                <div
                                  class="tw-flex tw-flex-col tw-gap-2 tw-pb-2 tw-min-w-0"
                                >
                                  <div
                                    class="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2 tw-min-w-0"
                                  >
                                    <div class="tw-font-bold">
                                      Available Layouts
                                    </div>
                                    <app-button
                                      label="Create Layout"
                                      icon="fa-light fa-plus"
                                      emphasis="Low"
                                      (click)="navigateToCreateLayout()"
                                    />
                                  </div>
                                  <app-checkbox
                                    [control]="includePublicControl"
                                    label="Include Public"
                                  />
                                  <div
                                    class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-end tw-gap-2"
                                  >
                                    <app-input
                                      class="tw-w-full sm:tw-w-1/2 tw-min-w-0"
                                      [control]="layoutFieldSearchControl"
                                      label="Search by Field"
                                      placeholder="Enter field name..."
                                    />
                                    <app-button
                                      class="tw-w-full sm:tw-w-auto tw-pb-2 tw-self-end"
                                      label="Search"
                                      icon="fa-light fa-search"
                                      (click)="searchCustomLayoutsByField()"
                                    />
                                    @if (hasCustomLayoutFieldSearch) {
                                      <app-button
                                        class="tw-w-full sm:tw-w-auto tw-pb-2 tw-self-end"
                                        label="Reset"
                                        emphasis="Low"
                                        (click)="resetCustomLayoutFieldSearch()"
                                      />
                                    }
                                  </div>
                                </div>
                                <div
                                  class="tw-flex tw-flex-row tw-w-full tw-min-w-0 tw-max-w-full tw-overflow-x-auto"
                                >
                                  @if (customLayoutsLoading) {
                                    <app-empty-state
                                      height="300px"
                                      icon="fa-light fa-table-columns"
                                      label="Loading layouts"
                                      message="Retrieving custom layouts"
                                      class="tw-w-full"
                                    >
                                      <div class="tw-py-4">
                                        <app-progressbar />
                                      </div>
                                    </app-empty-state>
                                  } @else {
                                    <app-table
                                      class="tw-w-full tw-min-w-0 tw-block"
                                      [dataSource]="customLayouts"
                                      [showFilter]="true"
                                      [showRecordLabel]="false"
                                      [scrollable]="false"
                                      recordLabel="Layout"
                                      recordLabelPlural="Layouts"
                                      dataKey="layoutId"
                                      [pageSize]="10"
                                      [globalFilterFields]="[
                                        'owner',
                                        'name',
                                        'description',
                                      ]"
                                      filterPlaceholder="Search layouts"
                                      filterLabel="Search layouts"
                                    >
                                      <app-column
                                        name="owner"
                                        label="Owner"
                                        [sort]="true"
                                      />
                                      <app-column
                                        name="name"
                                        label="Name"
                                        [sort]="true"
                                      />
                                      <app-column
                                        name="description"
                                        label="Description"
                                      />
                                      <app-column
                                        name="actions"
                                        label=""
                                        width="160px"
                                      >
                                        <ng-template #cell let-row>
                                          <app-button
                                            [label]="
                                              selectedCustomLayout?.layoutId ===
                                              row.layoutId
                                                ? 'Selected'
                                                : 'Select'
                                            "
                                            [icon]="
                                              selectedCustomLayout?.layoutId ===
                                              row.layoutId
                                                ? 'fa-light fa-circle-check'
                                                : 'fa-light fa-circle'
                                            "
                                            [emphasis]="
                                              selectedCustomLayout?.layoutId ===
                                              row.layoutId
                                                ? 'High'
                                                : 'Low'
                                            "
                                            [ariaLabel]="
                                              (selectedCustomLayout?.layoutId ===
                                              row.layoutId
                                                ? 'Selected: '
                                                : 'Select ') +
                                              (row.name ||
                                                row.description ||
                                                row.owner ||
                                                'Layout')
                                            "
                                            (click)="selectCustomLayout(row)"
                                          />
                                        </ng-template>
                                      </app-column>
                                    </app-table>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      }
                      @if (!activeReportOptionReport) {
                        @if (outputStepError) {
                          <div
                            class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                            role="alert"
                          >
                            {{ outputStepError }}
                          </div>
                        }
                        <app-action-row classes="tw-pt-6">
                          <app-button
                            left1
                            label="Previous"
                            emphasis="Low"
                            (click)="stepper.previous()"
                          />
                          <app-button
                            right1
                            label="Next"
                            [disabled]="!canProceedFromOutput()"
                            (click)="goNextFromOutput()"
                          />
                        </app-action-row>
                      }
                    </div>
                  </ng-template>
                </app-step>

                <app-step
                  name="general"
                  label="General parameters"
                  icon="fa-circle-4"
                >
                  <ng-template>
                    <div
                      class="tw-p-6"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      <h2
                        id="query-page-wizard-step-general"
                        tabindex="-1"
                        class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                      >
                        General Parameters
                      </h2>

                      @if (showGeneralParametersCoreForm) {
                        <section
                          id="qp-general"
                          class="tw-scroll-mt-4 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[var(--sed-engaged-black-color)]"
                          tabindex="-1"
                        >
                          <div
                            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4"
                          >
                            <app-select
                              [control]="form.controls.reportIncludeType"
                              [options]="reportIncludeTypeOptions"
                              optionLabel="label"
                              optionValue="value"
                              label="Claims/Incidents"
                            />
                            <app-date-picker
                              [control]="form.controls.asOfDate"
                              label="As of Date"
                            />
                            <app-select
                              [control]="form.controls.linesOfBusiness"
                              [options]="lobOptions"
                              optionLabel="label"
                              optionValue="value"
                              optionDisabled="disabled"
                              label="Lines of Business"
                              [multiple]="true"
                              [chip]="true"
                              [multipleFilter]="false"
                              [showToggleAll]="false"
                              [showHeader]="false"
                            />
                          </div>
                        </section>

                        <section
                          id="qp-claim-filters"
                          class="tw-scroll-mt-4 tw-pt-4 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[var(--sed-engaged-black-color)]"
                          tabindex="-1"
                        >
                          <h3 class="tw-font-semibold tw-text-lg tw-pb-2">
                            Claim Status
                          </h3>
                          <div class="tw-flex tw-flex-wrap tw-gap-4">
                            <app-checkbox
                              [control]="form.controls.claimOpen"
                              label="Open"
                            />
                            <app-checkbox
                              [control]="form.controls.claimClosed"
                              label="Closed"
                            />
                            <app-checkbox
                              [control]="form.controls.claimDeleted"
                              label="Deleted"
                            />
                          </div>
                        </section>

                        <section
                          id="qp-date-ranges"
                          class="tw-scroll-mt-4 tw-pt-4 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[var(--sed-engaged-black-color)]"
                          tabindex="-1"
                        >
                          <h3 class="tw-font-semibold tw-text-lg tw-pb-2">
                            Date Ranges
                          </h3>
                          <div
                            class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4"
                          >
                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)]"
                            >
                              Loss Date
                            </div>
                            <app-date-picker
                              [control]="form.controls.lossDateFrom"
                              label="From"
                              screenReaderLabelPrefix="Loss Date"
                            />
                            <app-date-picker
                              [control]="form.controls.lossDateThrough"
                              label="Through"
                              screenReaderLabelPrefix="Loss Date"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Claim Opened
                            </div>
                            <app-date-picker
                              [control]="form.controls.openedDateFrom"
                              label="From"
                              screenReaderLabelPrefix="Claim Opened"
                            />
                            <app-date-picker
                              [control]="form.controls.openedDateThrough"
                              label="Through"
                              screenReaderLabelPrefix="Claim Opened"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Claim Closed
                            </div>
                            <app-date-picker
                              [control]="form.controls.closedDateFrom"
                              label="From"
                              screenReaderLabelPrefix="Claim Closed"
                            />
                            <app-date-picker
                              [control]="form.controls.closedDateThrough"
                              label="Through"
                              screenReaderLabelPrefix="Claim Closed"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Claim Reopened
                            </div>
                            <app-date-picker
                              [control]="form.controls.reopenedDateFrom"
                              label="From"
                              screenReaderLabelPrefix="Claim Reopened"
                            />
                            <app-date-picker
                              [control]="form.controls.reopenedDateThrough"
                              label="Through"
                              screenReaderLabelPrefix="Claim Reopened"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Reported to Client
                            </div>
                            <app-date-picker
                              [control]="form.controls.reportedToClientFrom"
                              label="From"
                              screenReaderLabelPrefix="Reported to Client"
                            />
                            <app-date-picker
                              [control]="form.controls.reportedToClientThrough"
                              label="Through"
                              screenReaderLabelPrefix="Reported to Client"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Reported to SCMS
                            </div>
                            <app-date-picker
                              [control]="form.controls.reportedToCmsFrom"
                              label="From"
                              screenReaderLabelPrefix="Reported to SCMS"
                            />
                            <app-date-picker
                              [control]="form.controls.reportedToCmsThrough"
                              label="Through"
                              screenReaderLabelPrefix="Reported to SCMS"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Last Update
                            </div>
                            <app-date-picker
                              [control]="form.controls.lastUpdatedFrom"
                              label="From"
                              screenReaderLabelPrefix="Last Update"
                            />
                            <app-date-picker
                              [control]="form.controls.lastUpdatedThrough"
                              label="Through"
                              screenReaderLabelPrefix="Last Update"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Last Financial Update
                            </div>
                            <app-date-picker
                              [control]="form.controls.lastFinancialUpdateFrom"
                              label="From"
                              screenReaderLabelPrefix="Last Financial Update"
                            />
                            <app-date-picker
                              [control]="
                                form.controls.lastFinancialUpdateThrough
                              "
                              label="Through"
                              screenReaderLabelPrefix="Last Financial Update"
                            />
                            <div></div>

                            <div
                              class="tw-col-span-1 md:tw-col-span-3 tw-font-medium tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              Payment Date
                            </div>
                            <app-date-picker
                              [control]="form.controls.paymentDateFrom"
                              label="From"
                              screenReaderLabelPrefix="Payment Date"
                            />
                            <app-date-picker
                              [control]="form.controls.paymentDateThrough"
                              label="Through"
                              screenReaderLabelPrefix="Payment Date"
                            />
                            <div></div>
                          </div>
                        </section>
                      }
                      @if (showParameterPanelHost) {
                        <div
                          class="tw-pt-6 tw-mt-6 tw-border-t tw-border-[var(--sed-black-60-color)]"
                        >
                          <div
                            id="qp-parameter-panel"
                            class="tw-scroll-mt-4 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[var(--sed-engaged-black-color)]"
                            tabindex="-1"
                          >
                            <app-query-parameter-panel-host
                              [panelKey]="parameterActiveKey"
                              [queryForm]="form"
                              [extended]="extendedParamsForm"
                              [contractNumber]="
                                form.controls.contractNumber.value
                              "
                              [queryUniqueKey]="savedQueryId ?? editQueryId"
                              [parameterPanelRefreshToken]="
                                parameterPanelRefreshToken
                              "
                              [locationSelectionMethodCode]="
                                editDetailLocationMethodCode
                              "
                              [queryType]="form.controls.queryType.value ?? 'C'"
                              [contractLinesOfBusiness]="
                                generalDefaults?.availableLinesOfBusiness ?? []
                              "
                            />
                          </div>
                        </div>
                      }

                      @if (generalStepError) {
                        <div
                          class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mt-6 tw-text-[var(--sed-engaged-black-color)]"
                          role="alert"
                        >
                          {{ generalStepError }}
                        </div>
                      }
                      <app-action-row classes="tw-pt-6">
                        <app-button
                          left1
                          label="Previous"
                          emphasis="Low"
                          (click)="stepper.previous()"
                        />
                        <app-button
                          right1
                          label="Review and save"
                          (click)="goNextFromGeneral()"
                        />
                      </app-action-row>
                    </div>
                  </ng-template>
                </app-step>

                <app-step name="save" label="Save query" icon="fa-circle-5">
                  <ng-template>
                    <div
                      class="tw-p-6"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      <h2
                        id="query-page-wizard-step-save"
                        tabindex="-1"
                        class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                      >
                        Save Query
                      </h2>

                      <section
                        id="qp-query-details"
                        class="tw-scroll-mt-4 tw-rounded tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-2 focus:tw-ring-[var(--sed-engaged-black-color)] tw-pb-4"
                        tabindex="-1"
                      >
                        <h3 class="tw-font-semibold tw-text-lg tw-pb-4">
                          Query details
                        </h3>
                        <div
                          class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4"
                        >
                          <app-input
                            #nameInput
                            [control]="form.controls.name"
                            label="Query Name"
                            helpText="3-8 characters, letters, numbers, dashes, and underscores only"
                          />
                          <app-input
                            [control]="form.controls.description"
                            label="Description"
                            helpText="4-40 characters (optional)"
                          />
                        </div>

                        <div class="tw-pt-2 tw-flex tw-flex-wrap tw-gap-4">
                          <app-checkbox
                            [control]="form.controls.isPrivate"
                            label="Private Query"
                          />
                          <app-checkbox
                            [control]="form.controls.viewSpii"
                            label="View SPII"
                            [ariaDescribedBy]="
                              !canViewSpii
                                ? 'qp-spii-auth-notice-save'
                                : undefined
                            "
                          />
                          @if (!canViewSpii) {
                            <span
                              id="qp-spii-auth-notice-save"
                              class="tw-text-sm tw-text-[var(--sed-black-60-color)] tw-pt-2"
                            >
                              You are not authorized for Query SPII access.
                            </span>
                          }
                        </div>
                      </section>

                      <div
                        class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-pb-4"
                      >
                        <div>
                          <b>Query Type:</b> {{ form.controls.queryType.value }}
                        </div>
                        <div>
                          <b>Contract:</b>
                          {{ form.controls.contractNumber.value }}
                        </div>
                        @if (outputLayoutType === 'reports') {
                          <div>
                            <b>Reports Selected:</b>
                            {{ form.controls.selectedReportIds.value.length }}
                          </div>
                        } @else {
                          <div>
                            <b>Custom Layout:</b>
                            {{ selectedCustomLayout?.name }}
                          </div>
                        }
                        <div>
                          <b>LOB Selected:</b>
                          {{ form.controls.linesOfBusiness.value.length }}
                        </div>
                      </div>

                      @if (saveError) {
                        <div
                          class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                          role="alert"
                        >
                          {{ saveError }}
                        </div>
                      }
                      @if (saveSuccess) {
                        <div
                          class="tw-bg-[color-mix(in_srgb,var(--sed-status-green-color)_12%,white)] tw-border tw-border-[var(--sed-status-green-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                          role="status"
                        >
                          {{ saveSuccess }}
                        </div>
                      }

                      <app-action-row classes="tw-pt-6">
                        <app-button
                          left1
                          label="Previous"
                          emphasis="Low"
                          (click)="stepper.previous()"
                        />
                        <app-button
                          right1
                          [loading]="saving"
                          label="Save Query"
                          (click)="saveA1()"
                        />
                        @if (savedQueryId) {
                          <app-button
                            right2
                            label="Schedule"
                            emphasis="Low"
                            (click)="goToScheduleStep()"
                          />
                        }
                      </app-action-row>
                    </div>
                  </ng-template>
                </app-step>

                <app-step name="schedule" label="Schedule" icon="fa-circle-6">
                  <ng-template>
                    <div
                      class="tw-p-6"
                      (keydown)="onQueryWizardKeydownDelegate($event)"
                    >
                      <h2
                        id="query-page-wizard-step-schedule"
                        tabindex="-1"
                        class="tw-pb-4 tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-offset-2 focus-visible:tw-ring-[var(--sed-engaged-black-color)]"
                      >
                        Schedule Query
                      </h2>
                      <p class="tw-pb-4 tw-text-[var(--sed-black-60-color)]">
                        Optionally schedule this query to run automatically at a
                        specified time.
                      </p>

                      @if (
                        form.controls.name.value ||
                        form.controls.description.value
                      ) {
                        <div
                          class="tw-bg-[var(--sed-grey-40-color)] tw-rounded tw-p-4 tw-mb-4"
                        >
                          <table class="tw-w-full tw-text-sm">
                            <tr>
                              <th
                                class="tw-text-left tw-pr-4 tw-py-1 tw-font-semibold"
                              >
                                Name:
                              </th>
                              <td class="tw-py-1">
                                {{ form.controls.name.value || '&nbsp;' }}
                              </td>
                              <th
                                class="tw-text-left tw-pr-4 tw-pl-6 tw-py-1 tw-font-semibold"
                              >
                                Description:
                              </th>
                              <td class="tw-py-1">
                                {{
                                  form.controls.description.value || '&nbsp;'
                                }}
                              </td>
                            </tr>
                          </table>
                        </div>
                      }

                      @if (scheduleError) {
                        <div
                          class="tw-bg-[color-mix(in_srgb,var(--sed-status-red-color)_12%,white)] tw-border tw-border-[var(--sed-status-red-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                          role="alert"
                        >
                          {{ scheduleError }}
                        </div>
                      }
                      @if (scheduleSuccess) {
                        <div
                          class="tw-bg-[color-mix(in_srgb,var(--sed-status-green-color)_12%,white)] tw-border tw-border-[var(--sed-status-green-color)] tw-rounded tw-p-3 tw-mb-3 tw-text-[var(--sed-engaged-black-color)]"
                          role="status"
                        >
                          {{ scheduleSuccess }}
                        </div>
                      }

                      <div class="tw-max-w-full sm:tw-max-w-[600px]">
                        <div
                          class="tw-pb-4 tw-flex tw-flex-wrap tw-gap-4 tw-items-end"
                        >
                          <div class="tw-w-full sm:tw-min-w-[140px] tw-min-w-0">
                            <app-select
                              [control]="scheduleForm.controls.frequency"
                              label="Frequency"
                              [options]="scheduleFrequencyOptions"
                              optionLabel="label"
                              optionValue="value"
                            />
                          </div>
                          <div class="tw-w-full sm:tw-min-w-[140px] tw-min-w-0">
                            <app-date-picker
                              [control]="scheduleForm.controls.runDate"
                              label="Run Date"
                              [minDate]="scheduleMinDate"
                            />
                          </div>
                          <div class="tw-flex tw-gap-2 tw-items-end">
                            <span class="tw-font-semibold tw-pb-2"
                              >Run Time:</span
                            >
                            <app-input
                              [control]="scheduleForm.controls.runTimeHour"
                              label=""
                              type="Integer"
                              placeholder="HH"
                              classes="tw-w-14"
                              [min]="1"
                              [max]="12"
                            />
                            <span class="tw-pb-2">:</span>
                            <app-input
                              [control]="scheduleForm.controls.runTimeMinute"
                              label=""
                              type="Integer"
                              placeholder="MM"
                              classes="tw-w-14"
                              [min]="0"
                              [max]="59"
                            />
                            <app-select
                              [control]="scheduleForm.controls.runTimeAmPm"
                              label=""
                              [options]="runTimeAmPmOptions"
                              optionLabel="label"
                              optionValue="value"
                              [filter]="false"
                              [showClear]="false"
                              classes="tw-w-20"
                            />
                          </div>
                        </div>

                        @if (
                          scheduleForm.controls.frequency.value === 'weekly'
                        ) {
                          <div class="tw-pb-4">
                            <app-select
                              [control]="scheduleForm.controls.dayOfWeek"
                              label="Day of Week"
                              [options]="dayOfWeekOptions"
                              optionLabel="label"
                              optionValue="value"
                            />
                          </div>
                        }

                        @if (
                          scheduleForm.controls.frequency.value === 'monthly'
                        ) {
                          <div class="tw-pb-4">
                            <app-select
                              [control]="scheduleForm.controls.dayOfMonth"
                              label="Day of Month"
                              [options]="dayOfMonthOptions"
                              optionLabel="label"
                              optionValue="value"
                            />
                          </div>
                        }
                      </div>

                      @if (scheduleForm.controls.frequency.value !== 'once') {
                        <ng-container
                          *ngTemplateOutlet="queryRecurringScheduleOptionsTpl"
                        />
                      }

                      <app-action-row classes="tw-pt-6">
                        <app-button
                          left1
                          label="Previous"
                          emphasis="Low"
                          (click)="stepper.previous()"
                        />
                        <app-button
                          left2
                          label="Skip"
                          emphasis="Low"
                          (click)="skipSchedule()"
                        />
                        <app-button
                          right1
                          [loading]="scheduling"
                          label="Create Schedule"
                          (click)="createSchedule()"
                        />
                      </app-action-row>
                    </div>
                  </ng-template>
                </app-step>
              </app-stepper>
            }
          }
        </section>
      </div>
    </app-home-card>
  `,
  styles: `
    :host ::ng-deep #query-page-output-layout-panel .p-datatable {
      max-width: 100%;
    }

    :host
      ::ng-deep
      #query-page-output-layout-panel
      .p-datatable-table-container,
    :host ::ng-deep #query-page-output-layout-panel .p-datatable-wrapper {
      max-width: 100%;
    }

    :host ::ng-deep #query-page-output-layout-panel .p-datatable-table {
      width: 100%;
      table-layout: fixed;
    }

    :host
      ::ng-deep
      #query-page-output-layout-panel
      .p-datatable-tbody
      > tr
      > td,
    :host
      ::ng-deep
      #query-page-output-layout-panel
      .p-datatable-thead
      > tr
      > th {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    :host
      ::ng-deep
      #query-page-output-layout-panel
      .p-datatable-tbody
      > tr
      > td:last-child,
    :host
      ::ng-deep
      #query-page-output-layout-panel
      .p-datatable-thead
      > tr
      > th:last-child {
      overflow: visible;
      white-space: normal;
    }
  `,
})
export class QueryPageComponent extends BaseComponent implements OnInit {
  @ViewChild('stepper')
  stepper?: StepperComponent;

  @ViewChild('nameInput')
  nameInput?: InputComponent;

  @ViewChild('descriptionInput')
  descriptionInput?: InputComponent;

  private _activeSection: QuerySectionKey | null = null;
  get activeSection(): QuerySectionKey | null {
    return this._activeSection;
  }
  set activeSection(value: QuerySectionKey | null) {
    this._activeSection = value;
    this.syncDocumentTitle();
  }
  canViewSpii = false;
  saving = false;
  saveError = '';
  saveSuccess = '';
  /** Validation message on Select output step (break levels, etc.). */
  outputStepError = '';
  /** Validation message on General parameters step (shown before Review and save). */
  generalStepError = '';
  /** Legacy QueryWizardParameters.aspx valcClaimStatus ErrorMessage. */
  readonly claimStatusRequiredMessage =
    'You must choose at least one Claim Status';
  readonly linesOfBusinessRequiredMessage =
    'Select at least one line of business under Major Classification.';
  savedQueryId: number | null = null;
  /** From GET query detail: PKG_QUERY.GetLocationSelectionType_p (A/U/S); drives Locations sidebar control. */
  editDetailLocationMethodCode: string | null = null;
  editLayoutId: number | null = null;
  copyLayoutId: number | null = null;
  layoutViewOnly = false;
  layoutEditorReturnContext: LayoutEditorReturnContext | null = null;
  editQueryId: number | null = null;
  /**
   * Incremented after each successful `loadQueryForEdit` GET so parameter panels clear
   * `loadedParameterResources` and refetch banking/payments (avoids stale UI when the DB
   * was updated elsewhere, e.g. legacy Payments/Banks).
   */
  parameterPanelRefreshToken = 0;
  editMode = false;
  requestInitialStep: string | null = null;
  /** Wizard step name from `?step=` (mirrors app-stepper). */
  requestWizardStep: string | null = null;
  /** Legacy QueryDefineParametersMenu selection. */
  parameterActiveKey: ParameterSideMenuKey = 'General';
  parameterMenuExpandedSimple = true;
  parameterMenuExpandedAdvanced = false;
  private isLoadingForEdit = false;

  scheduling = false;
  isLoadingScheduleQuery = false;
  scheduleError = '';
  scheduleSuccess = '';
  scheduleMinDate = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  private readonly liveAnnouncer = inject(LiveAnnouncer);
  private readonly snackbar = inject(Snackbar);
  private readonly pageTitleService = inject(OmniInsightsTitleService);

  scheduleForm = new FormGroup({
    frequency: new FormControl<string>('once'),
    runDate: new FormControl<Date | null>(
      this.getDefaultScheduleDateTime().date,
    ),
    runTimeHour: new FormControl<number>(
      this.getDefaultScheduleDateTime().hour,
      { nonNullable: true },
    ),
    runTimeMinute: new FormControl<number>(
      this.getDefaultScheduleDateTime().minute,
      { nonNullable: true },
    ),
    runTimeAmPm: new FormControl<'AM' | 'PM'>(
      this.getDefaultScheduleDateTime().amPm,
      { nonNullable: true },
    ),
    dayOfWeek: new FormControl<number>(0),
    dayOfMonth: new FormControl<number>(1),
    incrementAsOfDate: new FormControl<boolean>(true, { nonNullable: true }),
    lossDateFrom: new FormControl<boolean>(true, { nonNullable: true }),
    lossDateThrough: new FormControl<boolean>(true, { nonNullable: true }),
    claimOpenedFrom: new FormControl<boolean>(true, { nonNullable: true }),
    claimOpenedThrough: new FormControl<boolean>(true, { nonNullable: true }),
    claimClosedFrom: new FormControl<boolean>(true, { nonNullable: true }),
    claimClosedThrough: new FormControl<boolean>(true, { nonNullable: true }),
    claimReopenedFrom: new FormControl<boolean>(true, { nonNullable: true }),
    claimReopenedThrough: new FormControl<boolean>(true, { nonNullable: true }),
    reportedToClientFrom: new FormControl<boolean>(true, { nonNullable: true }),
    reportedToClientThrough: new FormControl<boolean>(true, {
      nonNullable: true,
    }),
    reportedToCmsFrom: new FormControl<boolean>(true, { nonNullable: true }),
    reportedToCmsThrough: new FormControl<boolean>(true, { nonNullable: true }),
    lastUpdatedFrom: new FormControl<boolean>(true, { nonNullable: true }),
    lastUpdatedThrough: new FormControl<boolean>(true, { nonNullable: true }),
    lastFinancialUpdateFrom: new FormControl<boolean>(true, {
      nonNullable: true,
    }),
    lastFinancialUpdateThrough: new FormControl<boolean>(true, {
      nonNullable: true,
    }),
    paymentDateFrom: new FormControl<boolean>(true, { nonNullable: true }),
    paymentDateThrough: new FormControl<boolean>(true, { nonNullable: true }),
  });

  scheduleFrequencyOptions = [
    { label: 'Once', value: 'once' },
    { label: 'Daily', value: 'daily' },
    { label: 'Weekly', value: 'weekly' },
    { label: 'Monthly', value: 'monthly' },
    { label: 'Quarterly', value: 'quarterly' },
    { label: 'Semi-Annual', value: 'semi-annual' },
    { label: 'Annual', value: 'annual' },
  ];

  runTimeAmPmOptions = [
    { label: 'AM', value: 'AM' as const },
    { label: 'PM', value: 'PM' as const },
  ];

  private twelveHourPartsFrom24(
    hour24: number,
    minute: number,
  ): { hour: number; minute: number; amPm: 'AM' | 'PM' } {
    return {
      hour: hour24 % 12 || 12,
      minute,
      amPm: hour24 >= 12 ? 'PM' : 'AM',
    };
  }

  /** Browser-local fallback when server clock is unavailable. */
  private getDefaultScheduleDateTime(): {
    date: Date;
    hour: number;
    minute: number;
    amPm: 'AM' | 'PM';
  } {
    const now = new Date();
    const parts = this.twelveHourPartsFrom24(now.getHours(), now.getMinutes());
    return {
      date: DateEx.todayDateOnly(),
      ...parts,
    };
  }

  /** Legacy uses web server `DateTime.Now` (see `Schedule.cs`); match that, not the browser clock. */
  private fromServerClock(s: QueryScheduleDefaultTimeResponse): {
    date: Date;
    hour: number;
    minute: number;
    amPm: 'AM' | 'PM';
  } {
    const parts = this.twelveHourPartsFrom24(s.hour, s.minute);
    return {
      date: new Date(s.year, s.month - 1, s.day),
      ...parts,
    };
  }

  /** Legacy `Schedule` ctor sets `RunDate = DateTime.Now`; refresh when the schedule UI is shown. */
  private async patchScheduleRunDateTimeToNow(): Promise<void> {
    try {
      const s = await this.api.query.getScheduleDefaultTime();
      const d = this.fromServerClock(s);
      this.scheduleForm.patchValue({
        runDate: d.date,
        runTimeHour: d.hour,
        runTimeMinute: d.minute,
        runTimeAmPm: d.amPm,
      });
    } catch {
      const d = this.getDefaultScheduleDateTime();
      this.scheduleForm.patchValue({
        runDate: d.date,
        runTimeHour: d.hour,
        runTimeMinute: d.minute,
        runTimeAmPm: d.amPm,
      });
    }
  }

  /** Legacy `Schedule` private field defaults (all increment flags true). */
  private resetScheduleFormToLegacyDefaults(): void {
    this.queryScheduleIncrementFlags = {
      beginSelected: true,
      endSelected: true,
    };
    this.scheduleForm.patchValue(
      {
        frequency: 'once',
        dayOfWeek: 0,
        dayOfMonth: 1,
        incrementAsOfDate: true,
        lossDateFrom: true,
        lossDateThrough: true,
        claimOpenedFrom: true,
        claimOpenedThrough: true,
        claimClosedFrom: true,
        claimClosedThrough: true,
        claimReopenedFrom: true,
        claimReopenedThrough: true,
        reportedToClientFrom: true,
        reportedToClientThrough: true,
        reportedToCmsFrom: true,
        reportedToCmsThrough: true,
        lastUpdatedFrom: true,
        lastUpdatedThrough: true,
        lastFinancialUpdateFrom: true,
        lastFinancialUpdateThrough: true,
        paymentDateFrom: true,
        paymentDateThrough: true,
      },
      { emitEvent: false },
    );
  }

  private mapApiScheduleFrequencyToForm(
    frequency: string | null | undefined,
  ): string {
    const normalized = (frequency ?? '').trim().toLowerCase();
    switch (normalized) {
      case 'once':
      case 'onetime':
        return 'once';
      case 'daily':
        return 'daily';
      case 'weekly':
        return 'weekly';
      case 'monthly':
        return 'monthly';
      case 'quarterly':
        return 'quarterly';
      case 'semi-annual':
      case 'semiannual':
        return 'semi-annual';
      case 'annual':
        return 'annual';
      default:
        return 'once';
    }
  }

  private applyScheduleHydrationFromDetail(detail: QueryDetailResponse): void {
    const schedule = detail.schedule;
    if (!schedule) {
      return;
    }

    this.scheduleForm.patchValue(
      {
        frequency: this.mapApiScheduleFrequencyToForm(schedule.frequency),
        incrementAsOfDate: schedule.incrementAsOfDateSelected ?? true,
        lossDateFrom: schedule.lossDateFromSelected ?? true,
        lossDateThrough: schedule.lossDateThroughSelected ?? true,
        claimOpenedFrom: schedule.claimOpenedFromSelected ?? true,
        claimOpenedThrough: schedule.claimOpenedThroughSelected ?? true,
        claimClosedFrom: schedule.claimClosedFromSelected ?? true,
        claimClosedThrough: schedule.claimClosedThroughSelected ?? true,
        claimReopenedFrom: schedule.claimReopenedFromSelected ?? true,
        claimReopenedThrough: schedule.claimReopenedThroughSelected ?? true,
        reportedToClientFrom: schedule.reportedToClientFromSelected ?? true,
        reportedToClientThrough:
          schedule.reportedToClientThroughSelected ?? true,
        reportedToCmsFrom: schedule.reportedToCmsFromSelected ?? true,
        reportedToCmsThrough: schedule.reportedToCmsThroughSelected ?? true,
        lastUpdatedFrom: schedule.lastUpdatedFromSelected ?? true,
        lastUpdatedThrough: schedule.lastUpdatedThroughSelected ?? true,
        lastFinancialUpdateFrom:
          schedule.lastFinancialUpdateFromSelected ?? true,
        lastFinancialUpdateThrough:
          schedule.lastFinancialUpdateThroughSelected ?? true,
        paymentDateFrom: schedule.paymentDateFromSelected ?? true,
        paymentDateThrough: schedule.paymentDateThroughSelected ?? true,
      },
      { emitEvent: false },
    );

    this.queryScheduleIncrementFlags = {
      beginSelected: schedule.workStatusBeginDateSelected ?? true,
      endSelected: schedule.workStatusEndDateSelected ?? true,
    };
  }

  private buildScheduleRelativeDatesPayload() {
    const sf = this.scheduleForm.controls;
    return {
      incrementAsOfDate: sf.incrementAsOfDate.value,
      lossDateFrom: sf.lossDateFrom.value,
      lossDateThrough: sf.lossDateThrough.value,
      claimOpenedFrom: sf.claimOpenedFrom.value,
      claimOpenedThrough: sf.claimOpenedThrough.value,
      claimClosedFrom: sf.claimClosedFrom.value,
      claimClosedThrough: sf.claimClosedThrough.value,
      claimReopenedFrom: sf.claimReopenedFrom.value,
      claimReopenedThrough: sf.claimReopenedThrough.value,
      reportedToClientFrom: sf.reportedToClientFrom.value,
      reportedToClientThrough: sf.reportedToClientThrough.value,
      reportedToCmsFrom: sf.reportedToCmsFrom.value,
      reportedToCmsThrough: sf.reportedToCmsThrough.value,
      lastUpdatedFrom: sf.lastUpdatedFrom.value,
      lastUpdatedThrough: sf.lastUpdatedThrough.value,
      lastFinancialUpdateFrom: sf.lastFinancialUpdateFrom.value,
      lastFinancialUpdateThrough: sf.lastFinancialUpdateThrough.value,
      paymentDateFrom: sf.paymentDateFrom.value,
      paymentDateThrough: sf.paymentDateThrough.value,
      workStatusBeginDateSelected:
        this.queryScheduleIncrementFlags.beginSelected,
      workStatusEndDateSelected: this.queryScheduleIncrementFlags.endSelected,
    };
  }

  private async loadQueryForScheduleOnly(query: QueryListItem): Promise<void> {
    this.isLoadingScheduleQuery = true;
    this.scheduleError = '';
    try {
      this.resetScheduleFormToLegacyDefaults();
      const detail = await this.api.query.getQuery(query.queryId);
      this.form.patchValue(
        {
          name: detail.saveOptions.name ?? query.name,
          description: detail.saveOptions.description ?? query.description,
          selectedReportIds: detail.selectedReportIds ?? [],
          ...generalDateFieldsFromQueryDetail(detail.general),
        },
        { emitEvent: false },
      );
      this.applyScheduleHydrationFromDetail(detail);
      this.syncQueryScheduleIncrementForSelectedReports();
      await this.patchScheduleRunDateTimeToNow();
    } catch {
      this.scheduleError = 'Failed to load query details for scheduling.';
      this.activeSection = 'view-queries';
    } finally {
      this.isLoadingScheduleQuery = false;
    }
  }

  dayOfWeekOptions = [
    { label: 'Sunday', value: 0 },
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
  ];

  dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
    label: `${i + 1}`,
    value: i + 1,
  }));

  queryMenu: { key: QuerySectionKey; label: string }[] = [
    { key: 'request', label: 'Request Query/Report' },
    { key: 'view-queries', label: 'View Queries/Reports' },
    { key: 'create-layout', label: 'Create Custom Layout' },
    { key: 'view-layouts', label: 'View Custom Layouts' },
    { key: 'view-schedules', label: 'View Schedules' },
    { key: 'view-completed', label: 'View Completed Queries' },
    { key: 'view-suspended', label: 'View Suspended Queries/Reports' },
  ];

  queryTypeOptions: { label: string; value: string }[] = [
    { label: 'Claim Query', value: 'C' },
    { label: 'Payment Query', value: 'A' },
    // { label: 'Banking Query', value: 'B' }, // US 331143	Query - Remove Banking Query Type
    { label: 'Time Tracking Query', value: 'T' },
  ];

  /** Map API query type (name or code) to form option value (code) */
  private mapQueryTypeToCode(apiValue: string | null | undefined): string {
    const v = (apiValue ?? '').trim();
    if (!v) return 'C';
    const map: Record<string, string> = {
      C: 'C',
      A: 'A',
      B: 'B',
      T: 'T',
      Claim: 'C',
      Payment: 'A',
      Banking: 'B',
      'Time Tracking': 'T',
      TimeTracking: 'T',
    };
    return map[v] ?? v;
  }
  reportIncludeTypeOptions: { label: string; value: string }[] = [
    { label: 'Claims only', value: 'ClaimOnly' },
    { label: 'Incidents only', value: 'IncidentOnly' },
    { label: 'Incidents and Claims', value: 'IncidentAndClaim' },
  ];
  clientOptions: { label: string; value: number }[] = [];
  /** All contract clients for the Select Client step (loaded with paging until complete). */
  clientRows: QueryClientItem[] = [];
  /** Stable reference for the client table; selected row moved to top when applicable. */
  clientTableRows: QueryClientItem[] = [];
  /** Client name from saved query when the contract is not in the loaded client list. */
  selectedClientDisplayName: string | null = null;
  clientsLoading = false;
  availableReports: QueryReportItem[] = [];
  selectedReports: SelectedReportEntry[] = [];
  /** Checkbox state for Selected Reports list (pruned in `syncReportSelectionBuckets`). */
  private readonly selectedReportCheckedControlEntries = new Map<
    number,
    { control: FormControl<boolean>; sub: Subscription }
  >();
  lobOptions: LobSelectOption[] = [];
  activeReportOptionReport: QueryReportItem | null = null;
  reportOptionStates: Record<number, ReportOptionState> = {};
  reportOptionMetadata: Record<number, QueryReportOptionsMetadataResponse> = {};
  /** Legacy Query.Schedule work-status increment flags (shared by Reserve Change and Rentokil). */
  queryScheduleIncrementFlags = {
    beginSelected: true,
    endSelected: true,
  };
  readonly scheduleIncrementFromRows = QUERY_SCHEDULE_INCREMENT_FROM_ROWS;
  readonly scheduleIncrementThroughRows = QUERY_SCHEDULE_INCREMENT_THROUGH_ROWS;
  reportOptionState: ReportOptionState | null = null;
  reportOptionError = '';
  reportOptionLoading = false;
  payCodeSummarySortOptions = [
    { label: 'Pay Code', value: 'PAY CODE' },
    { label: 'Pay Code Totals', value: 'PAY CODE TOTALS' },
  ];

  /** Static dropdown options for query wizard Report Options (Select output step). */
  readonly reportOptionClaimReportTypeSelectItems: {
    label: string;
    value: string;
  }[] = [
    { label: 'Normal', value: QUERY_CLAIM_REPORT_TYPE_NORMAL_OPTION_VALUE },
    { label: 'Summary Only', value: 'S' },
    { label: 'More Detail', value: 'M' },
  ];
  readonly reportOptionFormType525Items: { label: string; value: string }[] = [
    {
      label: 'Select',
      value: QUERY_REPORT_FORM_TYPE_UNSELECTED_OPTION_VALUE,
    },
    { label: 'WC', value: 'WC' },
    { label: 'LIAB', value: 'LIAB' },
  ];
  readonly reportOptionPayCodeSeriesLabels: readonly string[] =
    REPORT_OPTION_PAY_CODE_SERIES_LABELS;
  readonly QUERY_RESERVE_CHANGE_REPORT_ID = QUERY_RESERVE_CHANGE_REPORT_ID;
  readonly reportOptionMinChangeUnitItems: {
    label: string;
    value: 'Dollar' | 'Percent';
  }[] = [
    { label: 'Dollar', value: 'Dollar' },
    { label: 'Percent', value: 'Percent' },
  ];
  readonly reportOptionDuration182Items: { label: string; value: string }[] = [
    { label: 'Open-Close', value: 'OPEN-CLOSE' },
    { label: 'Absence', value: 'ABSENCE' },
    { label: 'Benefits', value: 'BENEFITS' },
  ];
  readonly reportOptionStateOfItems: { label: string; value: string }[] = [
    { label: 'Jurisdiction', value: 'Jurisdiction' },
    { label: 'Loss', value: 'Loss' },
    { label: 'Pay/Prem', value: 'Pay/Prem' },
  ];
  readonly reportOptionCustomDatePeriodItems: {
    label: string;
    value: string;
  }[] = [
    { label: 'Days', value: 'D' },
    { label: 'Months', value: 'M' },
    { label: 'Years', value: 'Y' },
  ];
  readonly reportOptionMiscFieldValueItems: { label: string; value: string }[] =
    [
      { label: 'Do not Include', value: 'Do not Include' },
      { label: '-ALL-', value: '-ALL-' },
      { label: '-ALL EXCEPT BLANKS-', value: '-ALL EXCEPT BLANKS-' },
      { label: '-BLANKS ONLY-', value: '-BLANKS ONLY-' },
    ];

  /**
   * Single-value Report Options bound to `app-select`; flushed into
   * `reportOptionState` before validate/save.
   */
  reportOptionScalarForm = new FormGroup({
    reportType: new FormControl<string>(
      QUERY_CLAIM_REPORT_TYPE_NORMAL_OPTION_VALUE,
      {
        nonNullable: true,
      },
    ),
    claimId: new FormControl<string>('', { nonNullable: true }),
    sortBy: new FormControl<string>('', { nonNullable: true }),
    formType: new FormControl<string>(
      QUERY_REPORT_FORM_TYPE_UNSELECTED_OPTION_VALUE,
      { nonNullable: true },
    ),
    minChangeUnit: new FormControl<'Dollar' | 'Percent'>('Dollar', {
      nonNullable: true,
    }),
    duration: new FormControl<string>('OPEN-CLOSE', { nonNullable: true }),
    selectedBreakLevel: new FormControl<string>(
      QUERY_REPORT_BREAK_LEVEL_NONE_OPTION_VALUE,
      { nonNullable: true },
    ),
    selectedBankNumber: new FormControl<string>('', { nonNullable: true }),
    summarySort: new FormControl<string>('PAY CODE', { nonNullable: true }),
    reserveFromDate: new FormControl<Date | null>(null),
    reserveToDate: new FormControl<Date | null>(null),
    minChange: new FormControl<string>('', { nonNullable: true }),
    maxClaimCount: new FormControl<string>('', { nonNullable: true }),
    lastCsrRunDate: new FormControl<Date | null>(null),
  });

  /** Report Options checkboxes (state synced via listeners or flush for per-level). */
  reportOptionReportCheckboxForm = new FormGroup({
    workStatusBeginDateSelected: new FormControl(false, { nonNullable: true }),
    workStatusEndDateSelected: new FormControl(false, { nonNullable: true }),
    includePaymentDetail: new FormControl(false, { nonNullable: true }),
    payCodeAllSeries: new FormControl(false, { nonNullable: true }),
  });

  private readonly reportOptionLevelCheckboxControls = new WeakMap<
    SelectedLevelOption,
    {
      pageBreak: FormControl<boolean>;
      structureIntermediate: FormControl<boolean>;
      structureAccount: FormControl<boolean>;
      structureUnit: FormControl<boolean>;
    }
  >();

  private readonly payCodeSeriesReportCheckboxControls = new Map<
    string,
    FormControl<boolean>
  >();

  private readonly miscFieldReportOptionValueControls = new Map<
    string,
    FormControl<string>
  >();

  private readonly reportOptionLevelSelectControls = new WeakMap<
    SelectedLevelOption,
    {
      stateOf: FormControl<string | null>;
      customDatePeriod: FormControl<string | null>;
    }
  >();
  private readonly reportOptionLevelSelectControlsWired =
    new WeakSet<SelectedLevelOption>();

  generalDefaults: QueryGeneralDefaultsResponse | null = null;
  allReports: QueryReportItem[] = [];

  outputLayoutType: 'reports' | 'custom' = 'reports';
  customLayouts: LayoutListItem[] = [];
  selectedCustomLayout: LayoutListItem | null = null;
  customLayoutsLoading = false;

  /** Default off: show current user's layouts only; matches Custom Layouts list behavior. */
  includePublicControl = new FormControl(false, { nonNullable: true });
  layoutFieldSearchControl = new FormControl('', { nonNullable: true });

  /** Applied server-side field search; cleared on reset or empty search. */
  private appliedCustomLayoutFieldSearch: string | null = null;

  get hasCustomLayoutFieldSearch(): boolean {
    return !!this.appliedCustomLayoutFieldSearch;
  }

  form = new FormGroup({
    queryType: new FormControl<string>('C', {
      nonNullable: true,
    }),
    contractNumber: new FormControl<number | null>(null, {
      validators: [Validators.required],
    }),
    selectedReportIds: new FormControl<number[]>([], {
      nonNullable: true,
    }),
    reportIncludeType: new FormControl<string>('ClaimOnly', {
      nonNullable: true,
    }),
    asOfDate: new FormControl<Date | null>(DateEx.todayDateOnly(), {
      validators: [Validators.required],
    }),
    claimOpen: new FormControl<boolean>(true, { nonNullable: true }),
    claimClosed: new FormControl<boolean>(false, { nonNullable: true }),
    claimDeleted: new FormControl<boolean>(false, { nonNullable: true }),
    linesOfBusiness: new FormControl<string[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    lossDateFrom: new FormControl<Date | null>(null),
    lossDateThrough: new FormControl<Date | null>(null),
    openedDateFrom: new FormControl<Date | null>(null),
    openedDateThrough: new FormControl<Date | null>(null),
    closedDateFrom: new FormControl<Date | null>(null),
    closedDateThrough: new FormControl<Date | null>(null),
    reopenedDateFrom: new FormControl<Date | null>(null),
    reopenedDateThrough: new FormControl<Date | null>(null),
    reportedToClientFrom: new FormControl<Date | null>(null),
    reportedToClientThrough: new FormControl<Date | null>(null),
    reportedToCmsFrom: new FormControl<Date | null>(null),
    reportedToCmsThrough: new FormControl<Date | null>(null),
    lastUpdatedFrom: new FormControl<Date | null>(null),
    lastUpdatedThrough: new FormControl<Date | null>(null),
    lastFinancialUpdateFrom: new FormControl<Date | null>(null),
    lastFinancialUpdateThrough: new FormControl<Date | null>(null),
    paymentDateFrom: new FormControl<Date | null>(null),
    paymentDateThrough: new FormControl<Date | null>(null),
    name: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(8),
        ValidatorsEx.alphaNumericWith(
          '_-',
          'Query name can only contain letters, numbers, dashes, and underscores.',
        ),
      ],
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(40),
      ],
    }),
    isPrivate: new FormControl<boolean>(true, { nonNullable: true }),
    viewSpii: new FormControl<boolean>(false, { nonNullable: true }),
  });

  extendedParamsForm = createQueryExtendedParamsForm();

  async ngOnInit(): Promise<void> {
    this.wireGeneralDateRangeValidators();
    const section = this.activatedRoute.snapshot.queryParamMap.get('section');
    this.activeSection = section ? (section as QuerySectionKey) : null;
    this.requestWizardStep =
      this.activatedRoute.snapshot.queryParamMap.get('step');
    this.dispose.subs = this.activatedRoute.queryParams.subscribe((p) => {
      const step = p['step'] ?? null;
      const previousStep = this.requestWizardStep;
      this.requestWizardStep = step;
      if (step === 'save') {
        this.guardSaveStepEntry(previousStep);
      }
      if (this.requestWizardStep === 'general') {
        this.syncParameterSideMenu();
      }
      if (this.activeSection === 'request') {
        this.focusWizardStepHeading(this.requestWizardStep);
      }
    });

    // The Query page is route-cached (`routeCacheKey` in app.routes.ts), so the
    // component instance survives navigation to sibling tabs (Reports, Dashboards,
    // Alerts, etc.). Without this, the request wizard - including the Save Query
    // form - is still populated when the user returns. Reset the wizard whenever
    // the user navigates away from /query so they get a clean start on re-entry.
    this.dispose.subs = this.router.events.subscribe((event) => {
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      const targetUrl = event.urlAfterRedirects ?? event.url;
      if (targetUrl.startsWith('/query')) {
        return;
      }
      if (this.activeSection === 'request') {
        this.discardRequestWizardSession({ clearStepParam: false });
      }
      this.activeSection = null;
      this.requestInitialStep = null;
      this.requestWizardStep = null;
    });
    if (this.requestWizardStep === 'general') {
      this.syncParameterSideMenu();
    }

    this.canViewSpii = this.session.authorize('QuerySSN');
    if (!this.canViewSpii) {
      this.form.controls.viewSpii.disable();
    }

    await this.loadClients();

    this.dispose.subs =
      this.form.controls.contractNumber.valueChanges.subscribe(
        async (value: number | null) => {
          this.rebuildClientTableRows();
          if (this.isLoadingForEdit) {
            return;
          }
          this.form.controls.selectedReportIds.setValue([]);
          this.form.controls.linesOfBusiness.setValue([]);
          this.availableReports = [];
          this.selectedReports = [];
          this.activeReportOptionReport = null;
          this.reportOptionStates = {};
          this.reportOptionMetadata = {};
          this.reportOptionState = null;
          this.clearMiscFieldReportOptionValueControls();
          this.lobOptions = [];
          this.generalDefaults = null;
          this.allReports = [];
          this.outputLayoutType =
            (this.form.controls.queryType.value ?? 'C') === 'B'
              ? 'custom'
              : 'reports';
          this.customLayouts = [];
          this.selectedCustomLayout = null;
          this.extendedParamsForm = createQueryExtendedParamsForm();

          if (!value || value <= 0) {
            return;
          }

          await this.loadReportsAndDefaults(value);
        },
      );

    this.dispose.subs = this.form.controls.queryType.valueChanges.subscribe(
      async () => {
        if (this.isLoadingForEdit) {
          return;
        }
        this.form.controls.queryType.setErrors(null);
        const contractNumber = this.form.controls.contractNumber.value;
        const qt = this.form.controls.queryType.value ?? 'C';
        this.form.controls.selectedReportIds.setValue([]);
        this.availableReports = [];
        this.selectedReports = [];
        this.activeReportOptionReport = null;
        this.reportOptionStates = {};
        this.reportOptionMetadata = {};
        this.reportOptionState = null;
        this.clearMiscFieldReportOptionValueControls();
        this.outputLayoutType = qt === 'B' ? 'custom' : 'reports';
        this.customLayouts = [];
        this.selectedCustomLayout = null;
        this.extendedParamsForm = createQueryExtendedParamsForm();
        if (!contractNumber || contractNumber <= 0) {
          return;
        }

        await this.loadReportsAndDefaults(contractNumber);
        if (this.showGeneralParameterSidebar) {
          this.syncParameterSideMenu();
        }
      },
    );

    this.dispose.subs =
      this.form.controls.linesOfBusiness.valueChanges.subscribe(() => {
        if (
          (this.form.controls.linesOfBusiness.value ?? []).length > 0 &&
          this.generalStepError === this.linesOfBusinessRequiredMessage
        ) {
          this.generalStepError = '';
        }
        if (this.showGeneralParameterSidebar) {
          this.syncParameterSideMenu();
        }
      });

    this.dispose.subs = this.includePublicControl.valueChanges.subscribe(() => {
      void this.loadCustomLayouts();
    });

    this.setupReportOptionReportCheckboxListeners();
  }

  get showGeneralParameterSidebar(): boolean {
    return (
      this.activeSection === 'request' && this.requestWizardStep === 'general'
    );
  }

  /** Legacy banking wizard skips report layouts and goes straight to custom layout selection. */
  get isBankingQuery(): boolean {
    return (this.form.controls.queryType.value ?? 'C') === 'B';
  }

  /** Legacy QueryWizardSchedule.aspx: checkbox label + (M/dd/yyyy) or (blank) from General parameters. */
  scheduleIncrementLabel(
    baseLabel: string,
    generalField: QueryGeneralDateField,
  ): string {
    const value = this.form.controls[generalField]?.value as Date | null;
    return baseLabel + formatScheduleGeneralDateParen(value);
  }

  scheduleIncrementCheckboxControl(controlName: string): FormControl<boolean> {
    return this.scheduleForm.get(controlName) as FormControl<boolean>;
  }

  /**
   * Show the shared General block (claims/incidents, LOB, dates) only when the
   * Simple "General" item is selected.
   */
  get showGeneralParametersCoreForm(): boolean {
    return this.parameterActiveKey === 'General';
  }

  /**
   * Sub-panels (Payments/Banks, LOB sections, etc.): hidden when "General" is the active
   * sidebar item for claim and banking queries. Payment queries always show the panel host.
   */
  get showParameterPanelHost(): boolean {
    const qt = this.form.controls.queryType.value || 'C';
    if (qt === 'A') {
      return true;
    }
    return this.parameterActiveKey !== 'General';
  }

  private get parameterSideMenuOptions(): ParameterSideMenuOptions {
    return {
      selectedLobs: this.form.controls.linesOfBusiness.value ?? [],
      contractLobs: this.generalDefaults?.availableLinesOfBusiness ?? [],
    };
  }

  get visibleSimpleParameterMenuItems(): {
    key: ParameterSideMenuKey;
    label: string;
  }[] {
    const qt = this.form.controls.queryType.value || 'C';
    const opts = this.parameterSideMenuOptions;
    return SIMPLE_PARAMETER_MENU_KEYS.filter((k) =>
      isParameterSideMenuItemVisible(k, qt, opts),
    ).map((k) => ({ key: k, label: PARAMETER_SIDE_MENU_LABELS[k] }));
  }

  get visibleAdvancedParameterMenuItems(): {
    key: ParameterSideMenuKey;
    label: string;
  }[] {
    const qt = this.form.controls.queryType.value || 'C';
    const opts = this.parameterSideMenuOptions;
    return ADVANCED_PARAMETER_MENU_KEYS.filter((k) =>
      isParameterSideMenuItemVisible(k, qt, opts),
    ).map((k) => ({ key: k, label: PARAMETER_SIDE_MENU_LABELS[k] }));
  }

  private syncParameterSideMenu(): void {
    const qt = this.form.controls.queryType.value || 'C';
    const opts = this.parameterSideMenuOptions;
    const allVisible = [
      ...SIMPLE_PARAMETER_MENU_KEYS.filter((k) =>
        isParameterSideMenuItemVisible(k, qt, opts),
      ),
      ...ADVANCED_PARAMETER_MENU_KEYS.filter((k) =>
        isParameterSideMenuItemVisible(k, qt, opts),
      ),
    ];
    if (
      !this.parameterActiveKey ||
      !allVisible.includes(this.parameterActiveKey)
    ) {
      this.parameterActiveKey = (allVisible[0] ??
        'General') as ParameterSideMenuKey;
    }
    const advancedVisible = ADVANCED_PARAMETER_MENU_KEYS.filter((k) =>
      isParameterSideMenuItemVisible(k, qt, opts),
    );
    if (
      this.parameterActiveKey &&
      advancedVisible.includes(this.parameterActiveKey)
    ) {
      this.parameterMenuExpandedAdvanced = true;
      this.parameterMenuExpandedSimple = false;
    } else {
      this.parameterMenuExpandedSimple = true;
      this.parameterMenuExpandedAdvanced = false;
    }
  }

  toggleParameterMenuGroup(group: 'simple' | 'advanced'): void {
    if (group === 'simple') {
      this.parameterMenuExpandedSimple = true;
      this.parameterMenuExpandedAdvanced = false;
    } else {
      this.parameterMenuExpandedSimple = false;
      this.parameterMenuExpandedAdvanced = true;
    }
  }

  onParameterSideMenuClick(key: ParameterSideMenuKey): void {
    this.parameterActiveKey = key;
    queueMicrotask(() => {
      if (key === 'General') {
        document
          .getElementById(getParameterSideMenuScrollAnchor(key))
          ?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        return;
      }
      const panelEl = document.getElementById('qp-parameter-panel');
      if (panelEl) {
        panelEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      document
        .getElementById(getParameterSideMenuScrollAnchor(key))
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
    });
  }

  /**
   * Accessible name for main Query sidebar entries so SR users hear menu context, not only “button”.
   */
  queryMenuItemAriaLabel(itemLabel: string): string {
    return `${itemLabel}, Query menu option`;
  }

  /**
   * Accessible name for General parameters sidebar entries under Simple or Advanced.
   */
  parameterMenuItemAriaLabel(
    itemLabel: string,
    group: 'simple' | 'advanced',
  ): string {
    const groupName = group === 'simple' ? 'Simple' : 'Advanced';
    return `${itemLabel}, ${groupName} parameters menu option`;
  }

  /** Accessible name for Simple / Advanced expand toggles (state comes from aria-expanded). */
  parameterGroupToggleAriaLabel(group: 'simple' | 'advanced'): string {
    return group === 'simple'
      ? 'Simple parameter categories'
      : 'Advanced parameter categories';
  }

  selectSection(section: QuerySectionKey): void {
    const wasRequest = this.activeSection === 'request';
    if (section === 'create-layout') {
      this.prepareLayoutEditorForLayoutsMenu();
    }
    this.activeSection = section;
    if (section !== 'request') {
      this.requestInitialStep = null;
      if (wasRequest) {
        this.discardRequestWizardSession();
      }
    }
  }

  /**
   * Leaving the request stepper (without relying on save) must reset edit/create state so
   * the next "Request Query/Report" is a clean wizard, not the previous edit session.
   *
   * @param options.clearStepParam When true (default) also strips `?step=` from the URL.
   *   Set to false when the URL is already changing (e.g. user navigated to another tab)
   *   to avoid issuing a redundant navigation back into /query.
   */
  private discardRequestWizardSession(
    options: { clearStepParam?: boolean } = {},
  ): void {
    const { clearStepParam = true } = options;
    this.isLoadingForEdit = false;
    this.editQueryId = null;
    this.savedQueryId = null;
    this.editMode = false;
    this.saveError = '';
    this.saveSuccess = '';
    this.generalStepError = '';
    this.outputStepError = '';
    this.reportOptionError = '';
    this.reportOptionLoading = false;
    this.activeReportOptionReport = null;
    this.reportOptionState = null;
    this.reportOptionStates = {};
    this.reportOptionMetadata = {};
    this.selectedClientDisplayName = null;
    this.clearMiscFieldReportOptionValueControls();
    this.parameterActiveKey = 'General';
    this.parameterMenuExpandedSimple = true;
    this.parameterMenuExpandedAdvanced = false;
    this.editDetailLocationMethodCode = null;

    this.extendedParamsForm = createQueryExtendedParamsForm();

    if (!this.canViewSpii) {
      this.form.controls.viewSpii.enable({ emitEvent: false });
    }
    this.form.reset({
      queryType: 'C',
      contractNumber: null,
      selectedReportIds: [],
      reportIncludeType: 'ClaimOnly',
      asOfDate: DateEx.todayDateOnly(),
      claimOpen: true,
      claimClosed: false,
      claimDeleted: false,
      linesOfBusiness: [],
      lossDateFrom: null,
      lossDateThrough: null,
      openedDateFrom: null,
      openedDateThrough: null,
      closedDateFrom: null,
      closedDateThrough: null,
      reopenedDateFrom: null,
      reopenedDateThrough: null,
      reportedToClientFrom: null,
      reportedToClientThrough: null,
      reportedToCmsFrom: null,
      reportedToCmsThrough: null,
      lastUpdatedFrom: null,
      lastUpdatedThrough: null,
      lastFinancialUpdateFrom: null,
      lastFinancialUpdateThrough: null,
      paymentDateFrom: null,
      paymentDateThrough: null,
      name: '',
      description: '',
      isPrivate: true,
      viewSpii: false,
    });
    if (!this.canViewSpii) {
      this.form.controls.viewSpii.disable({ emitEvent: false });
    }

    this.availableReports = [];
    this.selectedReports = [];
    this.allReports = [];
    this.lobOptions = [];
    this.generalDefaults = null;
    this.outputLayoutType = 'reports';
    this.customLayouts = [];
    this.selectedCustomLayout = null;

    if (clearStepParam) {
      void this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: { step: null },
        queryParamsHandling: 'merge',
      });
    }
  }

  private syncDocumentTitle(): void {
    if (!this.activeSection || this.activeSection === 'request') {
      this.pageTitleService.clearSubmenu();
      this.pageTitleService.refreshTitle();
      return;
    }
    const label =
      this.activeSection === 'schedule-only'
        ? 'Schedule Query'
        : (this.queryMenu.find((x) => x.key === this.activeSection)?.label ??
          'Query');
    this.pageTitleService.setSubmenu(label);
  }

  async loadClients(): Promise<void> {
    this.clientsLoading = true;
    try {
      const pageSize = 250;
      const all: QueryClientItem[] = [];
      let page = 1;
      for (;;) {
        const result = await this.api.query.getClients({ page, pageSize });
        const items = result.items ?? [];
        all.push(...items);
        const total = result.paging?.totalCount ?? all.length;
        if (items.length === 0 || all.length >= total) {
          break;
        }
        page++;
      }
      this.clientRows = all;
      this.clientOptions = all.map((item) => ({
        label: item.display || `${item.contractNumber} - ${item.clientName}`,
        value: item.contractNumber,
      }));
      this.rebuildClientTableRows();
    } finally {
      this.clientsLoading = false;
    }
  }

  clientRowDisplayName(row: QueryClientItem): string {
    const name = row.clientName?.trim();
    if (name) {
      return name;
    }
    return row.display?.trim() || String(row.contractNumber);
  }

  isClientRowSelected(row: QueryClientItem): boolean {
    return this.form.controls.contractNumber.value === row.contractNumber;
  }

  /** Puts the selected client on the first table page (legacy row highlight may be off-page). */
  private rebuildClientTableRows(): void {
    const contractNumber = this.form.controls.contractNumber.value;
    if (!contractNumber || this.clientRows.length === 0) {
      this.clientTableRows = this.clientRows;
      return;
    }

    const index = this.clientRows.findIndex(
      (row) => row.contractNumber === contractNumber,
    );
    if (index <= 0) {
      this.clientTableRows = this.clientRows;
      return;
    }

    const rows = [...this.clientRows];
    const [selected] = rows.splice(index, 1);
    this.clientTableRows = [selected, ...rows];
  }

  getSelectedClientSummary(): string {
    const contractNumber = this.form.controls.contractNumber.value;
    if (!contractNumber) {
      return '';
    }

    const row = this.clientRows.find(
      (x) => x.contractNumber === contractNumber,
    );
    if (row) {
      return `${contractNumber} - ${this.clientRowDisplayName(row)}`;
    }

    const name = this.selectedClientDisplayName?.trim();
    return name ? `${contractNumber} - ${name}` : `${contractNumber}`;
  }

  selectClientRow(row: QueryClientItem): void {
    const cur = this.form.controls.contractNumber.value;
    if (cur === row.contractNumber) {
      this.form.controls.contractNumber.setValue(null);
      this.selectedClientDisplayName = null;
    } else {
      this.form.controls.contractNumber.setValue(row.contractNumber);
      this.selectedClientDisplayName = this.clientRowDisplayName(row);
    }
  }

  private async loadReportsAndDefaults(contractNumber: number): Promise<void> {
    const queryType = this.form.controls.queryType.value || 'C';
    const [reportsResponse, defaultsResponse] = await Promise.all([
      this.api.query.getReports(contractNumber, queryType),
      this.api.query.getGeneralDefaults(contractNumber),
    ]);

    this.allReports = [...(reportsResponse.availableReports ?? [])].sort(
      (a, b) =>
        (a.reportName ?? '').localeCompare(b.reportName ?? '', undefined, {
          sensitivity: 'base',
        }),
    );
    this.syncReportSelectionBuckets();

    this.generalDefaults = defaultsResponse;
    this.lobOptions = this.buildLobOptions(
      defaultsResponse.availableLinesOfBusiness,
    );
    this.applyLinesOfBusinessSelectionAfterDefaults();
    if (this.showGeneralParameterSidebar) {
      this.syncParameterSideMenu();
    }
  }

  private buildLobOptions(
    availableLinesOfBusiness: string[] | undefined,
  ): LobSelectOption[] {
    const available = new Set(
      (availableLinesOfBusiness ?? []).map((x) => x.trim().toLowerCase()),
    );
    return QUERY_LINES_OF_BUSINESS_ALL.map((lob) => ({
      label: lob,
      value: lob,
      disabled: !available.has(lob.toLowerCase()),
    }));
  }

  /** New query: select all contract-available LOBs when empty. Edit load: drop selections that are unavailable. */
  private applyLinesOfBusinessSelectionAfterDefaults(): void {
    const enabledValues = this.lobOptions
      .filter((o) => o.disabled !== true)
      .map((o) => o.value);
    const allowed = new Set(enabledValues);
    if (this.isLoadingForEdit) {
      const cur = this.form.controls.linesOfBusiness.value ?? [];
      this.form.controls.linesOfBusiness.setValue(
        cur.filter((x: string) => allowed.has(x)),
        { emitEvent: false },
      );
      return;
    }
    const cur = this.form.controls.linesOfBusiness.value ?? [];
    if (cur.length === 0) {
      this.form.controls.linesOfBusiness.setValue(enabledValues, {
        emitEvent: true,
      });
    } else {
      const next = cur.filter((x: string) => allowed.has(x));
      if (
        next.length !== cur.length ||
        next.some((v: string, i: number) => v !== cur[i])
      ) {
        this.form.controls.linesOfBusiness.setValue(next, { emitEvent: true });
      }
    }
  }

  goNextFromType(): void {
    if (!this.form.controls.queryType.value) {
      this.form.controls.queryType.setErrors({
        selection: 'A Query Type must be selected',
      });
      this.form.controls.queryType.markAsTouched();
      return;
    }

    this.form.controls.queryType.setErrors(null);
    this.stepper?.next();
  }

  async goNextFromClient(): Promise<void> {
    if (!this.form.controls.contractNumber.value) {
      this.form.controls.contractNumber.markAsTouched();
      return;
    }

    if (this.allReports.length === 0 || this.generalDefaults == null) {
      await this.loadReportsAndDefaults(
        this.form.controls.contractNumber.value,
      );
    }

    if (this.isBankingQuery) {
      this.outputLayoutType = 'custom';
      if (this.customLayouts.length === 0) {
        await this.loadCustomLayouts();
      }
    }

    this.stepper?.next();
  }

  canProceedFromOutput(): boolean {
    if (this.outputLayoutType === 'reports') {
      return this.form.controls.selectedReportIds.value.length > 0;
    }
    return this.selectedCustomLayout !== null;
  }

  async goNextFromOutput(): Promise<void> {
    this.outputStepError = '';
    if (this.outputLayoutType === 'reports') {
      if (this.form.controls.selectedReportIds.value.length === 0) {
        this.form.controls.selectedReportIds.markAsTouched();
        return;
      }
      await this.hydrateAllSelectedReportOptionStatesBeforePersist();
      const reportOptionsError = this.validateAllSelectedReportOptions();
      if (reportOptionsError) {
        this.outputStepError = reportOptionsError;
        return;
      }
    } else {
      if (!this.selectedCustomLayout) {
        return;
      }
    }

    this.stepper?.next();
  }

  /**
   * Move focus to the visible heading for the request wizard step (matches ?step= URL).
   * Improves screen reader / speech viewer context after Next, Previous, or direct navigation.
   */
  private focusWizardStepHeading(step: string | null): void {
    if (!step) {
      return;
    }
    if (step === 'general') {
      setTimeout(() => this.focusGeneralParametersSideMenu(), 0);
      return;
    }
    const idMap: Record<string, string> = {
      type: 'query-page-wizard-step-type',
      client: 'query-page-wizard-step-client',
      output: 'query-page-wizard-step-output',
      save: 'query-page-wizard-step-save',
      schedule: 'query-page-wizard-step-schedule',
    };
    const id = idMap[step];
    if (!id) {
      return;
    }
    setTimeout(() => document.getElementById(id)?.focus(), 0);
  }

  /** First focusable in the General parameters sidebar (section toggles + links). */
  private focusGeneralParametersSideMenu(): void {
    const nav = document.getElementById('query-page-general-parameters-nav');
    const first = nav?.querySelector('button') as HTMLButtonElement | null;
    first?.focus();
  }

  /** Focus the output step heading (report options or select reports / layouts). */
  private focusOutputStepStart(): void {
    this.focusWizardStepHeading('output');
  }

  /**
   * Delegated keyboard support: Enter activates native checkboxes; Space opens or advances
   * native single-selects (covers report option dropdowns without changing core controls).
   */
  onQueryWizardKeydownDelegate(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return;
    }

    if (
      event.key === 'Enter' &&
      target.tagName === 'INPUT' &&
      (target as HTMLInputElement).type === 'checkbox'
    ) {
      const input = target as HTMLInputElement;
      if (input.disabled) {
        return;
      }
      event.preventDefault();
      input.click();
      return;
    }

    if (
      (event.key === ' ' || event.code === 'Space') &&
      target.tagName === 'SELECT' &&
      !(target as HTMLSelectElement).multiple &&
      !(target as HTMLSelectElement).disabled
    ) {
      const select = target as HTMLSelectElement;
      event.preventDefault();
      const ext = select as HTMLSelectElement & { showPicker?: () => void };
      if (typeof ext.showPicker === 'function') {
        try {
          ext.showPicker();
          return;
        } catch {
          // Fall through to value cycling when showPicker is unavailable.
        }
      }
      if (select.options.length <= 1) {
        return;
      }
      const nextIndex =
        select.selectedIndex < 0 ||
        select.selectedIndex >= select.options.length - 1
          ? 0
          : select.selectedIndex + 1;
      select.selectedIndex = nextIndex;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  addSelectedReports(select: HTMLSelectElement): void {
    const selectedIds = Array.from(select.selectedOptions)
      .map((x) => Number(x.value))
      .filter((x) => x > 0);

    this.addReportsById(selectedIds);
    select.selectedIndex = -1;
  }

  addAllReports(): void {
    const ids = this.availableReports.map((x) => x.reportId);
    this.addReportsById(ids);
  }

  removeCheckedReports(): void {
    const ids = this.selectedReports
      .filter((x) => x.checked)
      .map((x) => x.report.reportId);
    this.removeReportsById(ids);
  }

  removeAllReports(): void {
    const ids = this.selectedReports.map((x) => x.report.reportId);
    this.removeReportsById(ids);
  }

  async selectOutputLayoutType(type: 'reports' | 'custom'): Promise<void> {
    this.outputLayoutType = type;
    if (type === 'custom' && this.customLayouts.length === 0) {
      await this.loadCustomLayouts();
    }
  }

  /**
   * Vertical tablist for Report Layouts vs Custom Layouts: Arrow keys move selection + focus;
   * Tab still visits both tabs in sequence (both tabindex 0).
   */
  onOutputLayoutTabKeydown(
    event: KeyboardEvent,
    current: 'reports' | 'custom',
  ): void {
    let next: 'reports' | 'custom' | null = null;
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = current === 'reports' ? 'custom' : null;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = current === 'custom' ? 'reports' : null;
        break;
      case 'Home':
        event.preventDefault();
        if (current === 'reports') {
          return;
        }
        next = 'reports';
        break;
      case 'End':
        event.preventDefault();
        if (current === 'custom') {
          return;
        }
        next = 'custom';
        break;
      default:
        return;
    }
    if (next === null) {
      return;
    }
    event.preventDefault();
    void this.selectOutputLayoutType(next).then(() =>
      document
        .getElementById(
          next === 'reports'
            ? 'query-page-output-tab-reports'
            : 'query-page-output-tab-custom',
        )
        ?.focus(),
    );
  }

  searchCustomLayoutsByField(): void {
    const term = this.layoutFieldSearchControl.value?.trim() ?? '';
    this.appliedCustomLayoutFieldSearch = term.length > 0 ? term : null;
    void this.loadCustomLayouts();
  }

  resetCustomLayoutFieldSearch(): void {
    this.layoutFieldSearchControl.setValue('');
    this.appliedCustomLayoutFieldSearch = null;
    void this.loadCustomLayouts();
  }

  async loadCustomLayouts(): Promise<void> {
    this.customLayoutsLoading = true;
    try {
      const response = await this.api.query.getLayouts({
        includePublic: this.includePublicControl.value ?? false,
        ...(this.appliedCustomLayoutFieldSearch
          ? {
              search: this.appliedCustomLayoutFieldSearch,
              searchField: 'Fields',
            }
          : {}),
      });
      this.customLayouts = response.items ?? [];
      if (
        this.selectedCustomLayout &&
        !this.customLayouts.some(
          (x) => x.layoutId === this.selectedCustomLayout!.layoutId,
        )
      ) {
        this.selectedCustomLayout = null;
      }
    } catch {
      this.customLayouts = [];
    } finally {
      this.customLayoutsLoading = false;
    }
  }

  selectCustomLayout(layout: LayoutListItem): void {
    this.selectedCustomLayout = layout;
  }

  removeCustomLayout(): void {
    this.selectedCustomLayout = null;
  }

  navigateToCreateLayout(): void {
    this.editLayoutId = null;
    this.copyLayoutId = null;
    this.layoutViewOnly = false;
    this.layoutEditorReturnContext = 'stepper';
    this.activeSection = 'create-layout';
  }

  toggleSelectedReportChecked(reportId: number, checked: boolean): void {
    const prior = this.selectedReports.find(
      (x) => x.report.reportId === reportId,
    );
    this.selectedReports = this.selectedReports.map((x) =>
      x.report.reportId === reportId
        ? {
            ...x,
            checked,
          }
        : x,
    );
    if (prior) {
      const name = prior.report.reportName?.trim() || `Report ${reportId}`;
      this.liveAnnouncer.announce(
        `${name}, ${checked ? 'checked for removal actions' : 'unchecked for removal actions'}`,
      );
    }
  }

  getSelectedReportCheckedControl(reportId: number): FormControl<boolean> {
    const row = this.selectedReports.find(
      (x) => x.report.reportId === reportId,
    );
    const nextChecked = !!row?.checked;
    let entry = this.selectedReportCheckedControlEntries.get(reportId);
    if (!entry) {
      const control = new FormControl(nextChecked, { nonNullable: true });
      const sub = control.valueChanges.subscribe((v) => {
        this.toggleSelectedReportChecked(reportId, v);
      });
      this.dispose.subs = sub;
      entry = { control, sub };
      this.selectedReportCheckedControlEntries.set(reportId, entry);
      return control;
    }
    if (entry.control.value !== nextChecked) {
      entry.control.setValue(nextChecked, { emitEvent: false });
    }
    return entry.control;
  }

  openReportOptions(reportId: number): void {
    this.clearMiscFieldReportOptionValueControls();
    const selected = this.selectedReports.find(
      (x) => x.report.reportId === reportId,
    );
    if (!selected) {
      return;
    }

    this.activeReportOptionReport = selected.report;
    this.reportOptionError = '';
    this.reportOptionState = this.reportOptionStates[reportId]
      ? { ...this.reportOptionStates[reportId] }
      : this.createDefaultReportOptionState(reportId, null);
    this.syncReportOptionScalarFormFromState();
    void this.loadReportOptionMetadataAndState(selected.report).then(() =>
      this.focusOutputStepStart(),
    );
  }

  closeReportOptions(save: boolean): void {
    if (!this.activeReportOptionReport) {
      return;
    }

    if (save) {
      const validationError = this.validateActiveReportOptions();
      if (validationError) {
        this.reportOptionError = validationError;
        return;
      }

      this.reportOptionStates[this.activeReportOptionReport.reportId] = {
        ...(this.reportOptionState ?? {}),
      };
    }

    this.clearMiscFieldReportOptionValueControls();
    this.activeReportOptionReport = null;
    this.reportOptionState = null;
    this.reportOptionError = '';
    this.focusOutputStepStart();
  }

  hasSavedOptions(reportId: number): boolean {
    const options = this.reportOptionStates[reportId];
    if (!options) {
      return false;
    }

    return this.buildOptionPairsForReport(reportId, options).length > 0;
  }

  private generalDateRangePairs = [
    { from: 'lossDateFrom', to: 'lossDateThrough', label: 'Loss Date' },
    { from: 'openedDateFrom', to: 'openedDateThrough', label: 'Claim Opened' },
    { from: 'closedDateFrom', to: 'closedDateThrough', label: 'Claim Closed' },
    {
      from: 'reopenedDateFrom',
      to: 'reopenedDateThrough',
      label: 'Claim Reopened',
    },
    {
      from: 'reportedToClientFrom',
      to: 'reportedToClientThrough',
      label: 'Reported to Client',
    },
    {
      from: 'reportedToCmsFrom',
      to: 'reportedToCmsThrough',
      label: 'Reported to SCMS',
    },
    {
      from: 'lastUpdatedFrom',
      to: 'lastUpdatedThrough',
      label: 'Last Updated',
    },
    {
      from: 'lastFinancialUpdateFrom',
      to: 'lastFinancialUpdateThrough',
      label: 'Last Financial Update',
    },
    {
      from: 'paymentDateFrom',
      to: 'paymentDateThrough',
      label: 'Payment Date',
    },
  ] as const;

  private getGeneralDateControl(
    key: (typeof this.generalDateRangePairs)[number]['from' | 'to'],
  ): FormControl<Date | null> {
    return (this.form.controls as Record<string, AbstractControl>)[
      key
    ] as FormControl<Date | null>;
  }

  private wireGeneralDateRangeValidators(): void {
    for (const { from, to, label } of this.generalDateRangePairs) {
      const fromCtrl = this.getGeneralDateControl(from);
      const toCtrl = this.getGeneralDateControl(to);

      fromCtrl.addValidators(
        ValidatorsEx.dateLessThan(
          toCtrl,
          false,
          `${label} From must be on or before Through.`,
        ),
      );
      toCtrl.addValidators(
        ValidatorsEx.dateGreaterThan(
          fromCtrl,
          false,
          `${label} Through must be on or after From.`,
        ),
      );

      this.dispose.subs = fromCtrl.valueChanges.subscribe(() =>
        toCtrl.updateValueAndValidity({ emitEvent: false }),
      );
      this.dispose.subs = toCtrl.valueChanges.subscribe(() =>
        fromCtrl.updateValueAndValidity({ emitEvent: false }),
      );
    }
  }

  private hasClaimStatusSelected(): boolean {
    return [
      this.form.controls.claimOpen.value,
      this.form.controls.claimClosed.value,
      this.form.controls.claimDeleted.value,
    ].some(Boolean);
  }

  private markClaimStatusTouched(): void {
    this.form.controls.claimOpen.markAsTouched();
    this.form.controls.claimClosed.markAsTouched();
    this.form.controls.claimDeleted.markAsTouched();
  }

  private validateLinesOfBusinessRequired(): string | null {
    if ((this.form.controls.linesOfBusiness.value ?? []).length === 0) {
      this.form.controls.linesOfBusiness.markAsTouched();
      return this.linesOfBusinessRequiredMessage;
    }
    return null;
  }

  /** Returns first General-step validation error, or null if valid. */
  private validateGeneralStep(): string | null {
    if (!this.form.controls.asOfDate.value) {
      this.form.controls.asOfDate.markAsTouched();
      return 'As of Date is required.';
    }
    const lobError = this.validateLinesOfBusinessRequired();
    if (lobError) {
      return lobError;
    }
    if (!this.hasClaimStatusSelected()) {
      this.markClaimStatusTouched();
      return this.claimStatusRequiredMessage;
    }
    return this.firstGeneralDateRangeError();
  }

  /**
   * Blocks stepper navigation to Save when Major Classification is empty (e.g. clicking
   * a completed Save step after clearing LOBs). Separate from report-specific LOB rules.
   */
  private guardSaveStepEntry(previousStep: string | null): void {
    if (
      this.activeSection !== 'request' ||
      previousStep === 'save' ||
      this.isLoadingForEdit
    ) {
      return;
    }

    const lobError = this.validateLinesOfBusinessRequired();
    if (lobError) {
      this.showQueryErrorSnackbar(lobError);
      this.parameterActiveKey = 'General';
      queueMicrotask(() => {
        this.stepper?.select('general');
        this.focusGeneralValidationTarget(lobError);
      });
      return;
    }

    if (!this.hasClaimStatusSelected()) {
      this.markClaimStatusTouched();
      const claimError = this.claimStatusRequiredMessage;
      this.showQueryErrorSnackbar(claimError);
      this.parameterActiveKey = 'General';
      queueMicrotask(() => {
        this.stepper?.select('general');
        this.focusGeneralValidationTarget(claimError);
      });
      return;
    }

    const financialError = this.validateFinancialsStep();
    if (financialError) {
      this.showFinancialsValidationFailure(financialError);
      queueMicrotask(() => {
        this.stepper?.select('general');
      });
      return;
    }

    const advancedPaymentsError = this.validateAdvancedPaymentsStep();
    if (advancedPaymentsError) {
      this.showAdvancedPaymentsValidationFailure(advancedPaymentsError);
      queueMicrotask(() => {
        this.stepper?.select('general');
      });
      return;
    }
  }

  private validateFinancialsStep(): string | null {
    return validateQueryFinancials(this.extendedParamsForm);
  }

  private validateAdvancedPaymentsStep(): string | null {
    const qt = this.form.controls.queryType.value ?? 'C';
    if (qt !== 'A' && qt !== 'B') {
      return null;
    }
    return validateQueryAdvancedPayments(this.extendedParamsForm);
  }

  private showFinancialsValidationFailure(error: string): void {
    markQueryFinancialsTouched(this.extendedParamsForm);
    this.showQueryErrorSnackbar(error);
    this.focusGeneralValidationTarget(error);
  }

  private showAdvancedPaymentsValidationFailure(error: string): void {
    markQueryAdvancedPaymentsTouched(this.extendedParamsForm);
    this.showQueryErrorSnackbar(error);
  }

  private focusGeneralValidationTarget(error: string): void {
    if (isQueryFinancialsValidationMessage(error)) {
      this.parameterActiveKey = 'Financials';
      queueMicrotask(() => {
        document.getElementById('qp-parameter-panel')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
      return;
    }
    if (error === this.claimStatusRequiredMessage) {
      this.parameterActiveKey = 'General';
      queueMicrotask(() => {
        const el = document.getElementById('qp-claim-filters');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el?.focus();
      });
      return;
    }
    if (error.startsWith('As of Date')) {
      this.parameterActiveKey = 'General';
      this.form.controls.asOfDate.markAsTouched();
      return;
    }
    if (
      error === this.linesOfBusinessRequiredMessage ||
      error.includes('Line of Business')
    ) {
      this.parameterActiveKey = 'General';
      return;
    }
    if (error) {
      this.parameterActiveKey = 'General';
      queueMicrotask(() => {
        document.getElementById('qp-date-ranges')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    }
  }

  private firstGeneralDateRangeError(): string | null {
    for (const { from, to } of this.generalDateRangePairs) {
      const fromCtrl = this.getGeneralDateControl(from);
      const toCtrl = this.getGeneralDateControl(to);
      fromCtrl.updateValueAndValidity({ emitEvent: false });
      toCtrl.updateValueAndValidity({ emitEvent: false });

      const msg =
        (toCtrl.errors?.['dateGreaterThan'] as string | undefined) ??
        (fromCtrl.errors?.['dateLessThan'] as string | undefined);
      if (msg) {
        fromCtrl.markAsTouched();
        toCtrl.markAsTouched();
        return msg;
      }
    }
    return null;
  }

  reportOptionLevelCheckboxInputId(
    reportId: number,
    rowIndex: number,
    part:
      | 'pageBreak'
      | 'structureIntermediate'
      | 'structureAccount'
      | 'structureUnit',
  ): string {
    return `qp-ro-sel-lvl-${reportId}-${rowIndex}-${part}`;
  }

  goNextFromGeneral(): void {
    this.generalStepError = '';
    this.saveError = '';
    this.saveSuccess = '';

    const lobError = this.validateLinesOfBusinessRequired();
    if (lobError) {
      this.showQueryErrorSnackbar(lobError);
      this.focusGeneralValidationTarget(lobError);
      return;
    }

    const error = this.validateGeneralStep();
    if (error) {
      if (error === this.claimStatusRequiredMessage) {
        this.parameterActiveKey = 'General';
        this.showQueryErrorSnackbar(error);
      } else {
        this.generalStepError = error;
      }
      this.focusGeneralValidationTarget(error);
      return;
    }

    const financialError = this.validateFinancialsStep();
    if (financialError) {
      this.showFinancialsValidationFailure(financialError);
      return;
    }

    const advancedPaymentsError = this.validateAdvancedPaymentsStep();
    if (advancedPaymentsError) {
      this.showAdvancedPaymentsValidationFailure(advancedPaymentsError);
      return;
    }

    this.stepper?.next();
  }

  async saveA1(): Promise<void> {
    this.saveError = '';
    this.saveSuccess = '';

    if (this.isLoadingForEdit) {
      this.saveError = 'Still loading query for edit. Please wait.';
      return;
    }

    const lobError = this.validateLinesOfBusinessRequired();
    if (lobError) {
      this.showQueryErrorSnackbar(lobError);
      this.focusGeneralValidationTarget(lobError);
      return;
    }

    const generalError = this.validateGeneralStep();
    if (generalError) {
      if (generalError === this.claimStatusRequiredMessage) {
        this.parameterActiveKey = 'General';
        this.showQueryErrorSnackbar(generalError);
        this.focusGeneralValidationTarget(generalError);
      } else if (this.requestWizardStep === 'general') {
        this.generalStepError = generalError;
        this.focusGeneralValidationTarget(generalError);
      } else {
        this.saveError = generalError;
      }
      return;
    }

    const financialError = this.validateFinancialsStep();
    if (financialError) {
      this.showFinancialsValidationFailure(financialError);
      return;
    }

    const advancedPaymentsError = this.validateAdvancedPaymentsStep();
    if (advancedPaymentsError) {
      this.showAdvancedPaymentsValidationFailure(advancedPaymentsError);
      return;
    }

    const nameControl = this.form.controls.name;
    nameControl.markAsTouched();
    if (nameControl.invalid) {
      if (nameControl.errors?.['required']) {
        this.saveError = 'Query name is required.';
      } else if (nameControl.errors?.['minlength']) {
        this.saveError = 'Query name must be at least 3 characters.';
      } else if (nameControl.errors?.['maxlength']) {
        this.saveError = 'Query name cannot exceed 8 characters.';
      } else if (nameControl.errors?.['alphaNumericWith']) {
        this.saveError = nameControl.errors['alphaNumericWith'];
      } else {
        this.saveError = 'Query name is invalid.';
      }
      this.nameInput?.focus();
      return;
    }

    const descriptionControl = this.form.controls.description;
    descriptionControl.markAsTouched();
    if (descriptionControl.invalid) {
      if (descriptionControl.errors?.['required']) {
        this.saveError = 'Query Description is required.';
      } else if (descriptionControl.errors?.['minlength']) {
        this.saveError = 'Description must be at least 4 characters.';
      } else if (descriptionControl.errors?.['maxlength']) {
        this.saveError = 'Description cannot exceed 40 characters.';
      } else {
        this.saveError = 'Description is invalid.';
      }
      return;
    }

    if (this.outputLayoutType === 'reports') {
      this.syncReportOptionReportCheckboxFormToState();
      await this.hydrateAllSelectedReportOptionStatesBeforePersist();
      if (this.showReportLobMismatchSnackbar()) {
        return;
      }
      const reportOptionsError = this.validateAllSelectedReportOptions();
      if (reportOptionsError) {
        this.saveError = reportOptionsError;
        return;
      }
    }

    this.saving = true;

    try {
      this.syncReportOptionReportCheckboxFormToState();
      this.flushAllReportOptionStatesBeforePersist();

      const queryIdToUpdate = this.editQueryId ?? this.savedQueryId;
      const payload: SaveQueryA1 = {
        ...(queryIdToUpdate != null && { queryId: queryIdToUpdate }),
        queryType: this.form.controls.queryType.value,
        contractNumber: this.form.controls.contractNumber.value ?? 0,
        selectedReportIds:
          this.outputLayoutType === 'reports'
            ? this.sortSelectedReportIdsLegacyOrder(
                this.form.controls.selectedReportIds.value,
              )
            : [],
        customLayoutId:
          this.outputLayoutType === 'custom' &&
          this.selectedCustomLayout?.layoutId
            ? this.selectedCustomLayout.layoutId
            : null,
        reportOptions:
          this.outputLayoutType === 'reports'
            ? this.getSelectedReportOptionsPayload()
            : [],
        general: {
          asOfDate: DateEx.toUtcDateOnly(this.form.controls.asOfDate.value),
          reportIncludeType: this.form.controls.reportIncludeType.value,
          claimStatus: {
            open: this.form.controls.claimOpen.value,
            closed: this.form.controls.claimClosed.value,
            deleted: this.form.controls.claimDeleted.value,
          },
          dateMode: 'absolute',
          absoluteDates: {
            lossDateFrom: this.form.controls.lossDateFrom.value,
            lossDateThrough: this.form.controls.lossDateThrough.value,
            openedDateFrom: this.form.controls.openedDateFrom.value,
            openedDateThrough: this.form.controls.openedDateThrough.value,
            closedDateFrom: this.form.controls.closedDateFrom.value,
            closedDateThrough: this.form.controls.closedDateThrough.value,
            reopenedDateFrom: this.form.controls.reopenedDateFrom.value,
            reopenedDateThrough: this.form.controls.reopenedDateThrough.value,
            reportedToClientFrom: this.form.controls.reportedToClientFrom.value,
            reportedToClientThrough:
              this.form.controls.reportedToClientThrough.value,
            reportedToCmsFrom: this.form.controls.reportedToCmsFrom.value,
            reportedToCmsThrough: this.form.controls.reportedToCmsThrough.value,
            lastUpdatedFrom: this.form.controls.lastUpdatedFrom.value,
            lastUpdatedThrough: this.form.controls.lastUpdatedThrough.value,
            lastFinancialUpdateFrom:
              this.form.controls.lastFinancialUpdateFrom.value,
            lastFinancialUpdateThrough:
              this.form.controls.lastFinancialUpdateThrough.value,
            paymentDateFrom: this.form.controls.paymentDateFrom.value,
            paymentDateThrough: this.form.controls.paymentDateThrough.value,
          },
          relativeDates: null,
          linesOfBusiness: this.form.controls.linesOfBusiness.value,
        },
        saveOptions: {
          name: this.form.controls.name.value,
          description: this.form.controls.description.value,
          isPrivate: this.form.controls.isPrivate.value,
          viewSpii: this.canViewSpii && this.form.controls.viewSpii.value,
        },
        extended: extendedParamsToPayload(this.extendedParamsForm, {
          queryType: this.form.controls.queryType.value ?? 'C',
          linesOfBusiness: this.form.controls.linesOfBusiness.value ?? [],
        }),
      };

      const response =
        queryIdToUpdate != null
          ? await this.api.query.updateQuery(queryIdToUpdate, payload)
          : await this.api.query.saveA1(payload);
      if (!response.success) {
        const lobMismatch = response.errors?.find(
          (e) => e.code === 'REPORT_LOB_MISMATCH',
        );
        if (lobMismatch?.message) {
          this.showQueryErrorSnackbar(lobMismatch.message);
          return;
        }
        this.saveError =
          response.message ||
          response.errors?.[0]?.message ||
          'Save failed. Please review your selections and try again.';
        const nameExists = response.errors?.some(
          (e) => e.code === 'QUERY_NAME_EXISTS',
        );
        if (nameExists) {
          this.nameInput?.focus();
        }
        return;
      }

      this.savedQueryId = response.queryId ?? null;
      this.saveSuccess = response.queryId
        ? `Query saved successfully. Query ID: ${response.queryId}. You can now schedule this query.`
        : 'Query saved successfully.';
    } catch {
      this.saveError =
        'Save failed. Please try again, or contact support if the problem continues.';
    } finally {
      this.saving = false;
    }
  }

  async goToScheduleStep(): Promise<void> {
    if (this.savedQueryId) {
      this.scheduleError = '';
      this.scheduleSuccess = '';
      await this.patchScheduleRunDateTimeToNow();
      this.stepper?.next();
    }
  }

  async createSchedule(): Promise<void> {
    if (!this.savedQueryId) {
      this.scheduleError = 'Query must be saved before scheduling.';
      return;
    }

    const runDate = this.scheduleForm.controls.runDate.value;
    if (!runDate) {
      this.scheduleError = 'Run date is required.';
      return;
    }

    const runDateOnly = new Date(
      runDate.getFullYear(),
      runDate.getMonth(),
      runDate.getDate(),
    );
    const todayOnly = new Date();
    todayOnly.setHours(0, 0, 0, 0);
    if (runDateOnly < todayOnly) {
      this.scheduleError = 'Run date must be today or in the future.';
      return;
    }

    const rtFallback = this.getDefaultScheduleDateTime();
    const hour =
      this.scheduleForm.controls.runTimeHour.value ?? rtFallback.hour;
    const minute =
      this.scheduleForm.controls.runTimeMinute.value ?? rtFallback.minute;
    const amPm =
      this.scheduleForm.controls.runTimeAmPm.value ?? rtFallback.amPm;
    if (hour < 1 || hour > 12) {
      this.scheduleError = 'Hour must be between 1 and 12.';
      return;
    }
    if (minute < 0 || minute > 59) {
      this.scheduleError = 'Minutes must be between 00 and 59.';
      return;
    }

    this.scheduling = true;
    this.scheduleError = '';
    this.scheduleSuccess = '';

    try {
      const frequency = this.scheduleForm.controls.frequency.value ?? 'once';
      const runDateStr = runDate.toISOString().split('T')[0];
      let hour24 = hour;
      if (amPm === 'PM' && hour !== 12) hour24 += 12;
      else if (amPm === 'AM' && hour === 12) hour24 = 0;
      const runTimeStr = `${hour24.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

      this.syncQueryScheduleIncrementForSelectedReports();
      const sf = this.scheduleForm.controls;
      const relativeDates = this.buildScheduleRelativeDatesPayload();

      const response = await this.api.query.createSchedule(this.savedQueryId, {
        frequency,
        runDate: runDateStr,
        runTime: runTimeStr,
        dayOfWeek:
          frequency === 'weekly' ? (sf.dayOfWeek.value ?? 0) : undefined,
        dayOfMonth:
          frequency === 'monthly' ? (sf.dayOfMonth.value ?? 1) : undefined,
        relativeDates,
      });

      if (response.success) {
        this.scheduleSuccess = 'Schedule created successfully.';
        if (this.activeSection === 'schedule-only') {
          this.editQueryId = null;
          this.savedQueryId = null;
          this.editMode = false;
          this.scheduleError = '';
          this.scheduleSuccess = '';
          this.activeSection = 'view-schedules';
        }
      } else {
        this.scheduleError = response.message || 'Failed to create schedule.';
      }
    } catch {
      this.scheduleError = 'An error occurred while creating the schedule.';
    } finally {
      this.scheduling = false;
    }
  }

  skipSchedule(): void {
    this.scheduleSuccess =
      'Query saved without scheduling. You can schedule it later from View Schedules.';
    this.selectSection('view-queries');
  }

  cancelScheduleOnly(): void {
    this.editQueryId = null;
    this.savedQueryId = null;
    this.editMode = false;
    this.isLoadingScheduleQuery = false;
    this.scheduleError = '';
    this.scheduleSuccess = '';
    this.resetScheduleFormToLegacyDefaults();
    this.activeSection = 'view-queries';
  }

  /**
   * Legacy QuerywizardOutput.AddSelected_Click walks AvalilableReportsListBox.Items
   * in list order (reports sorted by name), not HTML multi-select click order.
   */
  private sortSelectedReportIdsLegacyOrder(ids: number[]): number[] {
    const order = new Map(this.allReports.map((r, i) => [r.reportId, i]));
    return [...ids].sort(
      (a, b) => (order.get(a) ?? 9999) - (order.get(b) ?? 9999),
    );
  }

  private addReportsById(ids: number[]): void {
    if (ids.length === 0) {
      return;
    }

    const current = new Set(this.form.controls.selectedReportIds.value);
    ids.forEach((id) => current.add(id));
    this.form.controls.selectedReportIds.setValue(
      this.sortSelectedReportIdsLegacyOrder(Array.from(current)),
    );
    this.syncReportSelectionBuckets();
    this.syncQueryScheduleIncrementForSelectedReports();
  }

  private removeReportsById(ids: number[]): void {
    if (ids.length === 0) {
      return;
    }

    const removal = new Set(ids);
    const updated = this.form.controls.selectedReportIds.value.filter(
      (id: number) => !removal.has(id),
    );
    this.form.controls.selectedReportIds.setValue(updated);
    ids.forEach((id) => {
      delete this.reportOptionStates[id];
      delete this.reportOptionMetadata[id];
    });
    this.syncReportSelectionBuckets();
    this.syncQueryScheduleIncrementForSelectedReports();
  }

  private syncReportSelectionBuckets(): void {
    const selectedIds = new Set(this.form.controls.selectedReportIds.value);
    for (const id of [...this.selectedReportCheckedControlEntries.keys()]) {
      if (!selectedIds.has(id)) {
        const e = this.selectedReportCheckedControlEntries.get(id);
        e?.sub.unsubscribe();
        this.selectedReportCheckedControlEntries.delete(id);
      }
    }
    this.availableReports = this.allReports.filter(
      (x) => !selectedIds.has(x.reportId),
    );
    this.selectedReports = this.allReports
      .filter((x) => selectedIds.has(x.reportId))
      .map((x) => ({
        report: x,
        checked: false,
      }));
  }

  /**
   * Legacy GetReport() loads metadata before save/validation. Ensures sort/claim-id defaults
   * can be persisted even when the user never opened the report options panel.
   */
  private async ensureReportOptionMetadataCached(
    reportId: number,
  ): Promise<QueryReportOptionsMetadataResponse | null> {
    const contractNumber = this.form.controls.contractNumber.value ?? 0;
    const queryType = this.form.controls.queryType.value || 'C';
    if (contractNumber <= 0) {
      return this.reportOptionMetadata[reportId] ?? null;
    }

    if (!this.reportOptionMetadata[reportId]) {
      try {
        this.reportOptionMetadata[reportId] =
          await this.api.query.getReportOptionsMetadata(
            reportId,
            contractNumber,
            queryType,
          );
      } catch {
        return null;
      }
    }

    return this.reportOptionMetadata[reportId] ?? null;
  }

  private async ensureReportOptionStateHydrated(
    report: QueryReportItem,
  ): Promise<void> {
    const reportId = report.reportId;
    if (this.reportOptionStates[reportId]) {
      return;
    }

    const metadata = await this.ensureReportOptionMetadataCached(reportId);
    this.reportOptionStates[reportId] = this.createDefaultReportOptionState(
      reportId,
      metadata,
    );
  }

  private async hydrateAllSelectedReportOptionStatesBeforePersist(): Promise<void> {
    if (this.outputLayoutType !== 'reports') {
      return;
    }

    const reports = this.form.controls.selectedReportIds.value
      .map((id: number) => this.allReports.find((r) => r.reportId === id))
      .filter((r): r is QueryReportItem => !!r);

    await Promise.all(
      reports.map((report) => this.ensureReportOptionStateHydrated(report)),
    );
  }

  private async loadReportOptionMetadataAndState(
    report: QueryReportItem,
  ): Promise<void> {
    this.reportOptionLoading = true;
    try {
      const metadata = await this.ensureReportOptionMetadataCached(
        report.reportId,
      );
      const saved = this.reportOptionStates[report.reportId];
      this.reportOptionState = saved
        ? { ...saved }
        : this.createDefaultReportOptionState(report.reportId, metadata);
    } catch {
      // Keep options page usable even if metadata call fails.
      const saved = this.reportOptionStates[report.reportId];
      this.reportOptionState = saved
        ? { ...saved }
        : this.createDefaultReportOptionState(report.reportId, null);
    } finally {
      this.reportOptionLoading = false;
      this.syncReportOptionScalarFormFromState();
    }
  }

  private createDefaultReportOptionState(
    reportId: number,
    metadata: QueryReportOptionsMetadataResponse | null,
  ): ReportOptionState {
    const defaultState: ReportOptionState = {
      reportType: '',
      claimId: metadata?.claimIdOptions?.[0] ?? '',
      sortBy: metadata?.sortOptions?.[0] ?? '',
      availableLevels: [...(metadata?.availableLevels ?? [])]
        .filter(
          (level) =>
            !['LOCATION CODE', 'LOCATION STRUCTURE'].includes(
              level.toUpperCase(),
            ),
        )
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
      selectedLevels: [],
      minChangeUnit: 'Dollar',
      includePaymentDetail: false,
      summarySort: this.payCodeSummarySortOptions[0].value,
      selectedBankNumber: '',
      selectedPayCodes: [],
      selectedPayCodeSeries: [],
      selectedBankNumbers: [],
      selectedMiscFields: [],
      miscFieldSelections: {},
      selectedBreakLevel: '',
    };

    if (reportId === QUERY_RESERVE_CHANGE_REPORT_ID) {
      defaultState.claimId = resolveReserveChangeDefaultClaimId(
        metadata?.claimIdOptions ?? [],
      );
      defaultState.minChange = QUERY_RESERVE_CHANGE_DEFAULT_MIN_CHANGE;
      defaultState.minChangeUnit = 'Dollar';
    }

    if (reportId === 193) {
      defaultState.selectedPayCodeSeries = [
        ...REPORT_OPTION_PAY_CODE_SERIES_LABELS,
      ];
      defaultState.selectedBankNumber = '';
      defaultState.summarySort = this.payCodeSummarySortOptions[0].value;
    }

    if (reportId === 182) {
      defaultState.duration = 'OPEN-CLOSE';
    }

    if (reportId === 525) {
      defaultState.formType = 'WC';
      defaultState.maxClaimCount = '100';
    }

    return defaultState;
  }

  private getSelectedReportOptionsPayload(): QueryReportOptionsInput[] {
    return this.form.controls.selectedReportIds.value
      .map((reportId: number) => {
        const state = this.reportOptionStates[reportId];
        if (!state) {
          return null;
        }

        const options = this.buildOptionPairsForReport(reportId, state);
        return options.length === 0
          ? null
          : {
              reportId,
              options,
            };
      })
      .filter(
        (x: QueryReportOptionsInput | null): x is QueryReportOptionsInput =>
          !!x,
      );
  }

  private buildOptionPairsForReport(
    reportId: number,
    state: ReportOptionState,
  ): QueryReportOptionPair[] {
    const options: QueryReportOptionPair[] = [];

    if (this.supportsStandardOptions(reportId) || reportId === 193) {
      if (!this.hideSortByForReport(reportId)) {
        this.addPair(options, 'SORT-OPTION', state.sortBy);
      }
      const levels = state.selectedLevels ?? [];
      if (levels.length > 0) {
        this.addPair(
          options,
          'BREAK-LEVEL',
          levels.map((x) => x.name).join(','),
        );
        this.addPair(
          options,
          'PAGE-BRK-LIST',
          levels
            .filter((x) => x.breakPage)
            .map((x) => x.name)
            .join(','),
        );
      }

      const stateLevel = levels.find((x) => x.name.toUpperCase() === 'STATE');
      if (stateLevel?.stateOf) {
        this.addPair(options, 'STATE-OF', stateLevel.stateOf);
      }

      const structureLevel = levels.find(
        (x) => x.name.toUpperCase() === 'STRUCTURE',
      );
      if (structureLevel) {
        const structureFlags = [
          structureLevel.structureIntermediate ? 'I' : '',
          structureLevel.structureAccount ? 'A' : '',
          structureLevel.structureUnit ? 'U' : '',
        ].filter(Boolean);
        this.addPair(options, 'STRUCT-TOT-LEVEL', structureFlags.join(','));
      }

      const customDatesLevel = levels.find(
        (x) => x.name.toUpperCase() === 'CUSTOM DATES',
      );
      if (customDatesLevel && customDatesLevel.customDateValue) {
        const period = this.resolveCustomDatePeriodForLevel(customDatesLevel);
        this.addPair(
          options,
          'CUSTOM-DT-BREAK-DETL',
          `${customDatesLevel.customDateValue},${period}`,
        );
      }
    }

    if (reportId === 199 || reportId === 338) {
      this.addPair(options, 'RPT-LEVEL', state.reportType ?? '');
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
    }

    if (reportId === 525) {
      this.addPair(options, 'FORM-TYPE', state.formType ?? '');
      this.addPair(options, 'MAX-CLAIM-COUNT', state.maxClaimCount ?? '');
      this.addPair(
        options,
        'LAST-CSR-CUTOFF',
        this.toDateOptionValue(state.lastCsrRunDate),
      );
    }

    if (reportId === QUERY_RESERVE_CHANGE_REPORT_ID) {
      this.addPair(
        options,
        'DATE-BEGIN-2',
        this.toDateOptionValue(state.reserveFromDate),
      );
      this.addPair(
        options,
        'DATE-END-2',
        this.toDateOptionValue(state.reserveToDate),
      );
      this.addPair(options, 'RESERVE-AMT', state.minChange ?? '');
      this.addPair(options, 'EVENT-FLAGS[1]', state.minChangeUnit ?? '');
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
    }

    if (reportId === 379) {
      this.addPair(
        options,
        'DATE-BEGIN-2',
        this.toDateOptionValue(state.workStatusBeginDate),
      );
      this.addPair(
        options,
        'DATE-END-2',
        this.toDateOptionValue(state.workStatusEndDate),
      );
    }

    if (reportId === 193) {
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
      this.addPair(options, 'SUMMARY-SORT', state.summarySort ?? '');
      if (state.includePaymentDetail) {
        this.addPair(options, 'INC-PMNT-DETAIL', 'D');
      }

      this.addPair(options, 'BANK-NUMBER', state.selectedBankNumber ?? '');
      const paycodes = [
        ...(state.selectedPayCodeSeries ?? []),
        ...(state.selectedPayCodes ?? []),
      ];
      this.addPair(options, 'SELECTED-PAYCODES', paycodes.join(','));
    }

    if (reportId === 119) {
      this.addPair(options, 'SORT-OPTION', state.sortBy ?? '');
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
      this.addPair(
        options,
        'REPEATERLIST-REPORT-BREAK-LEVEL',
        state.selectedBreakLevel ?? '',
      );
      this.addPair(
        options,
        'MIN-NUM-OF-CLAIMS-TO-REPORT',
        state.numClaims ?? '',
      );
    }

    if (reportId === 182) {
      this.addPair(options, 'DURATION', state.duration ?? '');
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
    }

    if (reportId === 184) {
      this.addPair(options, 'CLAIM-ID', state.claimId ?? '');
      const miscRows = this.getMiscFieldRowsForReport(reportId);
      const formatted = miscRows
        .map((row) => {
          const selected =
            state.miscFieldSelections?.[this.getMiscFieldRowKey(row)] ??
            'Do not Include';
          if (!selected || selected === 'Do not Include') {
            return '';
          }

          return [
            row.contractNumber,
            row.accountNumber || 'ALL',
            row.unitNumber || 'ALL',
            `${row.fieldNumber}`.padStart(3, '0'),
            selected,
          ].join('|');
        })
        .filter((x) => !!x)
        .join(':');
      this.addPair(options, 'MISC-FIELDS', formatted);
    }

    return options;
  }

  supportsStandardOptions(reportId: number): boolean {
    return ![525, 379, 119, 193].includes(reportId);
  }

  hideSortByForReport(reportId: number): boolean {
    const report =
      this.allReports.find((x) => x.reportId === reportId) ??
      this.activeReportOptionReport;
    const normalized = (report?.reportName ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    return (
      reportId === 338 ||
      normalized.includes('CLAIMANDEXPENSEII') ||
      normalized.includes('COSTRANGEANALYSIS') ||
      normalized.includes('LOSSANALYSISII') ||
      normalized.includes('LOSTDAYSREPORT')
    );
  }

  private validateActiveReportOptions(): string {
    if (!this.activeReportOptionReport || !this.reportOptionState) {
      return '';
    }

    this.syncReportOptionScalarFormToState();
    this.syncMiscFieldReportOptionValueControlsToState();
    this.flushReportOptionLevelSelectControlsToModel();
    this.syncReportOptionReportCheckboxFormToState();
    this.flushReportOptionLevelCheckboxControlsToModel();

    const reportId = this.activeReportOptionReport.reportId;
    const reportName = this.activeReportOptionReport.reportName;
    if (
      (reportId === 199 || reportId === 338) &&
      !this.reportOptionState.claimId
    ) {
      return 'Claim# ID is required.';
    }

    const standardLevelError = this.validateStandardBreakLevels(
      reportName,
      this.reportOptionState.selectedLevels ?? [],
    );
    if (standardLevelError) {
      return standardLevelError;
    }

    if (reportId === 525) {
      if (!this.reportOptionState.formType) {
        return 'Form Type is required.';
      }

      if (
        !['WC', 'LIAB'].includes(
          (this.reportOptionState.formType ?? '').toUpperCase(),
        )
      ) {
        return 'Form Type must be WC or LIAB.';
      }

      if (!this.reportOptionState.maxClaimCount?.trim()) {
        return 'Maximum Number of Claims is required.';
      }

      const parsed = Number(this.reportOptionState.maxClaimCount);
      if (!Number.isInteger(parsed) || parsed < 1 || parsed > 9999) {
        return 'The maximum number of claims allowed is a range between 1 and 9999. Please enter a valid number.';
      }
    }

    if (reportId === QUERY_RESERVE_CHANGE_REPORT_ID) {
      const reserveError = this.validateReserveChangeReportOptions(
        this.reportOptionState,
      );
      if (reserveError) {
        return reserveError;
      }
    }

    if (reportId === 379) {
      if (
        !this.reportOptionState.workStatusBeginDate ||
        !this.reportOptionState.workStatusEndDate
      ) {
        return 'Work status begin and end dates are required.';
      }

      if (
        this.reportOptionState.workStatusEndDate <=
        this.reportOptionState.workStatusBeginDate
      ) {
        return 'Work Status End date should be more than the beginning date.';
      }
    }

    if (reportId === 119) {
      const numClaims = (this.reportOptionState.numClaims ?? '').trim();
      if (numClaims && !/^\d{1,3}$/.test(numClaims)) {
        return 'Repeater List Minimum Number of Claims must be between 0 and 999.';
      }
    }

    return '';
  }

  private addPair(
    options: QueryReportOptionPair[],
    optionType: string,
    optionValue?: string,
  ): void {
    const value = (optionValue ?? '').trim();
    if (!value) {
      return;
    }

    options.push({
      optionType,
      optionValue: value,
    });
  }

  /** Legacy report options use DateTime.ToShortDateString() (M/d/yyyy, no leading zeros). */
  private toDateOptionValue(value?: Date | null): string {
    if (!value) {
      return '';
    }

    const year = value.getFullYear();
    const month = value.getMonth() + 1;
    const day = value.getDate();
    return `${month}/${day}/${year}`;
  }

  private parseDateOption(value: string): Date | null {
    if (!value?.trim()) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  toDateOrNull(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  addSelectedLevel(select: HTMLSelectElement): void {
    if (!this.reportOptionState) {
      return;
    }

    const selectedNames = Array.from(select.selectedOptions).map(
      (x) => x.value,
    );
    const existing = new Set(
      (this.reportOptionState.selectedLevels ?? []).map((x) =>
        x.name.toUpperCase(),
      ),
    );
    const addLevels = selectedNames
      .filter((x) => !!x && !existing.has(x.toUpperCase()))
      .map((name) => this.createLevelOption(name));

    this.reportOptionState.selectedLevels = [
      ...(this.reportOptionState.selectedLevels ?? []),
      ...addLevels,
    ];
    this.reportOptionState.availableLevels = (
      this.reportOptionState.availableLevels ?? []
    ).filter((x) => !selectedNames.includes(x));
    this.ensureReportOptionLevelSelectControls();
    this.scheduleSyncReportOptionLevelSelectControlsFromState();
  }

  addAllLevels(): void {
    if (!this.reportOptionState) {
      return;
    }

    const allNames = [...(this.reportOptionState.availableLevels ?? [])];
    if (allNames.length === 0) {
      return;
    }

    const existing = new Set(
      (this.reportOptionState.selectedLevels ?? []).map((x) =>
        x.name.toUpperCase(),
      ),
    );
    const addLevels = allNames
      .filter((x) => !existing.has(x.toUpperCase()))
      .map((name) => this.createLevelOption(name));

    this.reportOptionState.selectedLevels = [
      ...(this.reportOptionState.selectedLevels ?? []),
      ...addLevels,
    ];
    this.reportOptionState.availableLevels = [];
    this.ensureReportOptionLevelSelectControls();
    this.scheduleSyncReportOptionLevelSelectControlsFromState();
  }

  private createLevelOption(name: string): SelectedLevelOption {
    const option: SelectedLevelOption = { name, breakPage: false };
    if (name.toUpperCase() === 'STATE') {
      option.stateOf = 'Jurisdiction';
    }
    if (name.toUpperCase() === 'CUSTOM DATES') {
      option.customDateValue = '30';
      option.customDatePeriod = 'D';
    }
    return option;
  }

  removeSelectedLevels(levelNames: string[]): void {
    if (!this.reportOptionState || levelNames.length === 0) {
      return;
    }

    const removal = new Set(levelNames.map((x) => x.toUpperCase()));
    const removed = (this.reportOptionState.selectedLevels ?? []).filter((x) =>
      removal.has(x.name.toUpperCase()),
    );
    this.reportOptionState.selectedLevels = (
      this.reportOptionState.selectedLevels ?? []
    ).filter((x) => !removal.has(x.name.toUpperCase()));
    this.reportOptionState.availableLevels = [
      ...(this.reportOptionState.availableLevels ?? []),
      ...removed.map((x) => x.name),
    ]
      .filter(
        (level) =>
          !['LOCATION CODE', 'LOCATION STRUCTURE'].includes(
            level.toUpperCase(),
          ),
      )
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    this.scheduleSyncReportOptionLevelSelectControlsFromState();
  }

  clearSelectedLevels(): void {
    const all = (this.reportOptionState?.selectedLevels ?? []).map(
      (x) => x.name,
    );
    this.removeSelectedLevels(all);
  }

  moveSelectedLevel(levelName: string, delta: number): void {
    if (!this.reportOptionState || !this.reportOptionState.selectedLevels) {
      return;
    }

    const currentIndex = this.reportOptionState.selectedLevels.findIndex(
      (x) => x.name.toUpperCase() === levelName.toUpperCase(),
    );
    if (currentIndex < 0) {
      return;
    }

    const targetIndex = currentIndex + delta;
    if (
      targetIndex < 0 ||
      targetIndex >= this.reportOptionState.selectedLevels.length
    ) {
      return;
    }

    const copy = [...this.reportOptionState.selectedLevels];
    const [item] = copy.splice(currentIndex, 1);
    copy.splice(targetIndex, 0, item);
    this.reportOptionState.selectedLevels = copy;
  }

  getSelectedClientLabel(): string {
    const contractNumber = this.form.controls.contractNumber.value;
    if (!contractNumber) {
      return '';
    }

    const row = this.clientRows.find(
      (x) => x.contractNumber === contractNumber,
    );
    if (row) {
      return `${contractNumber} ${this.clientRowDisplayName(row)}`;
    }

    const name = this.selectedClientDisplayName?.trim();
    if (name) {
      return `${contractNumber} ${name}`;
    }

    const selected = this.clientOptions.find((x) => x.value === contractNumber);
    if (!selected) {
      return `${contractNumber}`;
    }

    const label = selected.label || `${contractNumber}`;
    return label.replace(' - ', ' ');
  }

  isClaimReportOptionsActive(): boolean {
    const reportId = this.activeReportOptionReport?.reportId;
    return reportId === 199 || reportId === 338;
  }

  getAvailablePayCodes(): string[] {
    if (!this.activeReportOptionReport || !this.reportOptionState) {
      return [];
    }

    const allCodes =
      this.reportOptionMetadata[this.activeReportOptionReport.reportId]
        ?.payCodes ?? [];
    const selected = new Set(this.reportOptionState.selectedPayCodes ?? []);
    return allCodes
      .filter((x) => !selected.has(x))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }

  addSelectedPayCodes(select: HTMLSelectElement): void {
    if (!this.reportOptionState) {
      return;
    }

    const selected = Array.from(select.selectedOptions).map((x) => x.value);
    const next = new Set(this.reportOptionState.selectedPayCodes ?? []);
    selected.forEach((value) => next.add(value));
    this.reportOptionState.selectedPayCodes = Array.from(next);
  }

  addAllPayCodes(): void {
    if (!this.reportOptionState || !this.activeReportOptionReport) {
      return;
    }

    const allCodes =
      this.reportOptionMetadata[this.activeReportOptionReport.reportId]
        ?.payCodes ?? [];
    this.reportOptionState.selectedPayCodes = [...allCodes];
  }

  removeSelectedPayCodes(select: HTMLSelectElement): void {
    if (!this.reportOptionState) {
      return;
    }

    const selected = new Set(
      Array.from(select.selectedOptions).map((x) => x.value),
    );
    this.reportOptionState.selectedPayCodes = (
      this.reportOptionState.selectedPayCodes ?? []
    ).filter((x) => !selected.has(x));
  }

  removeAllPayCodes(): void {
    if (!this.reportOptionState) {
      return;
    }

    this.reportOptionState.selectedPayCodes = [];
  }

  getMiscFieldRows(): QueryMiscClientFieldRow[] {
    const reportId = this.activeReportOptionReport?.reportId;
    if (!reportId) {
      return [];
    }

    return this.getMiscFieldRowsForReport(reportId);
  }

  getMiscFieldRowKey(row: QueryMiscClientFieldRow): string {
    return `${row.contractNumber}|${row.accountNumber}|${row.unitNumber}|${row.fieldNumber}`;
  }

  getMiscFieldSelection(row: QueryMiscClientFieldRow): string {
    const key = this.getMiscFieldRowKey(row);
    return (
      this.reportOptionState?.miscFieldSelections?.[key] ?? 'Do not Include'
    );
  }

  reportOptionSortSelectItems(): { label: string; value: string }[] {
    const rid = this.activeReportOptionReport?.reportId;
    if (!rid) {
      return [];
    }
    return (this.reportOptionMetadata[rid]?.sortOptions ?? []).map((x) => ({
      label: x,
      value: x,
    }));
  }

  reportOptionClaimIdSelectItems(): { label: string; value: string }[] {
    const rid = this.activeReportOptionReport?.reportId;
    if (!rid) {
      return [];
    }
    const items = (this.reportOptionMetadata[rid]?.claimIdOptions ?? []).map(
      (x) => ({
        label: x,
        value: x,
      }),
    );
    const current = (
      this.reportOptionState?.claimId ??
      this.reportOptionScalarForm.controls.claimId.value ??
      ''
    ).trim();
    if (
      current &&
      !items.some((i) => i.value.toUpperCase() === current.toUpperCase())
    ) {
      return [{ label: current, value: current }, ...items];
    }
    return items;
  }

  reportOptionBreakLevelSelectItems(): { label: string; value: string }[] {
    const rid = this.activeReportOptionReport?.reportId;
    if (!rid) {
      return [];
    }
    const levels = this.reportOptionMetadata[rid]?.availableLevels ?? [];
    return [
      {
        label: 'None',
        value: QUERY_REPORT_BREAK_LEVEL_NONE_OPTION_VALUE,
      },
      ...levels.map((x) => ({ label: x, value: x })),
    ];
  }

  /**
   * Paycode report 193: `BANK-NUMBER` persists as `''` for "all banks". PrimeNG Select
   * treats `''` as unfilled, so the float label overlaps the option text; use a sentinel in the form only.
   */
  private activeBankNumberToScalarFormValue(
    stored: string | undefined | null,
  ): string {
    const v = stored ?? '';
    return v === '' ? QUERY_PAYCODE_ALL_BANK_NUMBERS_OPTION_VALUE : v;
  }

  private activeBankNumberFromScalarFormValue(
    form: string | undefined | null,
  ): string {
    const v = form ?? '';
    return v === QUERY_PAYCODE_ALL_BANK_NUMBERS_OPTION_VALUE ? '' : v;
  }

  private formTypeToScalarFormValue(stored: string | undefined | null): string {
    const v = (stored ?? '').trim();
    return v === '' ? QUERY_REPORT_FORM_TYPE_UNSELECTED_OPTION_VALUE : v;
  }

  private formTypeFromScalarFormValue(form: string | undefined | null): string {
    const v = form ?? '';
    return v === QUERY_REPORT_FORM_TYPE_UNSELECTED_OPTION_VALUE ? '' : v;
  }

  private breakLevelToScalarFormValue(
    stored: string | undefined | null,
  ): string {
    const v = stored ?? '';
    return v === '' ? QUERY_REPORT_BREAK_LEVEL_NONE_OPTION_VALUE : v;
  }

  private breakLevelFromScalarFormValue(
    form: string | undefined | null,
  ): string {
    const v = form ?? '';
    return v === QUERY_REPORT_BREAK_LEVEL_NONE_OPTION_VALUE ? '' : v;
  }

  reportOptionActiveBankNumberSelectItems(): {
    label: string;
    value: string;
  }[] {
    const rid = this.activeReportOptionReport?.reportId;
    if (!rid) {
      return [];
    }
    const nums = this.reportOptionMetadata[rid]?.activeBankNumbers ?? [];
    return [
      {
        label: '-- All Bank Numbers',
        value: QUERY_PAYCODE_ALL_BANK_NUMBERS_OPTION_VALUE,
      },
      ...nums.map((x) => ({ label: String(x), value: String(x) })),
    ];
  }

  private clearMiscFieldReportOptionValueControls(): void {
    this.miscFieldReportOptionValueControls.clear();
  }

  getMiscFieldValueControl(row: QueryMiscClientFieldRow): FormControl<string> {
    const key = this.getMiscFieldRowKey(row);
    let c = this.miscFieldReportOptionValueControls.get(key);
    if (!c) {
      c = new FormControl(this.getMiscFieldSelection(row), {
        nonNullable: true,
      });
      this.miscFieldReportOptionValueControls.set(key, c);
    } else {
      c.setValue(this.getMiscFieldSelection(row), { emitEvent: false });
    }
    return c;
  }

  private getReportOptionLevelSelectEntry(level: SelectedLevelOption): {
    stateOf: FormControl<string | null>;
    customDatePeriod: FormControl<string | null>;
  } {
    let entry = this.reportOptionLevelSelectControls.get(level);
    if (!entry) {
      entry = {
        stateOf: new FormControl<string | null>(
          level.stateOf ?? 'Jurisdiction',
        ),
        customDatePeriod: new FormControl<string | null>(
          level.customDatePeriod ?? 'D',
        ),
      };
      this.reportOptionLevelSelectControls.set(level, entry);
      this.wireReportOptionLevelSelectControlsToModel(level, entry);
    }
    return entry;
  }

  getReportOptionLevelStateOfControl(
    level: SelectedLevelOption,
  ): FormControl<string | null> {
    return this.getReportOptionLevelSelectEntry(level).stateOf;
  }

  getReportOptionLevelCustomDatePeriodControl(
    level: SelectedLevelOption,
  ): FormControl<string | null> {
    return this.getReportOptionLevelSelectEntry(level).customDatePeriod;
  }

  private applyReportOptionLevelSelectControlsToLevels(
    levels: SelectedLevelOption[] | undefined,
  ): void {
    if (!levels?.length) {
      return;
    }
    for (const level of levels) {
      const entry = this.reportOptionLevelSelectControls.get(level);
      if (!entry) {
        continue;
      }
      const so = entry.stateOf.value;
      level.stateOf =
        so === 'Jurisdiction' || so === 'Loss' || so === 'Pay/Prem'
          ? so
          : 'Jurisdiction';
      level.customDatePeriod = this.resolveCustomDatePeriodForLevel(level);
    }
  }

  private flushReportOptionLevelSelectControlsToModel(): void {
    this.applyReportOptionLevelSelectControlsToLevels(
      this.reportOptionState?.selectedLevels,
    );
  }

  /** Flush open modal + every cached report state before save (legacy Submit on each report). */
  private flushAllReportOptionStatesBeforePersist(): void {
    this.flushOpenReportOptionsWorkingCopyToModel();
    if (this.activeReportOptionReport && this.reportOptionState) {
      this.reportOptionStates[this.activeReportOptionReport.reportId] = {
        ...this.reportOptionState,
      };
    }
    for (const reportId of this.form.controls.selectedReportIds.value) {
      const state = this.reportOptionStates[reportId];
      this.applyReportOptionLevelSelectControlsToLevels(state?.selectedLevels);
    }
  }

  /** Report vs Major Classification (Lost Days, etc.) — save/update only; snackbar, not stepper gates. */
  private showReportLobMismatchSnackbar(): boolean {
    if (this.outputLayoutType !== 'reports') {
      return false;
    }

    const selectedReports = this.form.controls.selectedReportIds.value
      .map((reportId: number) =>
        this.allReports.find((r) => r.reportId === reportId),
      )
      .filter((r): r is QueryReportItem => !!r);

    const message = validateSelectedReportsLobCompatibility(
      this.form.controls.linesOfBusiness.value ?? [],
      selectedReports,
    );
    if (!message) {
      return false;
    }

    this.showQueryErrorSnackbar(message);
    return true;
  }

  private showQueryErrorSnackbar(message: string): void {
    this.snackbar.show(message, 'Error', 'fa-light fa-xmark', 6000);
  }

  /**
   * Legacy StandardReportOptions.ascx client validators — all selected reports,
   * not only the report options panel currently open.
   */
  private validateAllSelectedReportOptions(): string {
    if (this.outputLayoutType !== 'reports') {
      return '';
    }
    this.flushAllReportOptionStatesBeforePersist();

    for (const reportId of this.form.controls.selectedReportIds.value) {
      const report = this.allReports.find((r) => r.reportId === reportId);
      if (!report) {
        continue;
      }

      if (reportId === QUERY_RESERVE_CHANGE_REPORT_ID) {
        const state = this.reportOptionStates[reportId];
        if (!state?.reserveFromDate || !state?.reserveToDate) {
          return 'Complete the reserve change date required fields on the Reserve Change Report Options link';
        }
        const reserveError = this.validateReserveChangeReportOptions(state);
        if (reserveError) {
          return reserveError;
        }
      }

      const state = this.reportOptionStates[reportId];
      if (!state) {
        continue;
      }
      const levelError = this.validateStandardBreakLevels(
        report.reportName,
        state.selectedLevels ?? [],
      );
      if (levelError) {
        return levelError;
      }
    }
    return '';
  }

  /** Legacy ReserveChangeReportOptions.aspx / SaveQueryPageController validation. */
  private validateReserveChangeReportOptions(state: ReportOptionState): string {
    if (!state.reserveFromDate) {
      return 'Reserve Change Begin Date is required.';
    }
    if (!state.reserveToDate) {
      return 'Reserve Change End Date is required.';
    }
    if (state.reserveToDate <= state.reserveFromDate) {
      return 'Reserve Change Begin Date should be prior to the Reserve Change End Date.';
    }
    const minChangeRaw = (state.minChange ?? '').trim();
    if (minChangeRaw && !/^[-+]?\d*\.?\d*$/.test(minChangeRaw)) {
      return 'Invalid Minimum Change To Report.';
    }
    return '';
  }

  private syncQueryScheduleIncrementForSelectedReports(): void {
    const multi = this.isMultipleReportsSelected();
    if (multi) {
      this.queryScheduleIncrementFlags = {
        beginSelected: false,
        endSelected: false,
      };
    }
    const c = this.reportOptionReportCheckboxForm.controls;
    c.workStatusBeginDateSelected.setValue(
      this.queryScheduleIncrementFlags.beginSelected,
      { emitEvent: false },
    );
    c.workStatusEndDateSelected.setValue(
      this.queryScheduleIncrementFlags.endSelected,
      { emitEvent: false },
    );
    if (multi) {
      c.workStatusBeginDateSelected.disable({ emitEvent: false });
      c.workStatusEndDateSelected.disable({ emitEvent: false });
    } else {
      c.workStatusBeginDateSelected.enable({ emitEvent: false });
      c.workStatusEndDateSelected.enable({ emitEvent: false });
    }
  }

  private syncMiscFieldReportOptionValueControlsToState(): void {
    if (
      !this.reportOptionState ||
      this.activeReportOptionReport?.reportId !== 184
    ) {
      return;
    }
    const next = { ...(this.reportOptionState.miscFieldSelections ?? {}) };
    for (const row of this.getMiscFieldRowsForReport(184)) {
      const c = this.miscFieldReportOptionValueControls.get(
        this.getMiscFieldRowKey(row),
      );
      if (c) {
        next[this.getMiscFieldRowKey(row)] = c.value;
      }
    }
    this.reportOptionState.miscFieldSelections = next;
  }

  private syncReportOptionScalarFormFromState(): void {
    if (!this.reportOptionState) {
      return;
    }
    const s = this.reportOptionState;
    this.reportOptionScalarForm.patchValue(
      {
        reportType:
          s.reportType === 'S' || s.reportType === 'M'
            ? s.reportType
            : QUERY_CLAIM_REPORT_TYPE_NORMAL_OPTION_VALUE,
        claimId: resolveClaimIdFromStorage(
          s.claimId,
          this.reportOptionMetadata[
            this.activeReportOptionReport?.reportId ?? 0
          ]?.claimIdOptions ?? [],
        ),
        sortBy: s.sortBy ?? '',
        formType: this.formTypeToScalarFormValue(s.formType),
        minChangeUnit: s.minChangeUnit ?? 'Dollar',
        duration: s.duration ?? 'OPEN-CLOSE',
        selectedBreakLevel: this.breakLevelToScalarFormValue(
          s.selectedBreakLevel,
        ),
        selectedBankNumber: this.activeBankNumberToScalarFormValue(
          s.selectedBankNumber,
        ),
        summarySort: s.summarySort ?? this.payCodeSummarySortOptions[0].value,
        reserveFromDate: s.reserveFromDate ?? null,
        reserveToDate: s.reserveToDate ?? null,
        minChange: s.minChange ?? '',
        maxClaimCount: s.maxClaimCount ?? '',
        lastCsrRunDate: s.lastCsrRunDate ?? null,
      },
      { emitEvent: false },
    );
    this.syncReportOptionReportCheckboxFormFromState();
    this.ensureReportOptionLevelSelectControls();
    this.scheduleSyncReportOptionLevelSelectControlsFromState();
    this.syncReportOptionLevelCheckboxControlsFromState();
  }

  /**
   * Create select controls for all selected break levels before template bind.
   * Must not call setValue here — use syncReportOptionLevelSelectControlsFromState.
   */
  private ensureReportOptionLevelSelectControls(): void {
    if (!this.reportOptionState?.selectedLevels) {
      return;
    }
    for (const level of this.reportOptionState.selectedLevels) {
      if (this.reportOptionLevelSelectControls.has(level)) {
        continue;
      }
      const entry = {
        stateOf: new FormControl<string | null>(
          level.stateOf ?? 'Jurisdiction',
        ),
        customDatePeriod: new FormControl<string | null>(
          level.customDatePeriod ?? 'D',
        ),
      };
      this.reportOptionLevelSelectControls.set(level, entry);
      this.wireReportOptionLevelSelectControlsToModel(level, entry);
    }
  }

  /** Keep level model in sync when user changes State / period dropdowns (legacy UpdateOption). */
  private wireReportOptionLevelSelectControlsToModel(
    level: SelectedLevelOption,
    entry: {
      stateOf: FormControl<string | null>;
      customDatePeriod: FormControl<string | null>;
    },
  ): void {
    if (this.reportOptionLevelSelectControlsWired.has(level)) {
      return;
    }
    this.reportOptionLevelSelectControlsWired.add(level);
    this.dispose.subs = entry.stateOf.valueChanges.subscribe((so) => {
      level.stateOf =
        so === 'Jurisdiction' || so === 'Loss' || so === 'Pay/Prem'
          ? so
          : 'Jurisdiction';
    });
    this.dispose.subs = entry.customDatePeriod.valueChanges.subscribe((p) => {
      level.customDatePeriod = p === 'D' || p === 'M' || p === 'Y' ? p : 'D';
    });
  }

  private resolveCustomDatePeriodForLevel(
    level: SelectedLevelOption,
  ): 'D' | 'M' | 'Y' {
    const entry = this.reportOptionLevelSelectControls.get(level);
    const fromControl = entry?.customDatePeriod.value;
    const raw = fromControl ?? level.customDatePeriod ?? 'D';
    return raw === 'D' || raw === 'M' || raw === 'Y' ? raw : 'D';
  }

  /**
   * Push `selectedLevels` select values into existing controls (not from template getter).
   */
  private syncReportOptionLevelSelectControlsFromState(): void {
    if (!this.reportOptionState?.selectedLevels) {
      return;
    }
    for (const level of this.reportOptionState.selectedLevels) {
      const entry = this.reportOptionLevelSelectControls.get(level);
      if (!entry) {
        continue;
      }
      const so = level.stateOf ?? 'Jurisdiction';
      if (entry.stateOf.value !== so) {
        entry.stateOf.setValue(so, { emitEvent: false });
      }
      const p = level.customDatePeriod ?? 'D';
      if (entry.customDatePeriod.value !== p) {
        entry.customDatePeriod.setValue(p, { emitEvent: false });
      }
    }
  }

  private scheduleSyncReportOptionLevelSelectControlsFromState(): void {
    queueMicrotask(() => this.syncReportOptionLevelSelectControlsFromState());
  }

  /**
   * Push `selectedLevels` booleans into existing level checkbox controls.
   * Call after loading state / opening options — not from the template getter
   * (that would fight user edits before flush).
   */
  private syncReportOptionLevelCheckboxControlsFromState(): void {
    if (!this.reportOptionState?.selectedLevels) {
      return;
    }
    for (const level of this.reportOptionState.selectedLevels) {
      const e = this.reportOptionLevelCheckboxControls.get(level);
      if (!e) {
        continue;
      }
      const pb = !!level.breakPage;
      if (e.pageBreak.value !== pb) {
        e.pageBreak.setValue(pb, { emitEvent: false });
      }
      const si = !!level.structureIntermediate;
      if (e.structureIntermediate.value !== si) {
        e.structureIntermediate.setValue(si, { emitEvent: false });
      }
      const sa = !!level.structureAccount;
      if (e.structureAccount.value !== sa) {
        e.structureAccount.setValue(sa, { emitEvent: false });
      }
      const su = !!level.structureUnit;
      if (e.structureUnit.value !== su) {
        e.structureUnit.setValue(su, { emitEvent: false });
      }
    }
  }

  /** Flush in-memory report options (when modal is open) before building save payload. */
  private flushOpenReportOptionsWorkingCopyToModel(): void {
    if (!this.activeReportOptionReport || !this.reportOptionState) {
      return;
    }
    this.syncReportOptionScalarFormToState();
    this.syncMiscFieldReportOptionValueControlsToState();
    this.flushReportOptionLevelSelectControlsToModel();
    this.syncReportOptionReportCheckboxFormToState();
    this.flushReportOptionLevelCheckboxControlsToModel();
  }

  private syncReportOptionScalarFormToState(): void {
    if (!this.reportOptionState) {
      return;
    }
    const v = this.reportOptionScalarForm.getRawValue();
    const s = this.reportOptionState;
    const rt = v.reportType ?? '';
    s.reportType = rt === 'S' || rt === 'M' ? rt : '';
    s.claimId = v.claimId ?? '';
    s.sortBy = v.sortBy ?? '';
    s.formType = this.formTypeFromScalarFormValue(v.formType);
    s.minChangeUnit = v.minChangeUnit ?? 'Dollar';
    s.duration = v.duration ?? 'OPEN-CLOSE';
    s.selectedBreakLevel = this.breakLevelFromScalarFormValue(
      v.selectedBreakLevel,
    );
    s.selectedBankNumber = this.activeBankNumberFromScalarFormValue(
      v.selectedBankNumber,
    );
    s.summarySort = v.summarySort ?? this.payCodeSummarySortOptions[0].value;
    s.reserveFromDate = v.reserveFromDate ?? null;
    s.reserveToDate = v.reserveToDate ?? null;
    s.minChange = v.minChange ?? '';
    s.maxClaimCount = v.maxClaimCount ?? '';
    s.lastCsrRunDate = v.lastCsrRunDate ?? null;
  }

  private syncReportOptionReportCheckboxFormToState(): void {
    const raw = this.reportOptionReportCheckboxForm.getRawValue();
    this.queryScheduleIncrementFlags = {
      beginSelected: raw.workStatusBeginDateSelected,
      endSelected: raw.workStatusEndDateSelected,
    };
    if (!this.reportOptionState) {
      return;
    }
    this.reportOptionState.includePaymentDetail = raw.includePaymentDetail;
    if (this.activeReportOptionReport?.reportId === 193) {
      const nextSeries: string[] = [];
      for (const series of REPORT_OPTION_PAY_CODE_SERIES_LABELS) {
        if (this.getPayCodeSeriesCheckboxControl(series).value) {
          nextSeries.push(series);
        }
      }
      this.reportOptionState.selectedPayCodeSeries = nextSeries;
    }
  }

  private syncReportOptionReportCheckboxFormFromState(): void {
    if (!this.reportOptionState) {
      return;
    }
    const s = this.reportOptionState;
    this.reportOptionReportCheckboxForm.patchValue(
      {
        workStatusBeginDateSelected:
          this.queryScheduleIncrementFlags.beginSelected,
        workStatusEndDateSelected: this.queryScheduleIncrementFlags.endSelected,
        includePaymentDetail: !!s.includePaymentDetail,
        payCodeAllSeries: this.isAllPayCodeSeriesSelected(),
      },
      { emitEvent: false },
    );
    this.syncQueryScheduleIncrementForSelectedReports();
    if (this.activeReportOptionReport?.reportId === 193) {
      for (const series of REPORT_OPTION_PAY_CODE_SERIES_LABELS) {
        const c = this.getPayCodeSeriesCheckboxControl(series);
        const next = this.isPayCodeSeriesSelected(series);
        if (c.value !== next) {
          c.setValue(next, { emitEvent: false });
        }
      }
    }
  }

  private flushReportOptionLevelCheckboxControlsToModel(): void {
    if (!this.reportOptionState?.selectedLevels) {
      return;
    }
    for (const level of this.reportOptionState.selectedLevels) {
      const e = this.reportOptionLevelCheckboxControls.get(level);
      if (!e) {
        continue;
      }
      level.breakPage = e.pageBreak.value;
      level.structureIntermediate = e.structureIntermediate.value;
      level.structureAccount = e.structureAccount.value;
      level.structureUnit = e.structureUnit.value;
    }
  }

  private getReportOptionLevelCheckboxEntry(level: SelectedLevelOption): {
    pageBreak: FormControl<boolean>;
    structureIntermediate: FormControl<boolean>;
    structureAccount: FormControl<boolean>;
    structureUnit: FormControl<boolean>;
  } {
    let e = this.reportOptionLevelCheckboxControls.get(level);
    if (!e) {
      e = {
        pageBreak: new FormControl(!!level.breakPage, { nonNullable: true }),
        structureIntermediate: new FormControl(!!level.structureIntermediate, {
          nonNullable: true,
        }),
        structureAccount: new FormControl(!!level.structureAccount, {
          nonNullable: true,
        }),
        structureUnit: new FormControl(!!level.structureUnit, {
          nonNullable: true,
        }),
      };
      this.reportOptionLevelCheckboxControls.set(level, e);
    }
    return e;
  }

  getReportOptionLevelPageBreakControl(
    level: SelectedLevelOption,
  ): FormControl<boolean> {
    return this.getReportOptionLevelCheckboxEntry(level).pageBreak;
  }

  getReportOptionLevelStructureIntermediateControl(
    level: SelectedLevelOption,
  ): FormControl<boolean> {
    return this.getReportOptionLevelCheckboxEntry(level).structureIntermediate;
  }

  getReportOptionLevelStructureAccountControl(
    level: SelectedLevelOption,
  ): FormControl<boolean> {
    return this.getReportOptionLevelCheckboxEntry(level).structureAccount;
  }

  getReportOptionLevelStructureUnitControl(
    level: SelectedLevelOption,
  ): FormControl<boolean> {
    return this.getReportOptionLevelCheckboxEntry(level).structureUnit;
  }

  getPayCodeSeriesCheckboxControl(series: string): FormControl<boolean> {
    let c = this.payCodeSeriesReportCheckboxControls.get(series);
    if (!c) {
      c = new FormControl(false, { nonNullable: true });
      this.payCodeSeriesReportCheckboxControls.set(series, c);
    }
    if (
      this.reportOptionState &&
      this.activeReportOptionReport?.reportId === 193
    ) {
      const next = this.isPayCodeSeriesSelected(series);
      if (c.value !== next) {
        c.setValue(next, { emitEvent: false });
      }
    }
    return c;
  }

  private setupReportOptionReportCheckboxListeners(): void {
    const c = this.reportOptionReportCheckboxForm.controls;
    this.dispose.subs = c.includePaymentDetail.valueChanges.subscribe((v) => {
      if (this.reportOptionState) {
        this.reportOptionState.includePaymentDetail = v;
      }
    });
    this.dispose.subs = c.workStatusBeginDateSelected.valueChanges.subscribe(
      (v) => {
        this.queryScheduleIncrementFlags.beginSelected = !!v;
      },
    );
    this.dispose.subs = c.workStatusEndDateSelected.valueChanges.subscribe(
      (v) => {
        this.queryScheduleIncrementFlags.endSelected = !!v;
      },
    );

    this.dispose.subs = c.payCodeAllSeries.valueChanges.subscribe((v) => {
      if (
        this.activeReportOptionReport?.reportId !== 193 ||
        !this.reportOptionState
      ) {
        return;
      }
      this.toggleAllPayCodeSeries(!!v);
      for (const series of REPORT_OPTION_PAY_CODE_SERIES_LABELS) {
        this.getPayCodeSeriesCheckboxControl(series).setValue(!!v, {
          emitEvent: false,
        });
      }
    });

    for (const series of REPORT_OPTION_PAY_CODE_SERIES_LABELS) {
      const ctrl = this.getPayCodeSeriesCheckboxControl(series);
      this.dispose.subs = ctrl.valueChanges.subscribe((v) => {
        if (
          this.activeReportOptionReport?.reportId !== 193 ||
          !this.reportOptionState
        ) {
          return;
        }
        this.togglePayCodeSeries(series, !!v);
        this.reportOptionReportCheckboxForm.controls.payCodeAllSeries.setValue(
          this.isAllPayCodeSeriesSelected(),
          { emitEvent: false },
        );
      });
    }

    this.syncQueryScheduleIncrementForSelectedReports();
    this.dispose.subs =
      this.form.controls.selectedReportIds.valueChanges.subscribe(() => {
        this.syncQueryScheduleIncrementForSelectedReports();
      });
  }

  private getMiscFieldRowsForReport(
    reportId: number,
  ): QueryMiscClientFieldRow[] {
    return this.reportOptionMetadata[reportId]?.miscClientFieldRows ?? [];
  }

  isMultipleReportsSelected(): boolean {
    return this.form.controls.selectedReportIds.value.length > 1;
  }

  private validateStandardBreakLevels(
    reportName: string,
    selectedLevels: SelectedLevelOption[],
  ): string {
    const name = (reportName ?? '').trim().toUpperCase();
    const withTenLimit = new Set([
      'ACCUMULATIVE LOSS ANALYSIS',
      'AVERAGE DISABILITY DURATION',
      'CLAIM AND EXPENSE II',
      'CLAIM REPORT',
      'DISABILITY OFFSET REPORT',
      'DISABILITY STATUS REPORT',
      'DUPLICATE INDEMNITY PAYMENT REPORT',
      'LOST DAYS REPORT',
      'INDEMNITY REPORT',
      'MISCELLANEOUS CLIENT FIELD',
      'PAYCODE SUMMARY REPORT',
      'PAYCODE ANALYSIS',
      'RESERVE CHANGE',
      'TOWERS PERRIN REPORT',
      'TRANSACTION SUMMARY',
    ]);

    if (withTenLimit.has(name) && selectedLevels.length > 10) {
      return 'Cannot have more than 10 Break Levels.';
    }

    const withEightIfStructure = new Set([
      'ACCUMULATIVE LOSS ANALYSIS',
      'CLAIM REPORT',
      'DUPLICATE INDEMNITY PAYMENT REPORT',
      'INDEMNITY REPORT',
      'RESERVE CHANGE',
    ]);

    const hasStructure = selectedLevels.some(
      (x) => x.name.toUpperCase() === 'STRUCTURE',
    );
    const hasClient = selectedLevels.some(
      (x) => x.name.toUpperCase() === 'CLIENT',
    );
    if (
      withEightIfStructure.has(name) &&
      hasStructure &&
      selectedLevels.length > 8
    ) {
      return 'Cannot have more than 8 Break Levels if STRUCTURE is included.';
    }

    const withClientStructureBlock = new Set([
      'CLAIM REPORT',
      'COST RANGE ANALYSIS',
      'DISABILITY PAYMENT LISTING',
      'DUPLICATE INDEMNITY PAYMENT REPORT',
      'INDEMNITY REPORT',
      'LOSS ANALYSIS II',
      'RESERVE CHANGE',
    ]);
    if (withClientStructureBlock.has(name) && hasStructure && hasClient) {
      return 'Cannot have both Client and Structure Break Levels together.';
    }

    if (name === 'LOSS ANALYSIS II') {
      if (selectedLevels.length > 3) {
        return 'Cannot have more than 3 Break Levels.';
      }

      if (hasStructure && selectedLevels.length > 1) {
        return 'Cannot have more than 1 Break Levels if STRUCTURE is included.';
      }
    }

    const customDates = selectedLevels.find(
      (x) => x.name.toUpperCase() === 'CUSTOM DATES',
    );
    if (customDates) {
      const increment = Number(customDates.customDateValue ?? 0);
      if (!Number.isInteger(increment) || increment <= 0) {
        return 'Please enter a valid non-zero number for Date Incremented By in Custom Dates Break Level.';
      }
    }

    return '';
  }

  isPayCodeSeriesSelected(series: string): boolean {
    return (this.reportOptionState?.selectedPayCodeSeries ?? []).includes(
      series,
    );
  }

  togglePayCodeSeries(series: string, selected: boolean): void {
    if (!this.reportOptionState) {
      return;
    }

    const current = new Set(this.reportOptionState.selectedPayCodeSeries ?? []);
    if (selected) {
      current.add(series);
    } else {
      current.delete(series);
    }

    this.reportOptionState.selectedPayCodeSeries = Array.from(current);
  }

  isAllPayCodeSeriesSelected(): boolean {
    const selected = new Set(
      this.reportOptionState?.selectedPayCodeSeries ?? [],
    );
    return REPORT_OPTION_PAY_CODE_SERIES_LABELS.every((x) => selected.has(x));
  }

  toggleAllPayCodeSeries(selected: boolean): void {
    if (!this.reportOptionState) {
      return;
    }

    this.reportOptionState.selectedPayCodeSeries = selected
      ? [...REPORT_OPTION_PAY_CODE_SERIES_LABELS]
      : [];
  }

  onEditQueryRequested(query: QueryListItem): void {
    this.editQueryId = query.queryId;
    this.editMode = true;
    this.requestInitialStep = null;
    this.activeSection = 'request';
    void this.loadQueryForEdit(query.queryId);
  }

  onScheduleQueryRequested(query: QueryListItem): void {
    this.editQueryId = query.queryId;
    this.savedQueryId = query.queryId;
    this.editMode = true;
    this.scheduleError = '';
    this.scheduleSuccess = '';
    this.activeSection = 'schedule-only';
    void this.loadQueryForScheduleOnly(query);
  }

  async loadQueryForEdit(queryId: number): Promise<void> {
    try {
      this.savedQueryId = queryId;
      this.isLoadingForEdit = true;
      this.activeReportOptionReport = null;
      this.reportOptionState = null;
      this.reportOptionError = '';
      this.resetScheduleFormToLegacyDefaults();
      const detail = await this.api.query.getQuery(queryId);
      const queryTypeToPatch = this.mapQueryTypeToCode(detail.queryType);
      this.form.patchValue(
        {
          queryType: queryTypeToPatch,
          contractNumber: detail.contractNumber,
          selectedReportIds: detail.selectedReportIds,
          reportIncludeType: detail.general.reportIncludeType ?? 'ClaimOnly',
          ...generalDateFieldsFromQueryDetail(detail.general),
          claimOpen: detail.general.claimStatus.open,
          claimClosed: detail.general.claimStatus.closed,
          claimDeleted: detail.general.claimStatus.deleted,
          linesOfBusiness: detail.general.linesOfBusiness,
          name: detail.saveOptions.name,
          description: detail.saveOptions.description,
          isPrivate: detail.saveOptions.isPrivate,
          viewSpii: detail.saveOptions.viewSpii,
        },
        { emitEvent: false },
      );

      this.selectedClientDisplayName = detail.clientName?.trim() || null;

      this.editDetailLocationMethodCode =
        detail.locationSelectionMethodCode ?? null;

      this.extendedParamsForm.patchValue(
        {
          examinerIds: detail.selectedExaminerIds ?? [],
          examinerOfficeIds: (detail.selectedExaminerOfficeIds ?? []).map(
            String,
          ),
          processorOfficeIds: (detail.selectedProcessorOfficeIds ?? []).map(
            String,
          ),
          icdCodes: detail.icdCodes ?? [],
          accountUnitIds: [...(detail.locationAccountUnitIds ?? [])],
          structureNodeIds: [...(detail.structureNodeIds ?? [])],
        },
        { emitEvent: false },
      );

      await this.hydrateExtendedFormFromParameterApis(
        detail.contractNumber,
        queryId,
      );

      if (detail.general.linesOfBusiness?.some((l) => l === "Workers' Comp")) {
        try {
          const wc = await this.api.query.getWcClaimTypes(
            detail.contractNumber,
            queryId,
          );
          applyWcClaimTypesSelection(this.extendedParamsForm, wc.selection);
        } catch {
          /* WC subtype load is optional */
        }
        try {
          const inj = await this.api.query.getWcInjuryParameters(
            detail.contractNumber,
            queryId,
          );
          applyWcInjuryParametersFromApi(this.extendedParamsForm, inj);
        } catch (err) {
          console.error(
            '[Query] getWcInjuryParameters failed while loading query for edit',
            err,
          );
        }
        try {
          const qw = await this.api.query.getQueryWorkParameters(queryId);
          applyQueryWorkFromApi(this.extendedParamsForm, qw.work);
        } catch {
          /* optional */
        }
      }

      if (
        detail.general.linesOfBusiness?.some((l) => l === 'General Liability')
      ) {
        try {
          const gl = await this.api.query.getGlClaimTypes(
            detail.contractNumber,
            queryId,
          );
          applyMajorLiabilityParametersFromApi(
            this.extendedParamsForm,
            'majorGl',
            gl,
          );
        } catch (err) {
          console.error(
            '[Query] getGlClaimTypes failed while loading query for edit',
            err,
          );
        }
      }
      if (detail.general.linesOfBusiness?.some((l) => l === 'Auto Liability')) {
        try {
          const al = await this.api.query.getAlClaimTypes(
            detail.contractNumber,
            queryId,
          );
          applyMajorLiabilityParametersFromApi(
            this.extendedParamsForm,
            'majorAl',
            al,
          );
        } catch (err) {
          console.error(
            '[Query] getAlClaimTypes failed while loading query for edit',
            err,
          );
        }
      }
      if (detail.general.linesOfBusiness?.some((l) => l === 'Property')) {
        try {
          const pl = await this.api.query.getPlClaimTypes(
            detail.contractNumber,
            queryId,
          );
          applyMajorLiabilityParametersFromApi(
            this.extendedParamsForm,
            'majorPl',
            pl,
          );
        } catch (err) {
          console.error(
            '[Query] getPlClaimTypes failed while loading query for edit',
            err,
          );
        }
      }
      if (detail.general.linesOfBusiness?.some((l) => l === 'Disability')) {
        try {
          const ds = await this.api.query.getDsClaimTypes(queryId);
          applyDsClaimParametersFromApi(this.extendedParamsForm, ds);
        } catch {
          /* optional */
        }
      }

      if (detail.contractNumber) {
        await this.loadReportsAndDefaults(detail.contractNumber);
        const layoutId = detail.customLayoutId;
        if (layoutId != null && layoutId > 0) {
          this.outputLayoutType = 'custom';
          await this.loadCustomLayouts();
          this.selectedCustomLayout =
            this.customLayouts.find((x) => x.layoutId === layoutId) ?? null;
        } else if (queryTypeToPatch === 'B') {
          this.outputLayoutType = 'custom';
          await this.loadCustomLayouts();
        }
        try {
          const fin = await this.api.query.getFinancialOptions(
            detail.contractNumber,
            queryId,
          );
          applyFinancialOptionsFromApi(
            this.extendedParamsForm,
            fin.options ?? [],
          );
        } catch {
          /* optional */
        }
        this.syncReportSelectionBuckets();
        this.applyScheduleHydrationFromDetail(detail);
        await this.loadReportOptionsForEdit(detail);
        this.syncQueryScheduleIncrementForSelectedReports();
      }

      this.parameterPanelRefreshToken++;
    } catch {
      this.saveError = 'Failed to load query details for editing.';
    } finally {
      this.rebuildClientTableRows();
      this.isLoadingForEdit = false;
    }
  }

  private async loadReportOptionsForEdit(detail: {
    selectedReportIds: number[];
    reportOptions?: {
      reportId: number;
      options: { optionType: string; optionValue: string }[];
    }[];
  }): Promise<void> {
    const optionsByReport = new Map(
      (detail.reportOptions ?? []).map((ro) => [ro.reportId, ro.options]),
    );
    for (const reportId of detail.selectedReportIds) {
      const report = this.allReports.find((r) => r.reportId === reportId);
      if (report) {
        await this.loadReportOptionMetadataAndState(report);
        if (this.reportOptionState) {
          this.reportOptionStates[reportId] = { ...this.reportOptionState };
        }
        const savedOptions = optionsByReport.get(reportId);
        if (savedOptions?.length) {
          this.applySavedOptionsToState(reportId, savedOptions);
        }
        if (
          this.activeReportOptionReport?.reportId === reportId &&
          this.reportOptionState
        ) {
          this.reportOptionState = {
            ...this.reportOptionStates[reportId],
          };
          this.syncReportOptionScalarFormFromState();
        }
      }
    }
  }

  private applySavedOptionsToState(
    reportId: number,
    options: { optionType: string; optionValue: string }[],
  ): void {
    const state = this.reportOptionStates[reportId];
    if (!state) return;

    const getOpt = (type: string) =>
      options.find(
        (o) => (o.optionType ?? '').toUpperCase() === type.toUpperCase(),
      )?.optionValue ?? '';

    if (this.supportsStandardOptions(reportId) || reportId === 193) {
      const sortBy = getOpt('SORT-OPTION');
      if (sortBy) {
        const metadataOptions =
          this.reportOptionMetadata[reportId]?.sortOptions ?? [];
        const matched = metadataOptions.find(
          (o) => o.toUpperCase() === sortBy.toUpperCase(),
        );
        state.sortBy = matched ?? sortBy;
      }
      const breakLevel = getOpt('BREAK-LEVEL');
      const pageBrkList = getOpt('PAGE-BRK-LIST');
      if (breakLevel) {
        const pageBreakSet = new Set(
          (pageBrkList || '')
            .split(',')
            .map((x) => x.trim().toUpperCase())
            .filter(Boolean),
        );
        state.selectedLevels = breakLevel
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean)
          .map((name) => {
            const level = this.createLevelOption(name);
            level.breakPage = pageBreakSet.has(name.toUpperCase());
            return level;
          });
        state.availableLevels = (state.availableLevels ?? []).filter(
          (l) =>
            !state.selectedLevels?.some(
              (s) => s.name.toUpperCase() === l.toUpperCase(),
            ),
        );
      }
      const stateOf = getOpt('STATE-OF');
      if (stateOf && state.selectedLevels) {
        const stateLevel = state.selectedLevels.find(
          (x) => x.name.toUpperCase() === 'STATE',
        );
        if (stateLevel) {
          stateLevel.stateOf = mapReportOptionStateOfFromStorage(stateOf);
        }
      }
      const structTot = getOpt('STRUCT-TOT-LEVEL');
      if (structTot && state.selectedLevels) {
        const parts = structTot.split(',').map((x) => x.toUpperCase());
        const structureLevel = state.selectedLevels.find(
          (x) => x.name.toUpperCase() === 'STRUCTURE',
        );
        if (structureLevel) {
          structureLevel.structureIntermediate = parts.includes('I');
          structureLevel.structureAccount = parts.includes('A');
          structureLevel.structureUnit = parts.includes('U');
        }
      }
      const customDt = getOpt('CUSTOM-DT-BREAK-DETL');
      if (customDt && state.selectedLevels) {
        const [value, period] = customDt.split(',');
        const customLevel = state.selectedLevels.find(
          (x) => x.name.toUpperCase() === 'CUSTOM DATES',
        );
        if (customLevel && value && period) {
          customLevel.customDateValue = value;
          customLevel.customDatePeriod =
            period === 'D' || period === 'M' || period === 'Y' ? period : 'D';
        }
      }
    }

    if (reportId === 199 || reportId === 338) {
      const rptLevel = getOpt('RPT-LEVEL');
      if (rptLevel) state.reportType = rptLevel;
      const claimId = getOpt('CLAIM-ID');
      if (claimId) {
        state.claimId = resolveClaimIdFromStorage(
          claimId,
          this.reportOptionMetadata[reportId]?.claimIdOptions ?? [],
        );
      }
    }
    if (reportId === 525) {
      state.formType = getOpt('FORM-TYPE');
      state.maxClaimCount = getOpt('MAX-CLAIM-COUNT');
      const lastCsr = getOpt('LAST-CSR-CUTOFF');
      if (lastCsr) state.lastCsrRunDate = this.parseDateOption(lastCsr);
    }
    if (reportId === QUERY_RESERVE_CHANGE_REPORT_ID) {
      const from = getOpt('DATE-BEGIN-2');
      if (from) state.reserveFromDate = this.parseDateOption(from);
      const to = getOpt('DATE-END-2');
      if (to) state.reserveToDate = this.parseDateOption(to);
      const reserveAmt = getOpt('RESERVE-AMT');
      if (reserveAmt !== '') {
        state.minChange = reserveAmt;
      }
      state.minChangeUnit = mapReserveChangeMinChangeUnitFromStorage(
        getOpt('EVENT-FLAGS[1]'),
      );
      const claimId = getOpt('CLAIM-ID');
      if (claimId) {
        state.claimId = resolveClaimIdFromStorage(
          claimId,
          this.reportOptionMetadata[reportId]?.claimIdOptions ?? [],
        );
      }
    }
    if (reportId === 379) {
      const from = getOpt('DATE-BEGIN-2');
      if (from) state.workStatusBeginDate = this.parseDateOption(from);
      const to = getOpt('DATE-END-2');
      if (to) state.workStatusEndDate = this.parseDateOption(to);
    }
    if (reportId === 193) {
      state.claimId = resolveClaimIdFromStorage(
        getOpt('CLAIM-ID'),
        this.reportOptionMetadata[reportId]?.claimIdOptions ?? [],
      );
      state.summarySort = getOpt('SUMMARY-SORT') || state.summarySort;
      state.includePaymentDetail = getOpt('INC-PMNT-DETAIL') === 'D';
      state.selectedBankNumber = getOpt('BANK-NUMBER') ?? '';
      const paycodes = getOpt('SELECTED-PAYCODES');
      if (paycodes) {
        const series = [
          'All 100 Series',
          'All 200 Series',
          'All 300 Series',
          'All 400 Series',
          'All 500 Series',
          'All 600 Series',
          'All 700 Series',
        ];
        const inSeries = paycodes
          .split(',')
          .map((x) => x.trim())
          .filter((x) => series.includes(x));
        const other = paycodes
          .split(',')
          .map((x) => x.trim())
          .filter((x) => x && !series.includes(x));
        state.selectedPayCodeSeries = inSeries;
        state.selectedPayCodes = other;
      }
    }
    if (reportId === 119) {
      state.sortBy = getOpt('SORT-OPTION') || state.sortBy;
      state.claimId = resolveClaimIdFromStorage(
        getOpt('CLAIM-ID'),
        this.reportOptionMetadata[reportId]?.claimIdOptions ?? [],
      );
      state.selectedBreakLevel = getOpt('REPEATERLIST-REPORT-BREAK-LEVEL');
      state.numClaims = getOpt('MIN-NUM-OF-CLAIMS-TO-REPORT');
    }
    if (reportId === 184) {
      const raw = getOpt('MISC-FIELDS');
      if (raw) {
        const rows = this.getMiscFieldRowsForReport(184);
        const selections: Record<string, string> = {
          ...(state.miscFieldSelections ?? {}),
        };
        for (const segment of raw.split(':').filter(Boolean)) {
          const parts = segment.split('|');
          if (parts.length < 5) {
            continue;
          }
          const [c, a, u, fRaw, selected] = parts;
          const row = rows.find(
            (r) =>
              String(r.contractNumber) === c &&
              (r.accountNumber || 'ALL') === a &&
              (r.unitNumber || 'ALL') === u &&
              String(r.fieldNumber).padStart(3, '0') === fRaw.padStart(3, '0'),
          );
          if (row) {
            selections[this.getMiscFieldRowKey(row)] = selected;
          }
        }
        state.miscFieldSelections = selections;
      }
    }

    if (this.reportOptionState === state) {
      this.ensureReportOptionLevelSelectControls();
      this.scheduleSyncReportOptionLevelSelectControlsFromState();
    }
  }

  /** Loads states, flags, and vendors into the extended form so save matches DB without opening each sidebar panel first. */
  private async hydrateExtendedFormFromParameterApis(
    contractNumber: number,
    queryId: number,
  ): Promise<void> {
    try {
      const [states, flags, vendors] = await Promise.all([
        this.api.query.getStates(contractNumber, queryId),
        this.api.query.getFlags(contractNumber, queryId),
        this.api.query.getVendors(contractNumber, queryId),
      ]);

      const flagsFg = this.extendedParamsForm.get('flags') as FormGroup;
      for (const row of flags.flags ?? []) {
        const ctl = queryClaimFlagFormKeyForCode(row.flagCode ?? '');
        if (ctl && flagsFg.get(ctl)) {
          flagsFg
            .get(ctl)!
            .setValue(queryClaimFlagFromStorage(row.selectedValue), {
              emitEvent: false,
            });
        }
      }

      this.extendedParamsForm.patchValue(
        {
          lossStateCodes: (states.selectedLossStates ?? []).map(
            (s) => s.stateCode,
          ),
          jurisStateCodes: (states.selectedJurisStates ?? []).map(
            (s) => s.stateCode,
          ),
          payrollStateCodes: (states.selectedPayrollStates ?? []).map(
            (s) => s.stateCode,
          ),
          vendorIds: (vendors.selectedVendors ?? []).map((v) =>
            String(v.vendorId),
          ),
        },
        { emitEvent: false },
      );
    } catch {
      /* Panel sections can still hydrate when opened */
    }
  }

  onCreateLayoutRequested(): void {
    this.prepareLayoutEditorForLayoutsMenu();
    this.activeSection = 'create-layout';
  }

  onEditLayoutRequested(layout: LayoutListItem): void {
    this.layoutEditorReturnContext = 'layouts';
    this.editLayoutId = layout.layoutId;
    this.copyLayoutId = null;
    this.layoutViewOnly = !layout.isOwner;
    this.activeSection = 'create-layout';
  }

  onCopyLayoutRequested(layout: LayoutListItem): void {
    this.layoutEditorReturnContext = 'layouts';
    this.editLayoutId = null;
    this.copyLayoutId = layout.layoutId;
    this.layoutViewOnly = false;
    this.activeSection = 'create-layout';
  }

  onLayoutEditorCancelled(): void {
    const returnContext = this.layoutEditorReturnContext;
    this.clearLayoutEditorSession();
    if (returnContext === 'stepper') {
      this.returnToStepperCustomLayouts();
    } else {
      this.activeSection = 'view-layouts';
    }
  }

  onLayoutEditorSaved(layoutId: number): void {
    const returnContext = this.layoutEditorReturnContext;
    this.clearLayoutEditorSession();
    if (returnContext === 'stepper') {
      void this.returnToStepperCustomLayouts(layoutId);
    } else {
      this.activeSection = 'view-layouts';
    }
  }

  private prepareLayoutEditorForLayoutsMenu(): void {
    this.layoutEditorReturnContext = 'layouts';
    this.editLayoutId = null;
    this.copyLayoutId = null;
    this.layoutViewOnly = false;
  }

  private clearLayoutEditorSession(): void {
    this.editLayoutId = null;
    this.copyLayoutId = null;
    this.layoutViewOnly = false;
    this.layoutEditorReturnContext = null;
  }

  private async returnToStepperCustomLayouts(
    newLayoutId?: number,
  ): Promise<void> {
    this.outputLayoutType = 'custom';
    this.requestInitialStep = 'output';
    this.activeSection = 'request';
    await this.loadCustomLayouts();
    if (newLayoutId != null) {
      this.selectedCustomLayout =
        this.customLayouts.find((x) => x.layoutId === newLayoutId) ?? null;
    }
    setTimeout(() => this.focusOutputStepStart(), 0);
  }
}
